import React from 'react';
import { Link } from 'react-router-dom';

/* ─── reusable micro-components ────────────────────────────────────────── */
function Ex({ arrow, children }) {
  return (
    <div className="seo-example">
      {arrow ? <>{arrow} <span>→</span> {children}</> : children}
    </div>
  );
}
function FAQ({ items }) {
  return (
    <div>
      {items.map((it, i) => (
        <div className="faq-item" key={i}>
          <p className="faq-q">❓ {it.q}</p>
          <p className="faq-a">{it.a}</p>
        </div>
      ))}
    </div>
  );
}
function Related({ links }) {
  return (
    <div className="related-games">
      {links.map(([label, to]) => (
        <Link key={to} to={to} className="related-link">{label}</Link>
      ))}
    </div>
  );
}

/* ─── content map ──────────────────────────────────────────────────────── */
const CONTENT = {

  /* ══ JUMBLE SOLVER ═══════════════════════════════════════════════════════ */
  jumble: () => (
    <div className="seo-section">
      <h2>What Is a Jumble Puzzle?</h2>
      <p>
        A <strong>jumble puzzle</strong> is a word game where letters are scrambled and the
        player must rearrange them to form a real word. Jumble puzzles have appeared in
        newspapers across the United States since 1954, when Martin Naydel created the
        syndicated feature "Jumble — That Scrambled Word Game." Today, millions of people
        solve jumbles in print and online every single day.
      </p>
      <p>
        Jumble puzzles are not only entertaining — they sharpen vocabulary, improve spelling
        awareness, and strengthen pattern-recognition skills. Children and adults alike enjoy
        the satisfying "aha" moment when scrambled letters suddenly reveal a hidden word.
        Our free <strong>online jumble solver</strong> takes the frustration out of being
        stuck and helps you learn by showing all possible words that can be formed from your
        letters.
      </p>

      <h2>How to Use Our Jumble Solver</h2>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Step-by-Step Guide</h3>
            <ol>
              <li>Type the jumbled letters into the input box (e.g., <strong>RETAW</strong>).</li>
              <li>Choose a minimum word length (2, 3, 4, or 5 letters) to filter results.</li>
              <li>Click <strong>"Find Words"</strong> and results appear instantly.</li>
              <li>Words are grouped by length — longest first — for easy scanning.</li>
              <li>Hover over any word to see its Scrabble point value.</li>
            </ol>
            <p>
              The solver checks every combination of your letters against a dictionary of
              over <strong>190,000 words</strong>, so you'll never miss a valid answer.
            </p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Worked Examples</h3>
            <Ex arrow="RETAW">WATER, WARE, WEAR, WART, TARE, RATE, TEAR, AREA…</Ex>
            <Ex arrow="TARGE">GREAT, GRATE, GRATE, RAGE, GEAR, GATE, RATE…</Ex>
            <Ex arrow="SEINLT">LISTEN, SILENT, TINSEL, ENLIST, INLETS, LINT…</Ex>
            <p>Notice how a single set of letters can produce dozens of valid words across different lengths!</p>
          </div>
        </div>
      </div>

      <h2>Tips for Solving Jumbles Manually</h2>
      <ul>
        <li><strong>Identify vowels first.</strong> Count A, E, I, O, U. Their positions often unlock the word.</li>
        <li><strong>Look for common endings.</strong> Try tacking -ING, -ED, -ER, -ION onto letter groups you see.</li>
        <li><strong>Spot double-letter pairs.</strong> LL, TT, SS, OO are frequent in English and narrow the options.</li>
        <li><strong>Think about syllables.</strong> A 6-letter jumble likely has 2–3 syllables; try splitting the letters accordingly.</li>
        <li><strong>Use the context clue.</strong> Newspaper jumbles pair each scramble with a cartoon clue — use it!</li>
      </ul>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'What makes our jumble solver different from others?',
          a: 'Our solver uses a 190,000+ word Scrabble dictionary and returns results grouped by word length with Scrabble scores, making it easy to find the most useful word at a glance — not just a raw list.' },
        { q: 'Can I use the jumble solver for Scrabble or Words with Friends?',
          a: 'Absolutely. Because the solver finds all words formable from your letters (not just exact-length matches), it doubles as a powerful rack-management tool for tile-based word games.' },
        { q: 'Is there a letter limit?',
          a: 'You can enter up to 15 letters. For very large inputs, the solver caps results at 200 words to keep the display readable.' },
      ]} />

      <h2>Related Tools</h2>
      <Related links={[['🔍 Word Unscrambler','/unscramble'],['🔤 Anagram Solver','/anagram-solver'],['🃏 Scrabble Finder','/scrabble'],['✏️ Crossword Solver','/crossword']]} />
    </div>
  ),

  /* ══ WORD UNSCRAMBLER ════════════════════════════════════════════════════ */
  unscramble: () => (
    <div className="seo-section">
      <h2>What Is Word Unscrambling?</h2>
      <p>
        <strong>Word unscrambling</strong> is the challenge of rearranging a set of letters
        to form one or more valid English words. It is a core skill in popular word games
        like Scrabble, Words with Friends, and Boggle, as well as in word-jumble puzzles
        found in newspapers, magazines, and mobile apps.
      </p>
      <p>
        Our <strong>Word Unscrambler</strong> finds <em>every</em> valid English word you can
        build from your letters — not just the full-length answer, but all the shorter words
        hidden inside them too. Enter <strong>HELLO</strong> and you'll see HELLO, HELL, HOLE,
        HOE and more, neatly grouped by word length. That's exactly what you need for Scrabble,
        Words with Friends, and word-jumble puzzles, where the shorter plays often score the win.
      </p>
      <p>
        Word unscrambling is also a powerful educational activity. Researchers have found
        that rearranging letters reinforces spelling patterns, builds vocabulary, and
        improves working memory in both children and adults.
      </p>

      <h2>How to Use the Word Unscrambler</h2>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Step-by-Step</h3>
            <ol>
              <li>Enter your scrambled or available letters (up to 15) in the input box.</li>
              <li>Pick your <strong>Dictionary</strong> — TWL, SOWPODS, or Words With Friends.</li>
              <li>Click <strong>"Unscramble"</strong>.</li>
              <li>Every valid word appears instantly, grouped by length (longest first).</li>
              <li>Each result shows its Scrabble point value in parentheses.</li>
              <li>Copy a word to use it in your game or puzzle!</li>
            </ol>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Examples</h3>
            <Ex arrow="LEWOH">ELBOW, WHOLE, HOWL, HOE, OWL, LOW</Ex>
            <Ex arrow="EILSTN">LISTEN, SILENT, TINSEL, ENLIST, INLETS</Ex>
            <Ex arrow="AEPRS">PEARS, SPARE, REAPS, PARSE, RAPES, SPEAR</Ex>
          </div>
        </div>
      </div>

      <h2>Choosing Your Dictionary: TWL, SOWPODS or Words With Friends</h2>
      <p>
        The same rack of letters can produce different valid words depending on which official word
        list your game uses — so the Unscrambler lets you switch between the three big dictionaries
        and re-runs instantly:
      </p>
      <ul>
        <li><strong>TWL (North American):</strong> the tournament list for the USA, Canada, Thailand and Israel. Pick this for Scrabble in North America.</li>
        <li><strong>SOWPODS / Collins:</strong> the international list used in the UK, Europe, Australia and world championships — larger and more permissive, so it returns the most words.</li>
        <li><strong>Words With Friends:</strong> Zynga's mobile game uses its own dictionary that differs from both Scrabble lists.</li>
      </ul>
      <p>
        Short but high-value words such as <strong>QI</strong>, <strong>ZA</strong> and <strong>XU</strong>
        are valid in TWL and SOWPODS but not in every Words With Friends build — one reason your word may
        be accepted in one game and rejected in another. Set the dictionary to match your game before you play.
      </p>

      <h2>How Many Words Can You Make From Your Letters?</h2>
      <p>
        It depends on two things: how many letters you have, and how many of them are vowels. A balanced
        mix of vowels and consonants unlocks far more words than a vowel-starved rack. As a rough guide,
        a typical <strong>7-letter</strong> rack yields dozens of playable words across all lengths, while
        adding common letters like <strong>S</strong>, <strong>E</strong>, <strong>R</strong> and
        <strong> A</strong> dramatically increases your options (an S alone opens up plurals of almost
        everything). The Unscrambler counts them all for you and sorts them longest-first, so the
        highest-scoring plays are always at the top.
      </p>
      <p>
        Looking for a single, full-length answer instead of every sub-word? Try our{' '}
        <Link to="/anagram-solver">Anagram Solver</Link>, which only returns words that use every letter.
      </p>

      <h2>Unscramble Words for Your Favorite Games</h2>
      <p>
        A word unscrambler is the fastest way to break out of a tricky rack in almost any letter game:
      </p>
      <ul>
        <li><strong>Scrabble & Words With Friends:</strong> reveal every play your tiles can make, ranked by score.</li>
        <li><strong>Text Twist, Word Cookies & Wordscapes:</strong> find the bonus words hiding in your letter wheel.</li>
        <li><strong>Jumble & newspaper anagrams:</strong> unscramble the daily puzzle in one click.</li>
        <li><strong>Wordle & crosswords:</strong> generate candidate words when you know the letters but not the order.</li>
        <li><strong>ESL & spelling practice:</strong> discover new words and reinforce spelling patterns as you go.</li>
      </ul>

      <h2>Pro Tips for Manual Unscrambling</h2>
      <ul>
        <li><strong>Find prefix–suffix pairs first.</strong> Spot -ING, -TION, UN-, RE-, -ER fragments in your letters.</li>
        <li><strong>Pair consonant clusters.</strong> CH, SH, TH, PH, WH, PL, TR, CR often appear together.</li>
        <li><strong>Try vowel placement.</strong> Move each vowel to different positions mentally until a pattern clicks.</li>
        <li><strong>Think by length.</strong> A 7-letter scramble often forms a 7-letter word; don't assume it's two shorter words.</li>
      </ul>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'What is the difference between the Word Unscrambler and the Anagram Solver?',
          a: 'The Word Unscrambler finds every word you can make from your letters, including shorter words that use only some of them — grouped by length. The Anagram Solver is stricter: it only lists words that use ALL your letters exactly once (true anagrams). Use the Unscrambler to find every possible play; use the Anagram Solver when you only want the full-length answer.' },
        { q: 'Can it find multiple valid words for the same set of letters?',
          a: 'Yes. The letters AEPRS unscramble to PEARS, SPARE, REAPS, PARSE, and SPEAR — all valid 5-letter words. The tool lists all of them.' },
        { q: 'Does letter order matter when I type?',
          a: 'No. You can type letters in any order — SRTAE and TARES both find the same results: STARE, TEARS, RATES, ASTER, TARES.' },
        { q: 'How many letters can I unscramble at once?',
          a: 'You can enter up to 15 letters — the maximum length of a word on a Scrabble board. The more letters you add, the more sub-words the tool will find, all grouped neatly by length.' },
        { q: 'Why does the same word show up in one game but not another?',
          a: 'Because each game uses a different official dictionary. A word like QI or ZA is valid in TWL and SOWPODS Scrabble but may be rejected in Words With Friends. Use the Dictionary selector to match the exact word list your game uses.' },
        { q: 'Can it unscramble letters into two or more separate words?',
          a: 'No — the Unscrambler finds single words that can be built from your letters. If your puzzle answer is a phrase, unscramble the individual words separately, or use the results as building blocks to spot the phrase.' },
        { q: 'Is the Word Unscrambler free and does it work on mobile?',
          a: 'Yes on both counts. It is completely free with no sign-up, and the page is fully responsive, so it works just as well on a phone or tablet as on a desktop — handy when you are mid-game on the couch.' },
        { q: 'What is the highest-scoring word I can make?',
          a: 'Results are sorted longest-first, and each shows its Scrabble score, so the biggest plays sit at the top. Words containing Q, Z, J or X score the most points — keep an eye out for them when you scan the results.' },
      ]} />

      <h2>Related Tools</h2>
      <Related links={[['🔀 Jumble Solver','/jumble'],['🔤 Anagram Solver','/anagram-solver'],['🃏 Scrabble Finder','/scrabble'],['🎯 Boggle Solver','/boggle']]} />
    </div>
  ),

  /* ══ ANAGRAM SOLVER ══════════════════════════════════════════════════════ */
  'anagram-solver': () => (
    <div className="seo-section">
      <h2>Anagram Solver — Solve Anagrams &amp; Find All Possible Words</h2>
      <p>
        Our <strong>anagram solver</strong> takes the letters you enter and instantly
        <strong> solves the anagram</strong>, finding <strong>all possible words you can make</strong>
        {' '}from them — from the full-length anagram right down to short two- and three-letter
        words — checked against a dictionary of more than <strong>190,000 words</strong>. Results are grouped by length,
        longest first, and each word shows its Scrabble score so you can spot the best play at
        a glance. Click any word to see its meaning and which dictionary it comes from.
      </p>
      <p>
        Whether you are stuck on a Scrabble rack, chasing the bonus word in Text Twist,
        unscrambling a daily puzzle, or just love wordplay, this is the fastest way to see
        what those letters can become. Enter up to 15 letters, add <strong>blank tiles</strong>
        {' '}with <code>?</code>, and pick the dictionary that matches your game.
      </p>

      <h2>How to Use the Anagram Solver</h2>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>In Four Steps</h3>
            <ol>
              <li>Type your letters into the box (e.g., <strong>LISTEN</strong>).</li>
              <li>Add a <strong>?</strong> for any blank/wildcard tile (up to two).</li>
              <li>Choose a dictionary — <strong>TWL</strong>, <strong>SOWPODS</strong>, <strong>Words With Friends</strong>, or <strong>All</strong>.</li>
              <li>Read results grouped by length; use the filters to narrow them down.</li>
            </ol>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Example Results</h3>
            <Ex arrow="LISTEN">SILENT, TINSEL, ENLIST, INLETS, LINES, TILES, NEST, LIE</Ex>
            <Ex arrow="RETINAS">RETAINS, RETSINA, STAINER, NASTIER, RESIN, RATS</Ex>
            <Ex arrow="CA?">CAB, CAP, CAT, CAR, CAW, CAY, ACE, ARC</Ex>
            <Ex arrow="STONE">TONES, NOTES, ONSET, STONE, TOES, ONE, SO</Ex>
          </div>
        </div>
      </div>

      <h2>Using Blank Tiles &amp; Wildcards</h2>
      <p>
        Real Scrabble and Words With Friends sets include <strong>blank tiles</strong> that can
        stand in for any letter. Our solver supports them too: type a question mark
        (<code>?</code>) for each blank, up to two. For example, <strong>ca?</strong> returns
        CAB, CAM, CAP, CAR, CAT, CAW and more, while <strong>a?e</strong> matches ACE, ALE, APE,
        ATE and others. Blanks dramatically widen your options — use them to escape a
        vowel-heavy rack or find that game-winning play.
      </p>

      <h2>Anagram Solver for Scrabble &amp; Words With Friends</h2>
      <p>
        Different games use different official word lists, so the anagram solver lets you choose:
      </p>
      <ul>
        <li><strong>TWL / NWL</strong> — the North American tournament list used in the USA and Canada.</li>
        <li><strong>SOWPODS (Collins)</strong> — the international list used across the UK, Europe, and Australia; it is the largest and includes words like <em>QI</em> and <em>ZA</em>.</li>
        <li><strong>Words With Friends</strong> — Zynga's separate dictionary for the popular mobile app.</li>
        <li><strong>All Dictionaries</strong> — the combined set, so a word valid in <em>any</em> game shows up.</li>
      </ul>
      <p>
        Every result is scored with standard letter values, and the highest-scoring words rise
        to the top of each length group — exactly what you want when you are hunting for points.
      </p>

      <h2>How Many Words Can You Make From Your Letters?</h2>
      <p>
        It depends heavily on the letters. Common consonants (R, S, T, L, N) and plenty of
        vowels open up the most words — a rack like <strong>RETINAS</strong> or
        <strong> RATIONS</strong> can make well over a hundred, while awkward tiles (lots of
        consonants, or a lone Q with no U) make far fewer. Longer racks and the presence of an
        <strong> S</strong> (which pluralises so many words) both multiply your options. The
        solver shows a running total and a per-length breakdown so you always know how many
        words are on the table.
      </p>

      <h2>Handy 2-Letter &amp; 3-Letter Words</h2>
      <p>
        The short words are where games are won — they let you play parallel to existing tiles
        and clear awkward letters. A few worth memorising:
      </p>
      <ul>
        <li><strong>High-value 2-letter words:</strong> QI, ZA, XU, XI, JO, KA, OX, AX, EX.</li>
        <li><strong>Vowel dumps:</strong> AA, AE, AI, OE, OI, OU — great for offloading spare vowels.</li>
        <li><strong>Useful 3-letter words:</strong> ZAX, JEU, QUA, WIZ, FEZ, COZ, ADZ, BIZ.</li>
      </ul>
      <p>
        Set the <strong>Word length</strong> filter to 2 or 3 to list just the short plays your
        letters can make.
      </p>

      <h2>What Is an Anagram?</h2>
      <p>
        An <strong>anagram</strong> is a word or phrase formed by rearranging the letters of
        another, using each letter once. The word comes from the Greek <em>anagrammatismos</em>,
        "to transpose letters." Anagrams have fascinated writers and puzzlers for over 2,000
        years — Lewis Carroll wove them into his stories, "astronomer" famously rearranges to
        "moon starer," and "dormitory" to "dirty room." Strictly, a <em>true</em> anagram uses
        every letter; this solver goes further and also surfaces the shorter words hiding inside
        your letters, because that is what most players actually need.
      </p>

      <h2>How to Solve Anagrams in Your Head</h2>
      <p>
        A solver is instant, but training your brain to unscramble letters is a real skill — and
        surprisingly learnable. Top Scrabble and <em>Countdown</em> players do not test every
        arrangement; they look for <strong>familiar chunks</strong> and let the word fall out.
        Here is the method they use:
      </p>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>The 5-Step Technique</h3>
            <ol>
              <li><strong>Hunt for common endings first.</strong> Scan for the letters to build
                <strong> -ING, -ED, -ERS, -EST, -IER, -ILY,</strong> or <strong>-TION</strong>.
                Locking a 3–4 letter ending in place leaves far fewer letters to juggle.</li>
              <li><strong>Pull out likely prefixes.</strong> Set aside <strong>UN-, RE-, DE-, IN-,
                MIS-,</strong> or <strong>OVER-</strong> if the letters allow it.</li>
              <li><strong>Park the vowels.</strong> Separate your vowels (A, E, I, O, U) from the
                consonants — words alternate them more than you expect, so this exposes the skeleton.</li>
              <li><strong>Spot consonant blends.</strong> Real words cluster consonants in familiar
                pairs: <strong>ST, CH, TR, BR, CL, SP, TH, GR</strong>. Keep those pairs together.</li>
              <li><strong>Rearrange the middle.</strong> With the ends and blends fixed, only a few
                loose letters remain — the answer usually clicks into place.</li>
            </ol>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Quick Anagram Tricks</h3>
            <ul>
              <li>Write the letters in a <strong>circle</strong>, not a line — a straight row tricks
                your brain into reading the original scramble over and over.</li>
              <li>An <strong>S</strong> almost always goes on the end (a plural or verb form).</li>
              <li>A <strong>Q</strong> is nearly always followed by <strong>U</strong> — pair them
                first (unless you are hunting QI, QAT or QOPH).</li>
              <li>Say the letters aloud; your ear catches sounds your eye misses.</li>
              <li>Stuck? Change <strong>one</strong> letter's position at a time rather than
                reshuffling everything at once.</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>A Worked Example: Solving RETINAS</h2>
      <p>
        Say your rack is <strong>R E T I N A S</strong> and you want the seven-letter bingo. Watch
        the technique in action:
      </p>
      <ol>
        <li><strong>Spot the ending.</strong> There is an <strong>S</strong> — set it aside as a
          likely plural ending. Now you are only unscrambling six letters: R E T I N A.</li>
        <li><strong>Find the vowels.</strong> That is A, E, I against consonants R, T, N — a clean,
          balanced set that almost always makes a word.</li>
        <li><strong>Build a familiar stem.</strong> R-T-N with two vowels suggests <strong>RETAIN</strong>,
          a very common six-letter word.</li>
        <li><strong>Add the S back.</strong> RETAIN + S → <strong>RETAINS</strong>. That is already a
          valid bingo!</li>
        <li><strong>Rearrange for more.</strong> The same seven letters also spell
          <strong> RETINAS</strong>, <strong>RETSINA</strong>, <strong>STAINER</strong>,
          <strong> STEARIN</strong> and <strong>NASTIER</strong> — one lucky rack, six ways to score.</li>
      </ol>
      <p>
        Tiles that make lots of words like this are called <strong>bingo stems</strong>. R-E-T-I-N-A-S
        is one of the most productive racks in the game — worth memorising.
      </p>

      <h2>Famous Anagram Pairs</h2>
      <p>
        Some anagrams are so neat they have become classics — the two words or phrases share every
        letter yet mean something wonderfully related (or comically opposite). A few of the best:
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', margin: '0.5rem 0 1rem', fontFamily: 'Nunito, sans-serif' }}>
          <thead>
            <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
              <th style={{ padding: '0.5rem 0.75rem', borderBottom: '2px solid var(--border)' }}>Word / Phrase</th>
              <th style={{ padding: '0.5rem 0.75rem', borderBottom: '2px solid var(--border)' }}>Its Anagram</th>
              <th style={{ padding: '0.5rem 0.75rem', borderBottom: '2px solid var(--border)' }}>Why It's Loved</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['LISTEN', 'SILENT', 'You have to be silent to truly listen.'],
              ['EARTH', 'HEART', 'The heart of our world, rearranged.'],
              ['THE EYES', 'THEY SEE', 'A perfect, poetic match.'],
              ['DORMITORY', 'DIRTY ROOM', 'Every student agrees.'],
              ['ASTRONOMER', 'MOON STARER', 'The most elegant anagram in English.'],
              ['THE MORSE CODE', 'HERE COME DOTS', 'Wordplay about wordplay.'],
              ['ELEVEN PLUS TWO', 'TWELVE PLUS ONE', 'Both equal 13 — and share every letter.'],
              ['CONVERSATIONALISTS', 'CONSERVATIONALISTS', 'The longest famous pair — 18 letters each.'],
            ].map(([a, b, note]) => (
              <tr key={a} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '0.5rem 0.75rem', fontWeight: 700, letterSpacing: 0.5 }}>{a}</td>
                <td style={{ padding: '0.5rem 0.75rem', fontWeight: 700, color: 'var(--primary)', letterSpacing: 0.5 }}>{b}</td>
                <td style={{ padding: '0.5rem 0.75rem' }}>{note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>A Delightful Anagram Showcase</h2>
      <p>
        The very best anagrams do more than reuse the letters — they rearrange a word or phrase into
        something that <em>comments</em> on the original, whether by echoing its meaning or gently
        mocking it. These full-phrase gems have delighted word lovers for generations:
      </p>
      <Ex arrow="STATUE OF LIBERTY">BUILT TO STAY FREE</Ex>
      <Ex arrow="THE CLASSROOM">SCHOOLMASTER</Ex>
      <Ex arrow="SLOT MACHINES">CASH LOST IN 'EM</Ex>
      <Ex arrow="SNOOZE ALARMS">ALAS! NO MORE Z'S</Ex>
      <Ex arrow="A GENTLEMAN">ELEGANT MAN</Ex>
      <Ex arrow="DEBIT CARD">BAD CREDIT</Ex>
      <Ex arrow="ELECTION RESULTS">LIES, LET'S RECOUNT</Ex>
      <Ex arrow="FUNERAL">REAL FUN</Ex>
      <p>
        Each one uses <strong>exactly</strong> the same letters as its source — no more, no fewer. That
        is the mark of a true anagram, and the reason a well-crafted one feels almost magical.
      </p>

      <h2>Words With the Most Anagrams (Real Data)</h2>
      <p>
        Which letters make the <em>most</em> different words? We grouped every entry in our North
        American (TWL) dictionary by its letters to find the champions — the single sets of tiles that
        rearrange into the greatest number of valid words. If you ever draw one of these racks, you are
        spoilt for choice:
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', margin: '0.5rem 0 1rem', fontFamily: 'Nunito, sans-serif' }}>
          <thead>
            <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
              <th style={{ padding: '0.5rem 0.75rem', borderBottom: '2px solid var(--border)' }}>Letters</th>
              <th style={{ padding: '0.5rem 0.75rem', borderBottom: '2px solid var(--border)' }}>Words</th>
              <th style={{ padding: '0.5rem 0.75rem', borderBottom: '2px solid var(--border)' }}>The Anagrams</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['A E S T', 9, 'ATES, EAST, EATS, ETAS, SATE, SEAT, SETA, TASE, TEAS'],
              ['A E P R S', 12, 'APERS, APRES, ASPER, PARES, PARSE, PEARS, PRASE, PRESA, RAPES, REAPS, SPARE, SPEAR'],
              ['A E L R S T', 11, 'ALERTS, ALTERS, ARTELS, ESTRAL, LASTER, RATELS, SALTER, SLATER, STALER, STELAR, TALERS'],
              ['A E I N R S T', 9, 'ANTSIER, NASTIER, RATINES, RETAINS, RETINAS, RETSINA, STAINER, STEARIN, ANESTRI'],
              ['A E G I N R S T', 8, 'ANGRIEST, ANGSTIER, ASTRINGE, GANISTER, GANTRIES, GRANITES, INGRATES, RANGIEST'],
            ].map(([letters, count, words]) => (
              <tr key={letters} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '0.5rem 0.75rem', fontWeight: 700, letterSpacing: 1, whiteSpace: 'nowrap' }}>{letters}</td>
                <td style={{ padding: '0.5rem 0.75rem', fontWeight: 800, color: 'var(--primary)' }}>{count}</td>
                <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.92rem' }}>{words}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        Notice a pattern: the winners are packed with the game's most common tiles — <strong>A, E, R, S,
        T, N, I</strong> — the letters that combine most freely. The seven-letter champion above even
        includes <strong>RETINAS</strong>, the very rack we unscrambled earlier. A few entries (like
        APRES, ESTRAL or ANESTRI) are obscure but fully valid Scrabble words — exactly the kind our
        solver surfaces that you would never think of yourself. Counts differ slightly between the TWL,
        SOWPODS and Words With Friends dictionaries, so switch dictionaries to see the totals change.
      </p>

      <h2>Anagram vs. Unscramble vs. Jumble vs. Scrabble — What's the Difference?</h2>
      <p>
        People use these terms interchangeably, but each has a slightly different flavour. Here is how
        they relate, and which of our tools fits each job:
      </p>
      <ul>
        <li><strong>Anagram</strong> — rearranging letters into a new word or phrase. Strictly, a true
          anagram uses <em>every</em> letter; loosely, people mean "make words from these letters,"
          which is exactly what this <Link to="/anagram-solver">Anagram Solver</Link> does.</li>
        <li><strong>Unscramble</strong> — the everyday word for finding all the words hidden in a set
          of jumbled letters, sub-words included. Our <Link to="/unscramble">Word Unscrambler</Link>
          is built for that.</li>
        <li><strong>Jumble</strong> — the classic newspaper puzzle where you unscramble one word at a
          time, then combine circled letters. Try the <Link to="/jumble">Jumble Solver</Link>.</li>
        <li><strong>Scrabble</strong> — here the goal is not just any word but the
          <strong> highest-scoring</strong> legal play from your rack. The
          <Link to="/scrabble"> Scrabble Word Finder</Link> ranks results by points.</li>
      </ul>
      <p>
        Want only exact, full-length rearrangements (same letters, nothing shorter)? Use the
        <Link to="/anagram-generator"> Anagram Generator</Link> instead.
      </p>

      <h2>Anagram Glossary</h2>
      <ul>
        <li><strong>Anagram</strong> — a word or phrase made by rearranging all the letters of another
          (LISTEN → SILENT).</li>
        <li><strong>Sub-anagram</strong> — a shorter word made from <em>some</em> of the letters
          (LISTEN → TILES). This solver finds these too.</li>
        <li><strong>Pangram</strong> — a sentence using every letter of the alphabet at least once
          ("The quick brown fox jumps over the lazy dog"). Try our
          <Link to="/pangram-checker"> Pangram Checker</Link>.</li>
        <li><strong>Palindrome</strong> — a word that reads the same forwards and backwards (LEVEL,
          KAYAK). Check one with the <Link to="/palindrome">Palindrome Checker</Link>.</li>
        <li><strong>Isogram</strong> — a word with no repeating letters (SUBDERMATOGLYPHIC, at 17
          letters, is the longest common one).</li>
      </ul>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'What is an anagram solver?',
          a: 'An anagram solver rearranges the letters you enter and finds every valid word you can make from them — from full-length anagrams down to shorter words — checked against a real dictionary of 190,000+ words. Results are grouped by length and shown with their Scrabble score.' },
        { q: 'Can I use blank or wildcard tiles?',
          a: 'Yes. Type a question mark (?) for each blank tile — up to two, just like Scrabble and Words With Friends. Each ? can stand in for any letter, so "ca?" finds CAB, CAP, CAT, CAR and more.' },
        { q: 'Does the anagram solver work for Scrabble and Words With Friends?',
          a: 'Yes. Pick the TWL (North American), SOWPODS (international Collins), Words With Friends, or All Dictionaries option and every result is valid in that game. Each word shows its point value so you can spot the best play.' },
        { q: 'What is the difference between an anagram solver and a word unscrambler?',
          a: 'They do the same core job — both find every word you can make from a set of letters. "Anagram solver" is just the more common search term. If you want only exact, full-length rearrangements (same letters, nothing shorter), use our Anagram Generator instead.' },
        { q: 'How many words can I make from my letters?',
          a: 'It depends on your letters. Common letters and vowel-rich combinations (like the tiles in RETINAS or LISTEN) can make dozens of words, while awkward tiles make far fewer. The solver shows a running total and breaks results down by word length.' },
        { q: 'Is using an anagram solver cheating?',
          a: 'It is a learning and practice tool. Many players use it after a game to review missed words, to settle whether a play is valid, or to train their pattern recognition. Whether to use it mid-game is up to you and the people you play with.' },
      ]} />

      <h2>Related Tools</h2>
      <Related links={[['🎲 Anagram Generator','/anagram-generator'],['🔍 Word Unscrambler','/unscramble'],['🃏 Scrabble Finder','/scrabble'],['🔀 Jumble Solver','/jumble']]} />
    </div>
  ),

  /* ══ ANAGRAM GENERATOR ═══════════════════════════════════════════════════ */
  'anagram-generator': () => (
    <div className="seo-section">
      <h2>Anagram Generator — Turn Any Name or Word Into Anagrams</h2>
      <p>
        An <strong>anagram generator</strong> rearranges the letters of a word, name, or phrase into
        brand-new words and phrases that use <strong>exactly the same letters</strong>. This free
        <strong> anagram maker</strong> does more than most: it finds single-word anagrams
        <em> and</em> clever <strong>multi-word anagrams</strong>, so you can
        <strong> anagram your name</strong>, invent a username, brainstorm a brand, or just enjoy
        the wordplay — all instantly, with no sign-up.
      </p>
      <p>
        The practice is ancient: Greek and Roman writers believed the anagram of a person's name
        revealed their true nature, Renaissance courts employed professional anagrammatists, and
        Galileo even announced discoveries as anagrams to stake his claim in secret. Today, anagram
        makers power branding, puzzle design, party games, and creative writing.
      </p>

      <h2>How to Use the Anagram Generator</h2>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>In Four Steps</h3>
            <ol>
              <li>Type a word, your <strong>full name</strong>, or any letters (spaces are fine).</li>
              <li>Keep <strong>"Include multi-word anagrams"</strong> ticked for names and phrases.</li>
              <li>Pick a dictionary — TWL, SOWPODS, Words With Friends, or All.</li>
              <li>Hit <strong>Generate</strong>, then click any phrase to copy it.</li>
            </ol>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>What You Get</h3>
            <Ex arrow="CINEMA">ICEMAN, ANEMIC · CAME IN, ICE MAN</Ex>
            <Ex arrow="DORMITORY">DIRTY ROOM, DIRTY MOOR</Ex>
            <Ex arrow="CLINT EASTWOOD">DISLOCATE TOWN, OLD WEST ACTION</Ex>
            <Ex arrow="ELVIS">LIVES, VEILS, EVILS</Ex>
          </div>
        </div>
      </div>

      <h2>How to Make an Anagram of Your Name</h2>
      <p>
        Name anagrams are the most popular reason people reach for a generator — and the reason our
        <strong> multi-word</strong> mode exists. To <strong>anagram your name</strong>:
      </p>
      <ol>
        <li>Enter your <strong>first and last name</strong> together (add a middle name or initial for
          even more letters to play with).</li>
        <li>Leave multi-word mode on — a name rarely rearranges into a single word, but it often makes
          a great phrase.</li>
        <li>Scan the <strong>Multi-Word Anagrams</strong> list; the neatest results usually have the
          fewest, longest words.</li>
        <li>Click one to copy it for your username, gamertag, or bio.</li>
      </ol>
      <p>
        Some of history's most famous examples are name anagrams: J.K. Rowling hid
        <strong> "I am Lord Voldemort"</strong> inside <strong>Tom Marvolo Riddle</strong>, and
        <strong> Britney Spears</strong> is a perfect anagram of <strong>Presbyterians</strong>.
      </p>

      <h2>Funny Anagram Generator</h2>
      <p>
        Want a laugh instead of a puzzle? Switch the <strong>Anagram Style</strong> to
        <strong> 😂 Funny</strong> and the generator becomes a <strong>funny anagram generator</strong>:
        it rearranges every letter into the silliest, most sentence-like phrases and ranks the funniest
        first. Funny mode favours short, common, concrete words — the kind that read like an absurd
        little sentence — so your name might turn into something gloriously ridiculous. Try it on your
        full name, a friend's name, or a favourite phrase and share the best one.
      </p>
      <p>A few classic funny anagrams to show what's possible:</p>
      <Ex arrow="A DECIMAL POINT">I'M A DOT IN PLACE</Ex>
      <Ex arrow="THE EARTHQUAKES">THAT QUEER SHAKE</Ex>
      <Ex arrow="PUNISHMENT">NINE THUMPS</Ex>
      <Ex arrow="VACATION TIMES">I'M NOT AS ACTIVE</Ex>
      <Ex arrow="SLOT MACHINES">CASH LOST IN 'EM</Ex>

      <h2>Single-Word vs. Multi-Word Anagrams</h2>
      <p>
        Our generator produces two kinds of results, and understanding the difference helps you get
        what you want:
      </p>
      <ul>
        <li><strong>Exact (single-word) anagrams</strong> — one new word using all your letters, like
          CINEMA → ICEMAN. Every result is scored with its Scrabble value.</li>
        <li><strong>Multi-word anagrams</strong> — every letter rearranged into a readable two- or
          three-word phrase, like DORMITORY → DIRTY ROOM. This is what makes names and long words work,
          since they seldom form a single valid word.</li>
      </ul>
      <p>
        Turn multi-word mode off if you only want strict single-word anagrams. Want the shorter words
        hiding inside your letters instead? That is a job for the
        <Link to="/anagram-solver"> Anagram Solver</Link>.
      </p>

      <h2>Famous Name &amp; Phrase Anagrams</h2>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', margin: '0.5rem 0 1rem', fontFamily: 'Nunito, sans-serif' }}>
          <thead>
            <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
              <th style={{ padding: '0.5rem 0.75rem', borderBottom: '2px solid var(--border)' }}>Name / Phrase</th>
              <th style={{ padding: '0.5rem 0.75rem', borderBottom: '2px solid var(--border)' }}>Anagram</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['TOM MARVOLO RIDDLE', 'I AM LORD VOLDEMORT'],
              ['BRITNEY SPEARS', 'PRESBYTERIANS'],
              ['CLINT EASTWOOD', 'OLD WEST ACTION'],
              ['ALEC GUINNESS', 'GENUINE CLASS'],
              ['ASTRONOMER', 'MOON STARER'],
              ['DORMITORY', 'DIRTY ROOM'],
            ].map(([a, b]) => (
              <tr key={a} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '0.5rem 0.75rem', fontWeight: 700, letterSpacing: 0.5 }}>{a}</td>
                <td style={{ padding: '0.5rem 0.75rem', fontWeight: 700, color: 'var(--primary)', letterSpacing: 0.5 }}>{b}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>Creative Uses for an Anagram Maker</h2>
      <ul>
        <li><strong>Usernames &amp; gamertags:</strong> an anagram of your name gives you a handle that is unique yet still personal.</li>
        <li><strong>Pen names &amp; brand names:</strong> anagramming a core word produces memorable, one-of-a-kind names.</li>
        <li><strong>Puzzle &amp; party games:</strong> seed word searches, crosswords, and quizzes with ready-made anagrams.</li>
        <li><strong>Classroom activities:</strong> a fun, memorable way to teach spelling and letter patterns.</li>
        <li><strong>Gifts &amp; cards:</strong> a personalised name anagram makes a clever, thoughtful touch.</li>
      </ul>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'What is an anagram generator?',
          a: 'An anagram generator takes a word, name, or set of letters and rearranges them into new words and phrases that use exactly the same letters. Ours finds both single-word anagrams and clever multi-word anagrams, and scores each result.' },
        { q: 'How do I make an anagram of my name?',
          a: 'Type your name into the box (spaces are fine), keep "Include multi-word anagrams" ticked, and generate. The tool rearranges all the letters of your name into phrases — for example CLINT EASTWOOD becomes DISLOCATE TOWN.' },
        { q: 'Can it create multi-word anagrams and phrases?',
          a: 'Yes. Multi-word mode rearranges every letter into readable two- and three-word phrases, which is what makes name anagrams work. Turn it off if you only want exact single-word anagrams.' },
        { q: 'Can it make funny anagrams?',
          a: 'Yes — switch the Anagram Style to "Funny" and the generator rearranges every letter into the silliest, most sentence-like phrases, ranking the funniest results first. It is a great way to get a laugh out of your own name.' },
        { q: 'What is the difference between the Anagram Generator and the Anagram Solver?',
          a: 'The Generator creates true anagrams that use all your letters, including multi-word phrases — ideal for names and creative wordplay. The Anagram Solver finds every word you can make from your letters, including shorter sub-words. Use whichever fits your goal.' },
        { q: 'Is the anagram generator free?',
          a: 'Completely free, with no sign-up and no limits. Generate as many anagrams as you like from any word, name, or letters.' },
        { q: 'How can I get more or better anagrams?',
          a: 'Try switching the dictionary (SOWPODS and All Dictionaries return more options than TWL), make sure multi-word mode is on for names, and experiment with adding a middle name or initials for extra letters to rearrange.' },
      ]} />

      <h2>Related Tools</h2>
      <Related links={[['🔤 Anagram Solver','/anagram-solver'],['🔍 Word Unscrambler','/unscramble'],['🃏 Scrabble Finder','/scrabble'],['🎵 Rhyme Finder','/rhyme']]} />
    </div>
  ),

  /* ══ CROSSWORD SOLVER ════════════════════════════════════════════════════ */
  crossword: () => (
    <div className="seo-section">
      <h2>Crossword Solver — Find Crossword Clues &amp; Answers Instantly</h2>
      <p>
        Stuck on a stubborn clue? Our <strong>crossword solver</strong> turns the letters
        you already have into a shortlist of <strong>crossword clue answers</strong> in a single click.
        Choose how many letters the answer has, fill in the squares you know from crossing clues,
        leave the unknown squares blank, and the solver checks every possibility against a
        dictionary of more than <strong>190,000 words</strong> — then shows only the words that fit.
        Click any answer to read its meaning and confirm it's the one your <strong>crossword clue</strong>{' '}
        is looking for.
      </p>
      <p>
        It works for every kind of puzzle — the daily newspaper grid, the <em>New York Times</em>{' '}
        crossword, cryptic crosswords, quick crosswords, and puzzle apps. Whenever you know the
        answer length and a few crossing letters, this is the fastest way to find the
        <strong> crossword puzzle answers</strong> you're missing.
      </p>

      <h2>How to Use the Crossword Solver to Get the Answers</h2>
      <p>
        There are two ways to reach an answer, and you can combine them. Solve by the{' '}
        <strong>letters you already have</strong>, or — when you understand what the clue means —
        solve by the <strong>clue itself</strong>. Most of the time you'll do both: enter the length
        and any crossing letters, then add the clue to rank the results by meaning.
      </p>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Method 1 — Solve by the Letters</h3>
            <ol>
              <li><strong>Pick the number of letters</strong> in the answer (2–15) — just count the blank squares in the grid.</li>
              <li><strong>Type the letters you know</strong> into the boxes and leave the unknown squares empty; each empty box acts as a wildcard.</li>
              <li><strong>Read the matching crossword answers</strong> and click any word to check its meaning.</li>
            </ol>
            <p className="mb-0 small">
              Tip: every crossing letter you add trims the list — one extra letter often halves the results.
            </p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Method 2 — Solve by the Clue</h3>
            <ol>
              <li><strong>Set the number of letters</strong> (and any letters you have) as above.</li>
              <li><strong>Type the clue's meaning</strong> into the optional Clue box — e.g. <em>sea eagle</em>, <em>opera solo</em>, or <em>poker stake</em>.</li>
              <li><strong>Get answers ranked by definition</strong>, each shown with its meaning so you can confirm the fit.</li>
            </ol>
            <p className="mb-0 small">
              Works best for dictionary-style clues. It can't resolve names or places (like PARIS),
              which crossword wordlists leave out — use the letter pattern for those.
            </p>
          </div>
        </div>
      </div>
      <div className="seo-card mt-3">
        <h3>Example Answers</h3>
        <Ex arrow="c_t">cat, cot, cut, cit</Ex>
        <Ex arrow="_ight">light, night, right, sight, might, tight, fight</Ex>
        <Ex arrow="h__se">house, horse, those</Ex>
        <Ex arrow="sea eagle (4)">ERNE</Ex>
        <Ex arrow="poker stake (4)">ANTE</Ex>
      </div>

      <h2>How to Solve a Crossword Clue</h2>
      <p>
        A crossword clue gives you two things: a definition (or wordplay) and a letter count.
        You rarely need to crack the wordplay to make progress — once a few crossing answers
        give you letters, the pattern alone usually reveals the answer. Here's the reliable method:
      </p>
      <ul>
        <li><strong>Count the squares.</strong> The number of blank squares is the exact length of the crossword answer — set that as your letter count.</li>
        <li><strong>Fill in every crossing letter.</strong> Letters shared with answers you've already solved are the biggest clues you have. Enter them in the matching boxes.</li>
        <li><strong>Let the solver do the rest.</strong> With the length and a couple of letters locked in, the crossword clue solver lists only the words that fit — often just one or two.</li>
        <li><strong>Confirm the meaning.</strong> Click an answer to check its definition against the clue's wording before you write it in.</li>
      </ul>

      <h2>Crossword Solving Strategies</h2>
      <ul>
        <li><strong>Use crossing letters aggressively.</strong> Every letter you confirm from a crossing answer dramatically reduces the candidate crossword answers for an unknown word.</li>
        <li><strong>Count letters precisely.</strong> Miscounting by one letter is the most common mistake; re-count the blank squares in the grid before you set the answer length.</li>
        <li><strong>Solve the easy clues first.</strong> Work through the clues you know across the whole puzzle before the hard ones — each solved answer hands you crossing letters for the next.</li>
        <li><strong>Look for common short answers.</strong> Crossword constructors love 3-letter answers like ERA, ATE, ORE, EEL, and ALT — learn the frequent ones.</li>
        <li><strong>Know your crosswordese.</strong> Words rarely used in daily speech (ETUI, OLEO, ERNE, SNEE, ADIT) turn up constantly in crossword answers — keep them in mind.</li>
        <li><strong>Watch clue signals.</strong> A question mark hints at a pun, an abbreviation in the clue signals an abbreviated answer, and "perhaps" or "say" often flags an example.</li>
      </ul>

      <h2>Crossword Answers vs. Anagram &amp; Word Solvers</h2>
      <p>
        A <strong>crossword solver</strong> matches a fixed letter pattern — you know some
        positions and the total length. That's different from an <Link to="/anagram-solver">anagram
        solver</Link>, which rearranges a set of letters into new words, or a{' '}
        <Link to="/unscramble">word unscrambler</Link>, which finds every word hidden in a jumble
        of tiles. If your clue is really a jumble or you have loose letters rather than a pattern,
        those tools are the better fit; for a numbered grid with crossing letters, the crossword
        clue solver above is the one you want.
      </p>

      <h2>Most Common Crossword Answers (Crosswordese)</h2>
      <p>
        Some words appear in crosswords far more often than in everyday writing, simply because
        their friendly vowel-heavy spelling helps constructors fill the grid. Puzzlers call this
        vocabulary <strong>“crosswordese.”</strong> Learn the regulars below and you'll recognise
        a <strong>crossword answer</strong> the moment you read the clue:
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', margin: '0.5rem 0 1rem', fontFamily: 'Nunito, sans-serif' }}>
          <thead>
            <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
              <th style={{ padding: '0.5rem 0.75rem', borderBottom: '2px solid var(--border)' }}>Answer</th>
              <th style={{ padding: '0.5rem 0.75rem', borderBottom: '2px solid var(--border)' }}>Letters</th>
              <th style={{ padding: '0.5rem 0.75rem', borderBottom: '2px solid var(--border)' }}>Typical Clue</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['OREO', 4, 'Chocolate sandwich cookie'],
              ['ERIE', 4, 'Great Lake bordering Ohio'],
              ['OLEO', 4, 'Butter substitute'],
              ['ETUI', 4, 'Small case for needles'],
              ['ERNE', 4, 'Sea eagle'],
              ['ALOE', 4, 'Soothing succulent'],
              ['ARIA', 4, 'Opera solo'],
              ['EWER', 4, 'Wide-mouthed water pitcher'],
              ['EPEE', 4, 'Fencing sword'],
              ['ERA', 3, 'Historical period; a baseball stat'],
              ['ADO', 3, 'Fuss or commotion'],
              ['ODE', 3, 'Poem of praise'],
              ['ANTE', 4, 'Poker stake before the deal'],
              ['ASEA', 4, 'Out on the ocean'],
            ].map(([w, n, clue]) => (
              <tr key={w} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '0.5rem 0.75rem', fontWeight: 700, letterSpacing: 0.5, color: 'var(--primary)' }}>{w}</td>
                <td style={{ padding: '0.5rem 0.75rem', fontWeight: 700 }}>{n}</td>
                <td style={{ padding: '0.5rem 0.75rem' }}>{clue}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>Common Crossword Abbreviations</h2>
      <p>
        When a clue is itself abbreviated — or ends with “for short,” “briefly,” or “Abbr.” — the
        answer is an abbreviation too. These short forms show up in grids constantly:
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', margin: '0.5rem 0 1rem', fontFamily: 'Nunito, sans-serif' }}>
          <thead>
            <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
              <th style={{ padding: '0.5rem 0.75rem', borderBottom: '2px solid var(--border)' }}>Answer</th>
              <th style={{ padding: '0.5rem 0.75rem', borderBottom: '2px solid var(--border)' }}>Clue Signal</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['ASST', 'Assistant, for short'],
              ['ATTY', 'Lawyer, briefly'],
              ['MGR', 'Team boss, for short'],
              ['SGT', 'Sergeant, briefly'],
              ['ETC', '“And so on,” abbreviated'],
              ['APR', 'Spring mo.'],
              ['AVE', 'Cross-street abbr.'],
              ['INC', 'Corporate name ender'],
            ].map(([w, clue]) => (
              <tr key={w} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '0.5rem 0.75rem', fontWeight: 700, letterSpacing: 0.5, color: 'var(--primary)' }}>{w}</td>
                <td style={{ padding: '0.5rem 0.75rem' }}>{clue}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>How to Solve Cryptic Crossword Clues</h2>
      <p>
        A <strong>cryptic crossword clue</strong> is really two clues in one: a straight{' '}
        <em>definition</em> (at the start or end of the clue) plus a piece of <em>wordplay</em>{' '}
        that spells the same answer a second way. The letter count is your anchor. Once you spot
        which wordplay device is in play — and the little “indicator” words that flag it — cryptic
        answers fall into place. Here are the six devices you'll meet most often:
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', margin: '0.5rem 0 1rem', fontFamily: 'Nunito, sans-serif' }}>
          <thead>
            <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
              <th style={{ padding: '0.5rem 0.75rem', borderBottom: '2px solid var(--border)' }}>Device</th>
              <th style={{ padding: '0.5rem 0.75rem', borderBottom: '2px solid var(--border)' }}>Indicator Words</th>
              <th style={{ padding: '0.5rem 0.75rem', borderBottom: '2px solid var(--border)' }}>Example → Answer</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Anagram', 'mixed, cooked, broken, arranged, wild', '“Hear silent broadcast (6)” → LISTEN'],
              ['Hidden word', 'in, some, part of, hiding', '“Organ concealed in the article (5)” → HEART'],
              ['Reversal', 'back, returning, up, recalled', '“Rats returning as a celebrity (4)” → STAR'],
              ['Charade', '(parts joined in order)', '“Vehicle + pet on the floor (6)” → CARPET'],
              ['Double definition', '(two meanings side by side)', '“Just carnival (4)” → FAIR'],
              ['Homophone', 'we hear, sounds like, reportedly', '“Feeble, reportedly, for seven days (4)” → WEEK'],
            ].map(([device, ind, ex]) => (
              <tr key={device} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '0.5rem 0.75rem', fontWeight: 700 }}>{device}</td>
                <td style={{ padding: '0.5rem 0.75rem', fontStyle: 'italic' }}>{ind}</td>
                <td style={{ padding: '0.5rem 0.75rem' }}>{ex}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        Tip: in the anagram example, “broadcast” signals that the letters of <strong>SILENT</strong>{' '}
        should be rearranged, and “Hear” is the definition of the answer <strong>LISTEN</strong> —
        same six letters, two different ways. Whenever you already have a crossing letter or two,
        drop them into the solver above and let the pattern confirm your cryptic answer.
      </p>

      <h2>Crossword Answers by Number of Letters</h2>
      <p>
        Most searches for help start with a length — “four-letter word for …” or “a 5-letter
        crossword answer.” The solver above is built exactly for that: set the number of letters
        first, and every result is guaranteed to be the right length. As a rough guide,{' '}
        <strong>3-letter answers</strong> lean on crosswordese and abbreviations (ERA, ADO, ODE),{' '}
        <strong>4- and 5-letter answers</strong> are the workhorses of most grids, and{' '}
        <strong>longer answers</strong> are usually the themed entries — so lock in any crossing
        letters you have and let them do the heavy lifting.
      </p>

      <h2>Famous Crossword Puzzles Worth Trying</h2>
      <p>
        The crossword was born on <strong>December 21, 1913</strong>, when journalist Arthur Wynne
        published a diamond-shaped “word-cross” in the <em>New York World</em>. A century later it's
        a global daily ritual — and a handful of puzzles have become institutions in their own right.
        Once you've cracked a few clues with the solver above, these are the grids worth graduating to:
      </p>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>🗽 The New York Times Crossword</h3>
            <p>
              The gold standard of American puzzles. It debuted in 1942 and, under editor Will
              Shortz since 1993, ramps up from a gentle Monday to a fiendish Saturday, with an
              oversized Sunday grid. It's also home to legendary trick puzzles — including the 1996
              Election Day grid that read as both “CLINTON ELECTED” and “BOB DOLE ELECTED.”
            </p>
            <p className="mb-0">
              <a href="https://www.nytimes.com/crosswords" target="_blank" rel="noopener noreferrer">Play the NYT Crossword →</a>
            </p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>📰 The Guardian Cryptic</h3>
            <p>
              Britain's best-loved free cryptic, famous for witty, mischievous setters — none more
              legendary than <strong>Araucaria</strong> (the Rev. John Graham), who in 2013 quietly
              announced his own illness through the answers of a single crossword. A perfect place
              to fall in love with wordplay.
            </p>
            <p className="mb-0">
              <a href="https://www.theguardian.com/crosswords" target="_blank" rel="noopener noreferrer">Solve the Guardian Cryptic →</a>
            </p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>⏱ The Times Crossword (London)</h3>
            <p>
              The UK's most prestigious cryptic, running since 1930 with anonymous setters and its
              own national <strong>Crossword Championship</strong> — where the fastest solvers in
              Britain race to finish fiendish grids against the clock.
            </p>
            <p className="mb-0">
              <a href="https://www.thetimes.co.uk/puzzles/crossword" target="_blank" rel="noopener noreferrer">Try The Times Crossword →</a>
            </p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>🌴 The LA Times Crossword</h3>
            <p>
              One of the most widely syndicated daily puzzles in America, prized for its smooth,
              accessible fill and clever themes — a friendly step up for solvers moving beyond the
              beginner grids, and free to play online every day.
            </p>
            <p className="mb-0">
              <a href="https://www.latimes.com/games/daily-crossword" target="_blank" rel="noopener noreferrer">Play the LA Times Crossword →</a>
            </p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>💼 The Wall Street Journal Crossword</h3>
            <p>
              A daily favourite that saves its best trick for Friday: the <strong>contest (meta)
              puzzle</strong>, where solving the grid is only step one — a hidden meta-answer waits
              beyond it, and readers race to email in the solution.
            </p>
            <p className="mb-0">
              <a href="https://www.wsj.com/games/crosswords?mod=WSJ_crosswords_play" target="_blank" rel="noopener noreferrer">Open the WSJ Crossword →</a>
            </p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>📱 USA Today Crossword</h3>
            <p>
              Bright, modern, and famously beginner-friendly, the USA Today puzzle has championed a
              new generation of diverse constructors — an easy, welcoming daily solve if you're just
              getting hooked on crosswords.
            </p>
            <p className="mb-0">
              <a href="https://www.usatoday.com/crossword/" target="_blank" rel="noopener noreferrer">Play the USA Today Crossword →</a>
            </p>
          </div>
        </div>
      </div>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'How do I find a crossword answer with this solver?',
          a: 'Pick the number of letters in the answer, then type any letters you already have from crossing clues and leave the unknown squares blank. The solver instantly lists every word that fits your crossword clue — click any answer to read its meaning.' },
        { q: 'Can the crossword solver read a clue description?',
          a: 'It solves by answer length and known letters, not by interpreting the clue text. Once you know the length and a couple of crossing letters, the pattern matcher narrows thousands of possible crossword answers down to a short, workable list.' },
        { q: 'What if my crossword clue has too many possible answers?',
          a: 'Add more known letters from crossing answers. Even one extra confirmed letter usually cuts the list of possible crossword answers in half, and two or three will often leave just one match.' },
        { q: 'Does it include abbreviations and proper nouns?',
          a: 'The dictionary focuses on common English words used in most crossword wordlists and excludes the majority of proper nouns. If a clue clearly points to a name or place, combine the pattern results with your own general knowledge.' },
        { q: 'Is the crossword clue solver free?',
          a: 'Yes — WordZingo\'s crossword clue solver is completely free, works on phone and desktop, needs no sign-up, and returns unlimited crossword answers.' },
      ]} />

      <h2>Related Tools</h2>
      <Related links={[['🔀 Jumble Solver','/jumble'],['🔍 Word Unscrambler','/unscramble'],['💡 Wordle Solver','/wordle-solver'],['🎵 Rhyme Finder','/rhyme']]} />
    </div>
  ),

  /* ══ WORDLE SOLVER ═══════════════════════════════════════════════════════ */
  'wordle-solver': () => (
    <div className="seo-section">
      <h2>About Wordle and Our Solver</h2>
      <p>
        <strong>Wordle</strong> was created in 2021 by Brooklyn software engineer Josh Wardle
        as a gift for his word-puzzle-loving partner. After the <em>New York Times</em>
        acquired it in January 2022 for a reported seven-figure sum, it became a global
        daily phenomenon with millions of solvers worldwide. The rules are elegantly simple:
        guess a secret 5-letter word in six tries, using colour-coded feedback after each guess.
      </p>
      <p>
        Our <strong>Wordle Solver</strong> uses the same constraint logic that information-theory
        experts apply: it filters the full list of valid 5-letter words down to only those
        consistent with every colour clue you've received. As you add more guesses and clues,
        the candidate list shrinks — often to just 1 or 2 words within 4 guesses.
      </p>

      <h2>Understanding the Colour System</h2>
      <div className="row g-3">
        <div className="col-md-4">
          <div className="seo-card" style={{borderLeftColor:'#10b981'}}>
            <h3 style={{color:'#10b981'}}>🟩 Green</h3>
            <p>The letter is in the word AND in the correct position. Mark the cell green.</p>
          </div>
        </div>
        <div className="col-md-4">
          <div className="seo-card" style={{borderLeftColor:'#f59e0b'}}>
            <h3 style={{color:'#f59e0b'}}>🟨 Yellow</h3>
            <p>The letter IS in the word but in the WRONG position. Mark it yellow.</p>
          </div>
        </div>
        <div className="col-md-4">
          <div className="seo-card" style={{borderLeftColor:'#6b7280'}}>
            <h3 style={{color:'#6b7280'}}>⬛ Gray</h3>
            <p>The letter is NOT in the word at all. Mark it gray to eliminate it.</p>
          </div>
        </div>
      </div>

      <h2>Best Starting Words for Wordle</h2>
      <p>
        Information theory suggests starting with words that cover the most common letters.
        Studies and simulations consistently rank these openers highest:
      </p>
      <ul>
        <li><strong>CRANE</strong> — covers C, R, A, N, E (high-frequency, no repeats)</li>
        <li><strong>STARE</strong> — covers S, T, A, R, E</li>
        <li><strong>AUDIO</strong> — covers 4 of 5 vowels in one guess</li>
        <li><strong>RAISE</strong> — beloved by many top solvers</li>
        <li><strong>SLATE</strong> — eliminates common consonants alongside frequent vowels</li>
      </ul>
      <p>
        After your opener, let the solver's filtered list guide your second guess. Choose a
        word that tests as many unconfirmed letters as possible — don't just repeat known
        yellow letters in the same position.
      </p>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'How many 5-letter words are in the solver\'s dictionary?',
          a: 'Over 6,000 five-letter English words — a superset of the official Wordle answer list. The solver shows up to 50 top candidates at a time.' },
        { q: 'What is the best Wordle strategy?',
          a: 'Maximize information per guess. An ideal second guess tests letters not yet tried. Avoid guessing a word that repeats a confirmed letter in the same position — you gain zero new information from that.' },
        { q: 'Can I use the solver mid-game without giving away the answer?',
          a: 'Yes — the solver only knows what you tell it. Enter your actual guesses with their colours and let it suggest the next most informative word.' },
      ]} />

      <h2>Related Tools</h2>
      <Related links={[['🟩 Play Wordle','/wordle'],['✏️ Crossword Solver','/crossword'],['🔤 Anagram Solver','/anagram-solver'],['🔀 Jumble Solver','/jumble']]} />
    </div>
  ),

  /* ══ BOGGLE SOLVER ═══════════════════════════════════════════════════════ */
  boggle: () => (
    <div className="seo-section">
      <h2>About This Boggle Solver</h2>
      <p>
        Our <strong>Boggle Solver</strong> supports four board sizes — <strong>3×3, 4×4, 5×5, and 6×6</strong> —
        so you can solve any variant of the classic Boggle game. Enter your letters into the grid, click
        <em> Find All Words</em>, and the solver returns every valid word traceable through adjacent cells in
        milliseconds. Use <em>Random Board</em> to generate a practice grid or <em>Clear Board</em> to reset
        all cells and start over.
      </p>
      <p>
        Under the hood the solver uses a <em>Trie data structure</em> (a specialised prefix tree) combined
        with depth-first search (DFS). It prunes dead-end paths early, which is why even a 6×6 grid
        (36 cells) returns results almost instantly. Some boards yield <strong>400+ valid words</strong> —
        far more than any human could find in a timed game!
      </p>

      <h2>Board Size Guide</h2>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>3×3 Boggle (9 cells)</h3>
            <p>A beginner-friendly variant. Fewer adjacencies mean fewer long words, making it great for
            learners or a quick warm-up before tackling larger boards.</p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>4×4 Boggle (16 cells) — Classic</h3>
            <p>The standard Boggle board. 16 lettered dice, 3-minute timer, words scored by length.
            This is the version used in the original Parker Brothers / Hasbro game sold worldwide.</p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>5×5 Boggle (25 cells)</h3>
            <p>A tougher challenge with significantly more adjacency paths. Expect far more 6–8 letter
            words and a much higher ceiling for total score.</p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>6×6 Boggle (36 cells)</h3>
            <p>The hardest variant. Competitive Boggle tournaments sometimes use this size. The sheer
            number of possible paths makes manual solving nearly impossible — our solver handles it instantly.</p>
          </div>
        </div>
      </div>

      <h2>Boggle Rules You Need to Know</h2>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Core Rules</h3>
            <ul>
              <li>Letters must be <strong>adjacent</strong> (including diagonals).</li>
              <li>Each die can only be used <strong>once per word</strong>.</li>
              <li>Words must be at least <strong>3 letters</strong> long.</li>
              <li>Proper nouns and abbreviations do NOT count.</li>
              <li>The <strong>Qu</strong> die counts as two letters (Q+U).</li>
            </ul>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Official Scoring Table</h3>
            <ul>
              <li>3 letters → <strong>1 point</strong></li>
              <li>4 letters → <strong>1 point</strong></li>
              <li>5 letters → <strong>2 points</strong></li>
              <li>6 letters → <strong>3 points</strong></li>
              <li>7 letters → <strong>5 points</strong></li>
              <li>8+ letters → <strong>11 points</strong></li>
            </ul>
          </div>
        </div>
      </div>

      <h2>How to Use the Boggle Solver</h2>
      <ol>
        <li><strong>Choose a board size</strong> — 3×3, 4×4, 5×5, or 6×6.</li>
        <li><strong>Fill in the grid</strong> — click each cell and type a letter. Cells auto-advance left-to-right. Use <em>Random Board</em> to auto-fill or <em>Clear Board</em> to wipe all entries.</li>
        <li><strong>Set minimum word length</strong> — filter out short words to focus on high-value finds.</li>
        <li><strong>Click "Find All Words"</strong> — results appear grouped by length, longest first.</li>
        <li>Focus your in-game time on 7+ letter words — they score 5–11 points and opponents rarely spot them.</li>
      </ol>

      <h2>Competitive Boggle Tips</h2>
      <ul>
        <li><strong>Scan for -ER, -ING, -ED, -TION suffixes</strong> — they multiply the number of words you can build from a single cluster.</li>
        <li><strong>Find the longest word first</strong> and work backwards, noting all sub-words within it.</li>
        <li><strong>Check all 8 directions</strong> from every high-value letter (Q, J, X, Z).</li>
        <li><strong>Practise common 3-letter combos</strong> — ATE, ARE, OAR, EAR — so you spot them instantly.</li>
        <li><strong>Remember plurals and verb forms</strong> — STAR gives STARS, STARE, STARED, STARES.</li>
        <li><strong>On 5×5 and 6×6 boards</strong>, look for long words crossing the centre — more cells mean more paths.</li>
      </ul>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'Which board size should I use?',
          a: 'Use 4×4 for standard Boggle practice, 3×3 for quick sessions or beginners, and 5×5 / 6×6 if you play competitive variants or want an extra challenge.' },
        { q: 'Can I clear the board without changing its size?',
          a: 'Yes — click the red "Clear Board" button to wipe all letters while keeping the current grid size.' },
        { q: 'Do diagonal connections count?',
          a: 'Yes — a letter can connect to all 8 surrounding cells (up, down, left, right, and all 4 diagonals). Our solver accounts for diagonal adjacency on every board size.' },
        { q: 'Why does the solver find words I\'ve never seen before?',
          a: 'English contains thousands of valid but uncommon words. Our dictionary includes them all. Knowing obscure-but-valid words like NAEVI or RATELS is a real advantage in competitive play.' },
        { q: 'What is the highest-scoring Boggle board ever found?',
          a: 'Competitive solvers have found boards yielding over 2,000 points. Most real-game boards yield 50–200 points in 3 minutes.' },
      ]} />

      <h2>Related Tools</h2>
      <Related links={[['🃏 Scrabble Finder','/scrabble'],['🔀 Jumble Solver','/jumble'],['🔍 Word Unscrambler','/unscramble'],['🔤 Anagram Solver','/anagram-solver']]} />
    </div>
  ),

  /* ══ SCRABBLE FINDER ═════════════════════════════════════════════════════ */
  scrabble: () => (
    <div className="seo-section">
      <h2>About Scrabble</h2>
      <p>
        <strong>Scrabble</strong> was invented in 1938 by American architect Alfred Mosher
        Butts, who calculated letter frequencies by analysing the front page of the <em>New
        York Times</em>. Originally called "Criss-Cross Words," it was renamed Scrabble and
        licensed to Selchow &amp; Righter in 1953. Today over <strong>150 million sets</strong> have
        been sold in 121 countries in 29 languages, making it one of the best-selling board
        games of all time.
      </p>
      <p>
        Our <strong>Scrabble Word Finder</strong> takes your tile rack — including blank (?)
        tiles — and instantly returns every valid word you can play, ranked by point value.
        Stop leaving points on the board; start playing the highest-scoring words possible.
      </p>

      <h2>How to Use the Scrabble Word Finder</h2>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Step-by-Step</h3>
            <ol>
              <li>Type your 2–7 tile letters into the input.</li>
              <li>Use <strong>?</strong> for each blank tile you hold.</li>
              <li>Click <strong>"Find Words"</strong> for a ranked results table.</li>
              <li>Use the <strong>Min Score</strong> filter to show only high-value plays.</li>
              <li>Column "Blanks Used" shows if a word needs your blank tile.</li>
            </ol>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Tile Point Values</h3>
            <ul>
              <li><strong>10 pts:</strong> Q, Z</li>
              <li><strong>8 pts:</strong> J, X</li>
              <li><strong>5 pts:</strong> K</li>
              <li><strong>4 pts:</strong> F, H, V, W, Y</li>
              <li><strong>3 pts:</strong> B, C, M, P</li>
              <li><strong>2 pts:</strong> D, G</li>
              <li><strong>1 pt:</strong> A E I O U L N R S T</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Which Dictionary Should You Use?</h2>
      <p>
        Word validity depends on which official word list your game uses — so our finder lets you
        switch between the three major dictionaries. Pick the one that matches how you play:
      </p>
      <ul>
        <li><strong>TWL (North American):</strong> The tournament word list used in the USA, Canada, Thailand, and Israel (based on the NASPA Word List). Choose this for club and tournament play in North America.</li>
        <li><strong>SOWPODS / Collins:</strong> The international list (Collins Scrabble Words) used across the UK, Europe, Australia, and world championships. It's larger and more permissive — it accepts many words TWL doesn't.</li>
        <li><strong>Words With Friends:</strong> Zynga's popular mobile game uses its own dictionary, which differs from both Scrabble lists — some words valid in Scrabble are rejected in WWF and vice-versa.</li>
      </ul>
      <p>
        For example, short words like <strong>QI</strong> and <strong>ZA</strong> are valid in TWL and
        SOWPODS but not in every Words With Friends build. Set the dictionary to match your game and the
        results update instantly.
      </p>

      <h2>Advanced Scrabble Strategies</h2>
      <ul>
        <li><strong>Chase the Bingo (+50 points).</strong> Playing all 7 tiles in one turn earns a 50-point bonus. Keep an S, ER, ING, or ED on your rack to build towards 7-letter words.</li>
        <li><strong>Hoard S tiles and blanks.</strong> S tiles enable plurals and verb forms anywhere on the board; blank tiles substitute any letter. Never waste them on small words.</li>
        <li><strong>Exploit premium squares.</strong> Triple Word Score (TWS) squares can multiply a single play by 3× — or 9× if you cover two TWS squares with one word.</li>
        <li><strong>Block your opponent.</strong> Avoid playing adjacent to a TWS square unless you can use it yourself. Keeping the board "tight" limits opponent scoring opportunities.</li>
        <li><strong>Know the two-letter words.</strong> QI, ZA, XI, JO, KA, and dozens of others are valid in tournament Scrabble and create surprising scoring opportunities.</li>
      </ul>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'What is a "bingo" in Scrabble?',
          a: 'A bingo is when you play all seven tiles from your rack in a single turn, earning a 50-point bonus on top of the word\'s face value. Top players aim for a bingo every 5–7 turns.' },
        { q: 'Are blank tiles worth any points?',
          a: 'No — blank tiles score 0 points regardless of which letter they represent. Their value is flexibility: they let you complete high-scoring words you couldn\'t form otherwise.' },
        { q: 'What is the highest possible Scrabble score in one turn?',
          a: 'The theoretical maximum single-turn score is 1,778 points (OXYPHENBUTAZONE covering two Triple Word Scores). In competitive play, scores above 100 points in one turn are exceptional.' },
      ]} />

      <h2>Related Tools</h2>
      <Related links={[['🎯 Boggle Solver','/boggle'],['🔍 Word Unscrambler','/unscramble'],['🔀 Jumble Solver','/jumble'],['🔤 Anagram Solver','/anagram-solver']]} />
    </div>
  ),

  /* ══ WORD LADDER ═════════════════════════════════════════════════════════ */
  'word-ladder': () => (
    <div className="seo-section">
      <h2>The History of Word Ladders</h2>
      <p>
        <strong>Word Ladders</strong> — originally called <em>Doublets</em> — were invented
        in 1877 by Lewis Carroll, the author of <em>Alice's Adventures in Wonderland</em>.
        Carroll published a weekly Doublets column in <em>Vanity Fair</em> magazine and
        included puzzles in letters to his child friends. He described the challenge as
        "transforming one word into another by changing one letter at a time, each step
        being a real word."
      </p>
      <p>
        Word Ladders are more than just entertaining — they illuminate how interconnected
        the English language is. Our <strong>Word Ladder Solver</strong> uses a
        <em>Breadth-First Search (BFS)</em> algorithm to find the shortest possible path
        between two words, guaranteed to be optimal.
      </p>

      <h2>How Word Ladders Work</h2>
      <div className="seo-card">
        <h3>The Rules</h3>
        <ul>
          <li>Start word and end word must be the <strong>same length</strong>.</li>
          <li>Each step changes <strong>exactly one letter</strong>.</li>
          <li>Every intermediate word must be a <strong>real English word</strong>.</li>
          <li>The goal is to reach the end word in as <strong>few steps as possible</strong>.</li>
        </ul>
      </div>

      <h2>How to Use the Word Ladder Solver</h2>
      <ol>
        <li>Type your <strong>start word</strong> (e.g., LEAD).</li>
        <li>Type your <strong>end word</strong> (e.g., GOLD) — must be the same length.</li>
        <li>Click <strong>"Find Ladder"</strong>. The letter that changes on each step is highlighted.</li>
        <li>Try the preset examples (CAT→DOG, HEAD→TAIL) for instant fun.</li>
      </ol>
      <p>
        Shorter words (3–5 letters) produce faster results because there are more valid
        neighbours at each step. Very long words may have no path at all if no chain of
        valid words connects them.
      </p>

      <h2>Tips for Solving Word Ladders Manually</h2>
      <ul>
        <li><strong>Work from both ends.</strong> Find words one step from the start AND one step from the end, then look for overlap in the middle.</li>
        <li><strong>Target high-connectivity words.</strong> Words like CARE, BORE, LATE, FINE can each reach 10+ neighbours in one step, making them excellent bridge words.</li>
        <li><strong>Change vowels strategically.</strong> Swapping a single vowel (A→E→I→O→U) often unlocks new chains quickly.</li>
        <li><strong>3-letter words are gold.</strong> With only 3 positions to change, short words are almost always connected by short paths.</li>
      </ul>

      <h2>Word Ladder Examples</h2>
      <p>
        Ladder length and word length aren't the same thing — a longer word doesn't always
        mean a longer chain. Here's a real solved example for each common word length, plus a
        few longer themed chains for when you want more of a challenge. Paste any word below
        into the solver above to see its full path.
      </p>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>By Word Length</h3>
            <p><strong>3 letters</strong> — the easiest to solve, with plenty of neighbours at every step:</p>
            <Ex>TOP → HOP → HOT → <span>HAT</span> (3 steps)</Ex>
            <p><strong>4 letters</strong> — Lewis Carroll's original puzzle length:</p>
            <Ex>ROCK → SOCK → SACK → SANK → <span>SAND</span> (4 steps)</Ex>
            <p><strong>5 letters</strong> — fewer neighbours, but still fast when a bridge word exists:</p>
            <Ex>MOUSE → HOUSE → <span>HORSE</span> (2 steps)</Ex>
            <p><strong>6 letters</strong> — longer words need more shared letters to connect:</p>
            <Ex>PILLOW → BILLOW → BELLOW → <span>YELLOW</span> (3 steps)</Ex>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Longer Themed Chains</h3>
            <p>Some word pairs form surprisingly long chains through the English dictionary:</p>
            <Ex>RICH → RICK → ROCK → ROOK → BOOK → BOOR → <span>POOR</span> (6 steps)</Ex>
            <Ex>TEARS → SEARS → STARS → STARE → STALE → STILE → <span>SMILE</span> (6 steps)</Ex>
            <Ex>FLOUR → FLOOR → FLOOD → BLOOD → BROOD → BROAD → <span>BREAD</span> (6 steps)</Ex>
          </div>
        </div>
      </div>

      <h2>How to Create Your Own Word Ladder Puzzle</h2>
      <p>
        Making a word ladder puzzle for a classroom, worksheet, or friend is easy once you
        know the rules — and you can use this same solver as your puzzle-testing tool.
      </p>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Step by Step</h3>
            <ol>
              <li>Pick two words of the <strong>same length</strong>, ideally with a fun theme (CAT→DOG, WARM→COLD).</li>
              <li>Run both words through this solver to confirm a valid chain of real words connects them.</li>
              <li>Use the solver's shortest path as your official answer key, or hide the intermediate words as blanks.</li>
              <li>Write out each step, changing exactly <strong>one letter</strong> at a time.</li>
              <li>Double-check every intermediate word is recognisable to your intended audience.</li>
            </ol>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Tips for a Good Puzzle</h3>
            <ul>
              <li>3–4 letter word pairs are easiest to design and solve by hand.</li>
              <li>Thematically linked words (SUMMER→WINTER, HEAD→TAIL) feel more rewarding to solve.</li>
              <li>Always verify your puzzle in the solver before printing or sharing it.</li>
              <li>Avoid obscure or archaic dictionary words in the answer key — they frustrate hand-solvers.</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Word Ladders for Kids & Classrooms</h2>
      <p>
        Word ladders are a favourite tool for teachers and parents because they turn spelling
        practice into a puzzle. Each single-letter swap forces a player to notice how one
        change in a word's structure creates an entirely new meaning — reinforcing spelling
        patterns, vocabulary, and letter-position awareness at the same time.
      </p>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Learning Benefits</h3>
            <ul>
              <li><strong>Spelling accuracy:</strong> every intermediate word must be spelled correctly to count.</li>
              <li><strong>Vocabulary growth:</strong> players discover real words they hadn't used before.</li>
              <li><strong>Pattern recognition:</strong> repeated exposure to how vowels and consonants swap builds phonics intuition.</li>
              <li><strong>Logical thinking:</strong> planning several steps ahead mirrors basic path-finding and problem-solving skills.</li>
            </ul>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Suggested Word Lengths by Age</h3>
            <ul>
              <li><strong>Ages 5–7:</strong> 3-letter words (CAT, DOG, SUN) — short chains, quick wins.</li>
              <li><strong>Ages 8–11:</strong> 4-letter words (HEAD, TAIL, WARM, COLD) — the classic Lewis Carroll range.</li>
              <li><strong>Ages 12+ / adults:</strong> 5–6 letter words (FLOUR, TEARS, BLACK) for a bigger challenge.</li>
              <li>Use the solver to pre-check any classroom puzzle so no student gets stuck on an impossible pair.</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'What is the shortest possible word ladder?',
          a: 'If two words differ by exactly one letter (e.g., CAT → BAT), the ladder has just 1 step. Lewis Carroll\'s original puzzles typically required 6–8 steps for 4-letter words.' },
        { q: 'Are all word pairs connected by a word ladder?',
          a: 'No — some words exist in isolated "islands" with no chain of valid neighbours reaching other islands. Our solver returns "No path found" in those cases.' },
        { q: 'Why do some ladders take so many steps?',
          a: 'Words with unusual letter patterns (many uncommon consonants, rare vowel positions) have fewer valid single-step neighbours, forcing longer detours through the word graph.' },
        { q: 'Is there a word ladder generator I can use to make my own puzzles?',
          a: 'You don\'t need a separate generator — pick your two same-length words, run them through this solver to confirm a valid chain exists, then use that shortest path (or a longer hand-picked variation) as your puzzle\'s answer key.' },
        { q: 'Are word ladders good for kids and classrooms?',
          a: 'Yes. They build spelling accuracy, vocabulary, and pattern recognition by forcing players to think about how a single letter change affects a word\'s meaning. Start with 3-letter words for young learners and move to 4–6 letter words as skills grow.' },
      ]} />

      <h2>Related Tools</h2>
      <Related links={[['🔁 Palindrome Checker','/palindrome'],['🔤 Anagram Solver','/anagram-solver'],['✏️ Crossword Solver','/crossword'],['🎵 Rhyme Finder','/rhyme']]} />
    </div>
  ),

  /* ══ RHYME FINDER ════════════════════════════════════════════════════════ */
  rhyme: () => (
    <div className="seo-section">
      <h2>Why Rhyming Matters</h2>
      <p>
        Rhyme is one of the oldest and most powerful tools in human language. From ancient
        oral traditions to modern rap lyrics, rhyming makes words easier to remember,
        more pleasing to hear, and more emotionally resonant. Children learn language faster
        through nursery rhymes; medical students memorise drug interactions through rhyming
        mnemonics; orators use rhyming schemes to make speeches more memorable.
      </p>
      <p>
        Our <strong>Rhyme Finder</strong> searches over 190,000 words to surface
        every word that shares a sound ending with yours — grouped by how closely they
        rhyme (longer shared suffix = closer rhyme). Whether you're writing a sonnet,
        a rap verse, a children's book, or a greeting card, the perfect rhyme is one click away.
      </p>

      <h2>Types of Rhyme</h2>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Perfect (Pure) Rhyme</h3>
            <p>The vowel sound and all following consonants match exactly.</p>
            <Ex>CAT → bat, fat, hat, mat, pat, rat, sat</Ex>
            <Ex>MOON → boon, dune, June, tune, noon, soon, spoon</Ex>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Near (Slant) Rhyme</h3>
            <p>Sounds are similar but not identical — common in modern poetry and rap.</p>
            <Ex>LOVE → dove, above, glove, shove (different vowel sound)</Ex>
            <Ex>ORANGE → porridge, lozenge (near rhymes — "orange" is famously difficult!)</Ex>
          </div>
        </div>
      </div>

      <h2>Using Rhymes in Writing</h2>
      <ul>
        <li><strong>Poetry:</strong> Classic schemes (ABAB, AABB, ABBA) create rhythm and structure. Use perfect rhymes for formal poetry and near-rhymes for a contemporary feel.</li>
        <li><strong>Songwriting:</strong> End rhymes anchor verse lines; internal rhymes (rhyming within a line) add flow and velocity — essential in hip-hop.</li>
        <li><strong>Children's books:</strong> Predictable end-rhymes help early readers anticipate words and build phonemic awareness.</li>
        <li><strong>Mnemonics:</strong> "Thirty days hath September, April, June, and November" is a rhyming mnemonic for calendar months.</li>
        <li><strong>Advertising:</strong> "A Mars a day helps you work, rest and play." Rhyme in slogans increases memorability by up to 40%.</li>
      </ul>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'What words in English have no perfect rhyme?',
          a: '"Orange," "silver," "purple," "month," "ninth," and "wolf" are famously difficult to rhyme perfectly. Near-rhymes exist, but no perfect single-word partner does in standard dictionaries.' },
        { q: 'What is an eye rhyme?',
          a: 'Eye rhymes look like they should rhyme when written but don\'t when spoken — like "love" and "move," or "cough," "through," and "though." Poets sometimes use them for visual effect on the page.' },
        { q: 'How are the rhyme results grouped?',
          a: 'Results are grouped by shared suffix length. Words sharing the last 4 letters appear first (strongest rhyme), then 3-letter suffix matches, then 2-letter matches (weakest).' },
      ]} />

      <h2>Related Tools</h2>
      <Related links={[['🔁 Palindrome Checker','/palindrome'],['🔤 Anagram Solver','/anagram-solver'],['🎲 Anagram Generator','/anagram-generator'],['✏️ Crossword Solver','/crossword']]} />
    </div>
  ),

  /* ══ PALINDROME CHECKER ══════════════════════════════════════════════════ */
  palindrome: () => (
    <div className="seo-section">
      <h2>What Is a Palindrome?</h2>
      <p>
        A <strong>palindrome</strong> is a word, phrase, number, or sequence that reads
        the same forwards and backwards, ignoring spaces and punctuation. The word comes
        from the Greek <em>palindromos</em> — "running back again." Palindromes have
        fascinated humans for millennia: the Latin palindrome <em>SATOR AREPO TENET OPERA
        ROTAS</em> has been found inscribed on walls from ancient Pompeii to 6th-century
        Ethiopia.
      </p>
      <p>
        Our <strong>Palindrome Checker</strong> strips spaces and punctuation, then compares
        the cleaned string to its reverse — showing you both the forward and backward forms
        side by side. Try words, sentences, and even your own name!
      </p>

      <h2>Famous Palindromes to Try</h2>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Word Palindromes</h3>
            <ul>
              <li><strong>RACECAR</strong> — most cited English palindrome word</li>
              <li><strong>LEVEL</strong> — perfect symmetry</li>
              <li><strong>CIVIC</strong> — architectural feel</li>
              <li><strong>KAYAK</strong> — water sport connection</li>
              <li><strong>MADAM</strong> — classic form of address</li>
              <li><strong>RADAR</strong> — from Radio Detection And Ranging</li>
              <li><strong>REFER</strong> — verb palindrome</li>
              <li><strong>ROTOR</strong> — mechanical palindrome</li>
            </ul>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Sentence Palindromes</h3>
            <ul>
              <li><em>"A man a plan a canal Panama"</em></li>
              <li><em>"Was it a car or a cat I saw"</em></li>
              <li><em>"Never odd or even"</em></li>
              <li><em>"Madam I'm Adam"</em></li>
              <li><em>"Do geese see God"</em></li>
              <li><em>"Step on no pets"</em></li>
              <li><em>"Race fast safe car"</em></li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Types of Palindromes</h2>
      <ul>
        <li><strong>Character-level palindromes:</strong> Each individual character mirrors (RACECAR, LEVEL).</li>
        <li><strong>Word-level palindromes:</strong> Each word mirrors when the word sequence is reversed — "Fall leaves as leaves fall."</li>
        <li><strong>Numeric palindromes:</strong> Numbers like 121, 1331, and 12321 read the same both ways.</li>
        <li><strong>Biological palindromes:</strong> DNA sequences recognised by restriction enzymes are often palindromic — critical in molecular biology.</li>
        <li><strong>Musical palindromes:</strong> Compositions that sound the same played forwards and backwards — Bach was a master of this technique.</li>
      </ul>

      <h2>How the Checker Works</h2>
      <p>
        The algorithm is elegantly simple: (1) convert to lowercase, (2) strip all
        non-alphanumeric characters (spaces, punctuation, apostrophes), (3) compare the
        result to its reverse. If they match — it's a palindrome! This means "A man, a
        plan, a canal: Panama!" correctly identifies as palindromic even with commas and
        an exclamation mark.
      </p>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'What is the longest palindrome word in English?',
          a: '"Detartrated" (12 letters — past tense of "detartrate") is often cited as the longest palindromic word. "Redivider" (9 letters) is the most commonly referenced in mainstream usage.' },
        { q: 'Are there palindromes in other languages?',
          a: 'Yes — every language has them. Finnish has "saippuakivikauppias" (soapstone vendor, 19 letters), one of the world\'s longest single-word palindromes.' },
        { q: 'Is "racecar" really one word or two?',
          a: 'In standard dictionaries it\'s written as one word: "racecar." Some style guides hyphenate it (race-car) or write two words (race car). Our checker counts it as a palindrome regardless of spacing.' },
      ]} />

      <h2>Related Tools</h2>
      <Related links={[['🎵 Rhyme Finder','/rhyme'],['🔤 Anagram Solver','/anagram-solver'],['🪜 Word Ladder','/word-ladder'],['🔍 Word Unscrambler','/unscramble']]} />
    </div>
  ),

  /* ══ SUDOKU SOLVER ═══════════════════════════════════════════════════════ */
  sudoku: () => (
    <div className="seo-section">
      <h2>About Sudoku</h2>
      <p>
        <strong>Sudoku</strong> (数独, "single number") is a logic-based number-placement
        puzzle. Although it originated in the United States as "Number Place" (1979, Dell
        Magazines), it was popularised in Japan by Nikoli in 1984 under the name Sudoku.
        It exploded globally in 2004–05 when New Zealand judge Wayne Gould wrote a computer
        program to generate puzzles and convinced <em>The Times</em> of London to publish
        them. Today, billions of sudoku puzzles are solved every year across print, apps,
        and websites worldwide.
      </p>

      <h2>The Rules of Sudoku</h2>
      <div className="row g-3">
        <div className="col-md-4">
          <div className="seo-card">
            <h3>Rows</h3>
            <p>Each of the 9 horizontal rows must contain the digits <strong>1–9</strong> with no repetition.</p>
          </div>
        </div>
        <div className="col-md-4">
          <div className="seo-card">
            <h3>Columns</h3>
            <p>Each of the 9 vertical columns must contain <strong>1–9</strong> with no repetition.</p>
          </div>
        </div>
        <div className="col-md-4">
          <div className="seo-card">
            <h3>3×3 Boxes</h3>
            <p>Each of the nine 3×3 sub-grids (boxes) must contain <strong>1–9</strong> with no repetition.</p>
          </div>
        </div>
      </div>

      <h2>How to Use Our Sudoku Solver</h2>
      <ol>
        <li><strong>Load a sample puzzle</strong> (Easy / Medium / Hard) or manually click cells and type numbers 1–9.</li>
        <li>Use the <strong>number pad</strong> below the board or your keyboard to fill cells.</li>
        <li>Navigate with <strong>arrow keys</strong> between cells for fast entry.</li>
        <li>Red highlighting alerts you to <strong>conflicts</strong> (duplicate numbers) before you solve.</li>
        <li>Click <strong>"Solve Puzzle"</strong> and watch the board fill in — green cells show the computed answers.</li>
      </ol>

      <h2>How the Solver Works</h2>
      <p>
        Our solver implements a <strong>backtracking algorithm</strong> combined with
        constraint propagation. It works by: (1) finding an empty cell, (2) trying each
        digit 1–9, (3) checking if the digit is valid (no conflict in row, column, or box),
        (4) recursively attempting to solve the rest of the puzzle, and (5) backtracking
        if no valid digit leads to a solution. This brute-force-with-pruning approach
        solves even "world's hardest" sudoku puzzles in under a second.
      </p>

      <h2>Manual Solving Strategies</h2>
      <ul>
        <li><strong>Naked Singles:</strong> A cell with only one possible digit — fill it immediately. Start here.</li>
        <li><strong>Hidden Singles:</strong> A digit that can only go in one cell within a row, column, or box.</li>
        <li><strong>Naked Pairs:</strong> Two cells in a unit that can only contain the same two digits — eliminate those digits elsewhere in the unit.</li>
        <li><strong>Scanning:</strong> For each digit 1–9, scan rows and columns to eliminate candidates and find forced placements.</li>
        <li><strong>Start dense.</strong> Begin solving in rows, columns, or boxes with the most given numbers — they constrain the most.</li>
      </ul>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'What is the minimum number of given clues needed for a unique Sudoku solution?',
          a: 'In 2012, mathematicians proved the minimum is 17 clues. Any puzzle with 16 or fewer given numbers has multiple solutions.' },
        { q: 'Was Sudoku really invented in Japan?',
          a: 'No — it was invented by Howard Garns in the US in 1979, though he never received credit during his lifetime. Japan popularised and renamed it. Garns died in 1989, before the global Sudoku craze began.' },
        { q: 'How many valid completed Sudoku grids exist?',
          a: 'There are exactly 6,670,903,752,021,072,936,960 valid completed Sudoku grids — roughly 6.67 × 10²¹. When accounting for symmetry, this reduces to about 5.47 billion essentially different grids.' },
      ]} />

      <h2>Related Tools</h2>
      <Related links={[['🟩 Wordle Game','/wordle'],['🎭 Hangman','/hangman'],['🔎 Word Search','/word-search'],['🪜 Word Ladder','/word-ladder']]} />
    </div>
  ),

  /* ══ WORDLE GAME ═════════════════════════════════════════════════════════ */
  wordle: () => (
    <div className="seo-section">
      <h2>About the Wordle Game</h2>
      <p>
        <strong>Wordle</strong> was built in October 2021 by Josh Wardle, a Brooklyn-based
        software engineer, as a personal gift for his word-puzzle-loving partner Palak Shah.
        After sharing it with friends and family, he released it publicly — and within weeks
        it spread virally. From 90 daily players in November 2021, it exploded to 300,000
        by January 2022. The <em>New York Times</em> acquired it for a reported
        <strong>seven-figure sum</strong> shortly after.
      </p>
      <p>
        The game's genius lies in its simplicity: one puzzle per day, shared by everyone,
        with a clear visual feedback system using coloured tiles. The shareable grid of
        emoji squares — without spoiling the answer — drove massive social media adoption.
        Our version lets you play unlimited games with new words every time.
      </p>

      <h2>How to Play Wordle</h2>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>The Rules</h3>
            <ol>
              <li>Guess a valid 5-letter English word.</li>
              <li>After each guess, tiles change colour.</li>
              <li>🟩 <strong>Green</strong> = right letter, right spot.</li>
              <li>🟨 <strong>Yellow</strong> = right letter, wrong spot.</li>
              <li>⬛ <strong>Gray</strong> = letter not in the word.</li>
              <li>Solve in <strong>6 guesses</strong> or fewer to win!</li>
            </ol>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Controls</h3>
            <ul>
              <li>Type letters on your <strong>keyboard</strong> or click the on-screen keyboard.</li>
              <li>Press <strong>Enter</strong> to submit a guess.</li>
              <li>Press <strong>Backspace / ⌫</strong> to delete a letter.</li>
              <li>The keyboard colours update to track used letters.</li>
              <li>Only valid dictionary words are accepted.</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Winning Strategies</h2>
      <ul>
        <li><strong>Choose a powerful opener.</strong> CRANE, STARE, AUDIO, or RAISE cover the most common letters and give maximum information on the first guess.</li>
        <li><strong>Maximise information per guess.</strong> Your second guess should test as many new letters as possible — not repeat ones you already know.</li>
        <li><strong>Use yellow letters wisely.</strong> A yellow letter must appear in the word — just not in the position you placed it. Try it in every other position.</li>
        <li><strong>Think about word patterns.</strong> Many 5-letter words follow patterns like _IGHT, _OUND, _ANCE. Recognising these speeds up solving.</li>
        <li><strong>Don't waste guesses.</strong> Never guess a word containing a confirmed gray letter — you're guaranteed to lose a turn.</li>
      </ul>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'What is the statistically best first word in Wordle?',
          a: 'Multiple studies using information theory (entropy maximisation) point to CRANE, TRACE, SLATE, or RAISE as optimal openers. CRANE is a popular choice as it contains 5 common, distinct letters.' },
        { q: 'How is our version different from the NYT Wordle?',
          a: 'Our version draws random words from a 6,000+ word dictionary and lets you play unlimited times. The NYT version uses a curated ~2,300-word list with one puzzle per day shared globally.' },
        { q: 'What should I do if I\'m stuck?',
          a: 'Use our Wordle Solver — enter your guesses with their colour results and it will narrow down the candidates instantly. Think of it as a hint system, not cheating!' },
      ]} />

      <h2>Related Tools</h2>
      <Related links={[['💡 Wordle Solver','/wordle-solver'],['🎭 Hangman','/hangman'],['🔎 Word Search','/word-search'],['🔤 Anagram Solver','/anagram-solver']]} />
    </div>
  ),

  /* ══ HANGMAN ═════════════════════════════════════════════════════════════ */
  hangman: () => (
    <div className="seo-section">
      <h2>About Hangman</h2>
      <p>
        <strong>Hangman</strong> is one of the world's most recognised word-guessing games.
        Its exact origins are debated — the earliest documented reference appears in Alice
        Bertha Gomme's 1894 collection of children's games under the name "Birds, Beasts,
        and Fishes." By the mid-20th century it had spread globally as a paper-and-pencil
        classroom staple. The distinctive gallows drawing makes wrong guesses visually
        dramatic and memorably teaches letter frequency.
      </p>
      <p>
        Our online <strong>Hangman</strong> pulls random words from a 52,000+ word
        dictionary. Words range from 4 to 9 letters — long enough to be challenging but
        short enough to be solvable. You get 6 wrong guesses before the hangman is complete.
        Can you save the day?
      </p>

      <h2>How to Play Hangman</h2>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Game Rules</h3>
            <ol>
              <li>A secret word is chosen — dashes show its length.</li>
              <li>Click a letter (or press a key) to guess it.</li>
              <li>Correct guesses reveal the letter in all its positions.</li>
              <li>Wrong guesses add a body part to the hangman (6 max).</li>
              <li>Guess the full word before the drawing is complete to win!</li>
            </ol>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Letter Frequency Guide</h3>
            <p>Guess letters in order of frequency for the best odds:</p>
            <div style={{fontFamily:'Fredoka One,cursive', fontSize:'1.1rem', color:'var(--primary)', letterSpacing:2}}>
              E T A O I N S H R D L C U M W F G Y P B V K J X Q Z
            </div>
            <p><small><strong>E</strong> appears in ~12.7% of English letters — always guess it first!</small></p>
          </div>
        </div>
      </div>

      <h2>Letter Frequency Strategy</h2>
      <p>
        The most effective Hangman strategy is to guess letters in order of their frequency
        in English text. Linguists and cryptographers have studied this extensively:
      </p>
      <ul>
        <li><strong>E</strong> (12.7%) → always your first guess</li>
        <li><strong>T</strong> (9.1%), <strong>A</strong> (8.2%), <strong>O</strong> (7.5%), <strong>I</strong> (7.0%) → guess these next</li>
        <li><strong>N</strong> (6.7%), <strong>S</strong> (6.3%), <strong>H</strong> (6.1%), <strong>R</strong> (6.0%) → highly likely in most words</li>
        <li>After those 9 letters, adapt to the revealed pattern — if you see _ A _ _ you might guess double letters or common endings.</li>
      </ul>

      <h2>Educational Benefits of Hangman</h2>
      <p>
        Teachers have used Hangman in classrooms for over a century for good reason.
        It builds <strong>spelling awareness</strong>, reinforces <strong>letter-sound
        relationships</strong>, and teaches kids about <strong>vowels vs. consonants</strong>
        through the natural process of elimination. For English language learners (ELL
        students), Hangman is a low-pressure, high-engagement vocabulary builder.
      </p>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'What is the optimal Hangman strategy?',
          a: 'Guess E, T, A, O, I, N, S, H, R in that order. After each correct reveal, look at the pattern and think about common English word shapes — does it look like it could end in -ING, -ED, or -TION?' },
        { q: 'What happens if the same letter appears multiple times?',
          a: 'All instances are revealed simultaneously when you guess a letter correctly. If the word is APPLE and you guess P, both P positions light up at once.' },
        { q: 'Can I get the same word twice?',
          a: 'Words are chosen randomly from a 52,000-word pool, so repeats are very unlikely in casual play. Click "New Word" anytime to get a fresh challenge.' },
      ]} />

      <h2>Related Tools</h2>
      <Related links={[['🟩 Wordle Game','/wordle'],['🔎 Word Search','/word-search'],['🔀 Jumble Solver','/jumble'],['💡 Wordle Solver','/wordle-solver']]} />
    </div>
  ),

  /* ══ WORD SEARCH ═════════════════════════════════════════════════════════ */
  'word-search': () => (
    <div className="seo-section">
      <h2>About Word Search Puzzles</h2>
      <p>
        The modern <strong>word search puzzle</strong> was invented by Norman E. Gibbs in
        1968, who submitted it for publication in the Selenby Digest. It was first commercially
        distributed across the United States in the 1970s and quickly became a newspaper
        staple. Today, word search puzzles appear in millions of books, activity pads,
        educational apps, and websites — enjoyed by everyone from young children learning
        to read to seniors keeping their minds sharp.
      </p>
      <p>
        Our interactive <strong>Word Search</strong> generates fresh puzzles across 6
        categories (Animals, Fruits, Colors, Countries, Sports, Space) with words hidden in
        all 8 directions — horizontal, vertical, and diagonal. Click and drag to select
        letters; found words are highlighted in green!
      </p>

      <h2>How to Play Word Search</h2>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Getting Started</h3>
            <ol>
              <li>Click a <strong>category button</strong> (Animals, Fruits, etc.) to generate a puzzle.</li>
              <li>Study the word list on the right — memorise the first and last letters.</li>
              <li><strong>Click and drag</strong> across the letter grid to select a word.</li>
              <li>If the selection matches a hidden word, it turns <strong>green</strong>!</li>
              <li>Find all hidden words to complete the puzzle.</li>
            </ol>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Search Directions</h3>
            <p>Words can be hidden in <strong>8 different directions</strong>:</p>
            <ul>
              <li>→ Left to right (horizontal)</li>
              <li>↓ Top to bottom (vertical)</li>
              <li>↘ Diagonal (top-left to bottom-right)</li>
              <li>↙ Diagonal (top-right to bottom-left)</li>
              <li>← Right to left (reverse horizontal)</li>
              <li>↑ Bottom to top (reverse vertical)</li>
              <li>↗ Reverse diagonal</li>
              <li>↖ Reverse diagonal</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Strategies for Solving Word Searches Quickly</h2>
      <ul>
        <li><strong>Hunt for the first letter.</strong> Scan systematically (row by row, or column by column) for the first letter of each target word.</li>
        <li><strong>Look for uncommon letters first.</strong> Words containing Q, Z, X, or J are easiest to spot because those letters are rare in the fill grid.</li>
        <li><strong>Check all 8 directions</strong> once you find a first letter — the word could go any way.</li>
        <li><strong>Start with the longest words.</strong> Longer words are paradoxically easier to find (more distinctive letter sequence, fewer coincidental partial matches).</li>
        <li><strong>Use the progress bar</strong> as motivation — finding 50% of words gives you a natural momentum boost to finish.</li>
      </ul>

      <h2>Educational Benefits of Word Search</h2>
      <p>
        Word search puzzles offer surprising cognitive benefits beyond simple entertainment:
      </p>
      <ul>
        <li><strong>Pattern recognition:</strong> Training your eye to spot specific letter sequences sharpens general reading speed.</li>
        <li><strong>Vocabulary building:</strong> Theme-based puzzles introduce children to category-specific vocabulary (scientific names, geographical terms, etc.).</li>
        <li><strong>Focus and concentration:</strong> The systematic search requires sustained attention, a skill transferable to all forms of study and work.</li>
        <li><strong>Stress relief:</strong> The gentle challenge of a word search activates the brain's reward pathways without the frustration of harder puzzles — ideal for relaxation.</li>
        <li><strong>Memory:</strong> Rehearsing target words before searching them reinforces short-term memory and word recognition speed.</li>
      </ul>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'Can words overlap in the grid?',
          a: 'Yes — letters can be shared between two different words. For example, the E in ELEPHANT might also be the first letter of EAGLE if both words were present. This is intentional design.' },
        { q: 'How big is the grid?',
          a: 'Our grid is 14×14 cells, holding up to 8 words per puzzle. Each new puzzle is randomly generated — click any category button to get a completely fresh layout.' },
        { q: 'Can I play on mobile?',
          a: 'Yes — the grid supports touch events on mobile browsers. Tap the start cell, drag to the end cell, and lift your finger to check the selection.' },
      ]} />

      <h2>Related Tools</h2>
      <Related links={[['🎭 Hangman','/hangman'],['🟩 Wordle Game','/wordle'],['🎯 Boggle Solver','/boggle'],['🔎 Crossword Solver','/crossword']]} />
    </div>
  ),

  /* ══ SPELLING BEE SOLVER ════════════════════════════════════════════════ */
  'spelling-bee': () => (
    <div className="seo-section">
      <h2>What Is Spelling Bee?</h2>
      <p>
        The <strong>Spelling Bee puzzle</strong> was popularized by The New York Times, which
        has published a daily version since 2014. The classic format gives players seven letters
        arranged in a honeycomb — one letter in the center, six surrounding it. Every valid word
        must be at least four letters long, can reuse letters as many times as needed, and must
        always include the center letter. Words using all seven letters are called
        <strong> pangrams</strong> and earn bonus points.
      </p>
      <p>
        Our <strong>Spelling Bee Solver</strong> instantly finds every valid answer for any set of
        seven letters you enter. It highlights pangrams in gold so you can spot the most impressive
        words first. Whether you're stuck on your daily puzzle or just exploring what's possible
        with a given set, our solver covers the complete dictionary.
      </p>
      <div className="seo-card">
        <h3>How to Use This Solver</h3>
        <ol>
          <li>Enter all seven letters in the <strong>All Letters</strong> field (no spaces needed).</li>
          <li>Type the single <strong>center letter</strong> in the smaller box on the right.</li>
          <li>Click <strong>Find Words</strong> and results appear grouped by length.</li>
          <li>Pangrams — words using every one of the seven letters — are highlighted in gold.</li>
          <li>Copy any word to verify it in your official puzzle app.</li>
        </ol>
      </div>
      <h2>Example</h2>
      <Ex arrow="Letters: A C D E I L T · Center: A">Words: DETAIL, DIALECT, CITADEL (pangram!), TIDAL, LACED…</Ex>
      <h2>Tips & Strategies</h2>
      <ul>
        <li><strong>Find the pangram first:</strong> Pangrams always use every letter once — scan for the longest words and check each one.</li>
        <li><strong>Try common suffixes:</strong> -ING, -ED, -TION, -LY dramatically expand your word count with the right letters.</li>
        <li><strong>Plurals count:</strong> If LAKE is valid, LAKES often is too — always try adding S.</li>
        <li><strong>Work length down:</strong> Start with 7-letter words and work toward the 4-letter minimum threshold.</li>
        <li><strong>Center letter first:</strong> Build every attempt around the mandatory center letter to avoid wasted guesses.</li>
      </ul>
      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'Can I reuse letters?', a: 'Yes! Unlike some word games, Spelling Bee allows letters to appear multiple times in a single word. So with letters A C D E I L T you could use A twice in ACETALDEHYDE if it were in the dictionary.' },
        { q: 'What is a pangram?', a: 'A pangram is a word that uses all seven of the given letters at least once. In the NYT Spelling Bee, finding the pangram awards extra points and is often the hardest word in the puzzle.' },
        { q: 'Why might my word not appear in the official game?', a: 'The NYT Spelling Bee uses a curated word list that excludes proper nouns, obscure technical terms, and offensive words. Our solver uses a standard English dictionary which may include or exclude different words.' },
      ]} />
      <h2>Related Tools</h2>
      <Related links={[['🔀 Jumble Solver','/jumble'],['📏 Longest Word','/longest-word'],['🃏 Scrabble Finder','/scrabble'],['🔎 Word Pattern','/word-pattern']]} />
    </div>
  ),

  /* ══ LONGEST WORD FINDER ═════════════════════════════════════════════════ */
  'longest-word': () => (
    <div className="seo-section">
      <h2>About Longest Word Finder</h2>
      <p>
        The <strong>Longest Word Finder</strong> solves a deceptively simple question: given a
        random collection of letters, what is the longest real English word hiding inside them?
        This challenge appears in game shows like Countdown (UK) and Des chiffres et des lettres
        (France), where contestants race to form the longest word from nine random letters in
        thirty seconds. It tests not just vocabulary but rapid mental pattern-matching.
      </p>
      <p>
        Our tool scans over 190,000 words and surfaces every valid word you can build,
        ranked so the longest appear first. Unlike human guessing, the solver considers every
        possible subset of your letters simultaneously — guaranteeing you never miss a longer
        option you didn't think of.
      </p>
      <div className="seo-card">
        <h3>How to Use</h3>
        <ol>
          <li>Type up to 20 letters in the input field (the letters from your game, puzzle, or tile rack).</li>
          <li>Click <strong>Find Longest Word</strong>.</li>
          <li>The longest word(s) are shown prominently at the top, highlighted in purple.</li>
          <li>All valid shorter words appear below, grouped by length for easy browsing.</li>
        </ol>
      </div>
      <h2>Example</h2>
      <Ex arrow="Letters: RSTLNEA">Longest: ANTLERS (7) · Also: ALERTS, ALTERS, RANTLE, LEARNT…</Ex>
      <h2>Tips & Strategies</h2>
      <ul>
        <li><strong>Include vowels:</strong> A mix of 3–4 vowels and 4–5 consonants yields the most options in English.</li>
        <li><strong>Common combos:</strong> Letter groups like STR, ING, TION, and ENT appear in thousands of long words.</li>
        <li><strong>Use it for Countdown:</strong> Enter your 9 consonant/vowel draw and instantly beat the clock.</li>
        <li><strong>Scrabble prep:</strong> Use the finder to maximize your rack before drawing replacement tiles.</li>
      </ul>
      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'Can I use a letter more than once?', a: 'Only if that letter appears multiple times in your input. If you enter AABCDE you can use A twice, but not three times.' },
        { q: 'Is the longest word always the best Scrabble play?', a: 'Not necessarily — placement on bonus squares, board position, and leaving good tiles for future turns all matter. But finding the longest possible word is always a good starting point.' },
        { q: 'What\'s the longest word in English?', a: 'The longest word in a standard dictionary is "pneumonoultramicroscopicsilicovolcanoconiosis" (45 letters), a lung disease. You\'ll need a very specific set of 45 tiles to find that one!' },
      ]} />
      <h2>Related Tools</h2>
      <Related links={[['🐝 Spelling Bee','/spelling-bee'],['🔀 Jumble Solver','/jumble'],['🃏 Scrabble Finder','/scrabble'],['🔎 Word Pattern','/word-pattern']]} />
    </div>
  ),

  /* ══ WORD PATTERN FINDER ═════════════════════════════════════════════════ */
  'word-pattern': () => (
    <div className="seo-section">
      <h2>About Word Pattern Finder</h2>
      <p>
        A <strong>word pattern</strong> is a template where known letters are fixed and unknown
        positions are marked with a wildcard symbol. Pattern matching is a core skill in crossword
        solving, codeword puzzles, and word-deduction games like Wordle. When you know some letters
        but not all, a pattern search instantly narrows thousands of possibilities down to a handful
        of candidates.
      </p>
      <p>
        Our Word Pattern Finder uses the <strong>?</strong> symbol for unknown letters. Enter
        <code>?IGHT</code> and get EIGHT, FIGHT, LIGHT, MIGHT, NIGHT, RIGHT, SIGHT, TIGHT, WIGHT.
        Enter <code>TR??K</code> and find TRACK, TRICK, TROCK, TRUCK. Every match is drawn from
        our 190,000+ word dictionary.
      </p>
      <div className="seo-card">
        <h3>How to Use</h3>
        <ol>
          <li>Type the letters you know in their correct positions.</li>
          <li>Use <strong>?</strong> for every letter you don't know yet.</li>
          <li>Click <strong>Find Matches</strong> — known positions highlight normally, wildcard fills are shown in purple.</li>
          <li>Use the quick-example buttons at the top to see sample patterns instantly.</li>
        </ol>
      </div>
      <h2>Examples</h2>
      <Ex arrow="?A?E">BAKE, CAKE, CAME, CARE, DARE, FACE, FAKE, FAME, FATE, GAVE, HAZE…</Ex>
      <Ex arrow="S???T">SAINT, SCOUT, SHIRT, SHOUT, SLEET, SMELT, SNORT, SPORT, STILT…</Ex>
      <h2>Tips & Strategies</h2>
      <ul>
        <li><strong>Crossword use:</strong> When you have crossing letters, enter exactly what you know and let the pattern do the filtering — one crossing letter often eliminates 90% of candidates.</li>
        <li><strong>Wordle use:</strong> Use confirmed green letters in their positions to narrow your next guess dramatically.</li>
        <li><strong>Start with length:</strong> The number of characters in your pattern equals word length — a 5-character pattern only matches 5-letter words.</li>
        <li><strong>Try multiple wildcards:</strong> Even one known letter in a 6-letter word cuts results from 20,000 to under 2,000.</li>
      </ul>
      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'What\'s the difference between this and the Crossword Solver?', a: 'Both tools do pattern matching. The Crossword Solver uses _ (underscore) while this tool uses ? (question mark). Both are equivalent — choose whichever feels natural.' },
        { q: 'Can I mix known letters and wildcards freely?', a: 'Yes — any combination works. C?T matches CAT, COT, CUT, CIT. ?A?T matches BAIT, CART, CAST, DART, EAST, FACT, FAST, GAIT, LAST, MAST, PAST, RAFT, SALT, VAST, WART…' },
        { q: 'Does it work for proper nouns or names?', a: 'Our dictionary contains common English words only — no proper nouns, abbreviations, or names. If you\'re solving a puzzle that allows proper nouns, some answers may not appear.' },
      ]} />
      <h2>Related Tools</h2>
      <Related links={[['✏️ Crossword Solver','/crossword'],['💡 Wordle Solver','/wordle-solver'],['🐝 Spelling Bee','/spelling-bee'],['📏 Longest Word','/longest-word']]} />
    </div>
  ),

  /* ══ SCRABBLE CALCULATOR ═════════════════════════════════════════════════ */
  'scrabble-calculator': () => (
    <div className="seo-section">
      <h2>About Scrabble Score Calculator</h2>
      <p>
        <strong>Scrabble</strong> is the world's most popular crossword board game, played by an
        estimated 150 million people in 29 languages across 121 countries. Invented by Alfred
        Mosher Butts in 1938 and refined by James Brunot in 1948, the game assigns a point value
        to each letter tile based on its frequency in English — common letters like E, A, and I are
        worth just 1 point while rare letters like Q and Z score 10 each.
      </p>
      <p>
        Knowing how to calculate your score quickly — especially with premium square multipliers —
        is essential for competitive play. Our <strong>Scrabble Score Calculator</strong> handles
        the arithmetic instantly: enter a word, select any word-score multiplier (Double Word,
        Triple Word), and click individual tiles to apply letter multipliers.
      </p>
      <div className="seo-card">
        <h3>How to Use</h3>
        <ol>
          <li>Type your word in the input field — tiles appear automatically.</li>
          <li>Select a word multiplier (Double Word ×2 or Triple Word ×3) if your play covers that square.</li>
          <li>Click any individual tile to cycle through letter multipliers: normal → ×2 DL → ×3 TL → normal.</li>
          <li>The total score updates live, showing the breakdown below.</li>
        </ol>
      </div>
      <h2>Scrabble Letter Values</h2>
      <Ex arrow="1 pt">A, E, I, O, U, L, N, S, T, R</Ex>
      <Ex arrow="2 pts">D, G</Ex>
      <Ex arrow="3 pts">B, C, M, P</Ex>
      <Ex arrow="4 pts">F, H, V, W, Y</Ex>
      <Ex arrow="5 pts">K</Ex>
      <Ex arrow="8 pts">J, X</Ex>
      <Ex arrow="10 pts">Q, Z</Ex>
      <h2>Tips & Strategies</h2>
      <ul>
        <li><strong>Q without U:</strong> Learn words like QI, QOPH, QANAT — invaluable when the U tiles are gone.</li>
        <li><strong>Bingos earn +50:</strong> Playing all 7 tiles in one turn scores a 50-point bonus on top of the word score.</li>
        <li><strong>Triple Letter + Triple Word:</strong> Placing a Z or Q on a Triple Letter Square that is part of a Triple Word Score play can yield 90+ points for a single tile.</li>
        <li><strong>Two-letter words:</strong> Mastering all 107 valid two-letter words is the single biggest jump in Scrabble skill level.</li>
      </ul>
      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'Does the calculator include the 50-point bingo bonus?', a: 'Not automatically — if you play all 7 tiles, add 50 to the displayed total manually. We show the base word score; the bingo bonus is a flat addition.' },
        { q: 'What are blank tiles worth?', a: 'Blank tiles (wildcards) are worth 0 points in Scrabble. They substitute for any letter but contribute nothing to the score — a small price for extreme flexibility.' },
        { q: 'Can I calculate scores for Words With Friends?', a: 'The letter values differ slightly between Scrabble and Words With Friends, and bonus square positions are different too. This calculator uses official Scrabble tile values.' },
      ]} />
      <h2>Related Tools</h2>
      <Related links={[['🃏 Scrabble Finder','/scrabble'],['🔀 Jumble Solver','/jumble'],['📏 Longest Word','/longest-word'],['🔤 Anagram Solver','/anagram-solver']]} />
    </div>
  ),

  /* ══ PIG LATIN CONVERTER ═════════════════════════════════════════════════ */
  'pig-latin': () => (
    <div className="seo-section">
      <h2>About Pig Latin</h2>
      <p>
        <strong>Pig Latin</strong> is a playful English language game that transforms words
        according to a simple set of phonetic rules, producing a form of speech that sounds
        foreign and comically mysterious to uninitiated listeners. The name itself is ironic —
        it has nothing to do with pigs or Latin. The term appears in print as early as 1869 in
        Putnam's Magazine, though the game likely predates that by decades.
      </p>
      <p>
        Pig Latin was widely used by children in the 20th century as a "secret language" to
        communicate in plain earshot of adults. It has since appeared in films, cartoons, and
        pop culture — from Porky Pig to the Simpsons. Learning the rules also reinforces phonemic
        awareness: the skill of identifying and manipulating individual sounds within words,
        which is foundational to reading development in children.
      </p>
      <div className="seo-card">
        <h3>Pig Latin Rules</h3>
        <ol>
          <li><strong>Vowel start:</strong> Add "way" to the end → <em>apple</em> becomes <em>appleway</em></li>
          <li><strong>Consonant start:</strong> Move all leading consonants to the end, then add "ay" → <em>string</em> becomes <em>ingstray</em></li>
          <li><strong>Multiple consonants:</strong> Move the entire cluster → <em>school</em> becomes <em>oolschay</em></li>
          <li>Capitalization is preserved — <em>Hello</em> becomes <em>Ellohay</em></li>
        </ol>
      </div>
      <h2>Examples</h2>
      <Ex arrow="apple">appleway</Ex>
      <Ex arrow="banana">ananabay</Ex>
      <Ex arrow="string">ingstray</Ex>
      <Ex arrow="Hello World">Ellohay Orldway</Ex>
      <h2>Tips & Strategies</h2>
      <ul>
        <li><strong>Practice speed:</strong> Try speaking Pig Latin in real-time — it's a great phonemic awareness exercise for kids.</li>
        <li><strong>Remember vowel clusters:</strong> Consecutive vowels at the start (e.g., "ea" in "each") stay together — <em>each</em> → <em>eachway</em>.</li>
        <li><strong>Silent letters:</strong> Move the entire initial consonant cluster even if some are silent — <em>knight</em> → <em>ightkay</em> (kn- together).</li>
        <li><strong>Use for kids:</strong> Pig Latin is a wonderful early literacy game — it forces children to consciously identify the first sound in a word.</li>
      </ul>
      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'Are there different versions of Pig Latin?', a: 'Yes — some variants add "yay" instead of "way" for vowel-starting words (apple → appleyay), or use "ay" universally. Our converter uses the most common standard: "way" for vowels, "ay" for consonants.' },
        { q: 'Is Pig Latin a real language?', a: 'No — it\'s a language game, not a natural language. It has no native speakers, no grammar of its own, and no vocabulary beyond transformed English words.' },
        { q: 'Can I reverse Pig Latin?', a: 'Yes! Toggle to "Pig Latin → English" mode and paste your Pig Latin text. The converter applies the inverse rules to restore the original words.' },
      ]} />
      <h2>Related Tools</h2>
      <Related links={[['🔁 Palindrome Checker','/palindrome'],['🔤 Anagram Solver','/anagram-solver'],['🎵 Rhyme Finder','/rhyme'],['🐝 Spelling Bee','/spelling-bee']]} />
    </div>
  ),

  /* ══ WORD SCRAMBLE GAME ══════════════════════════════════════════════════ */
  'word-scramble': () => (
    <div className="seo-section">
      <h2>About Word Scramble</h2>
      <p>
        <strong>Word Scramble</strong> is one of the most universally loved word games, appearing
        on cereal boxes, in classrooms, on game shows, and in millions of puzzle books worldwide.
        The premise is beautifully simple: letters of a word are shuffled into a random order,
        and the player must identify the original word. The timed element transforms this from a
        calm puzzle into an exciting mental sprint.
      </p>
      <p>
        Research in cognitive psychology shows that timed word-recognition exercises like Word
        Scramble improve processing speed, strengthen working memory, and build mental lexicon
        depth. Players who regularly practice word scrambles tend to perform better on standardized
        vocabulary tests and reading comprehension assessments.
      </p>
      <div className="seo-card">
        <h3>How to Play</h3>
        <ol>
          <li>Click <strong>Start Game</strong> — a random word is fetched and scrambled.</li>
          <li>You have <strong>60 seconds</strong> — type your answer in the box.</li>
          <li>Hit Enter to submit. Correct? Score points and move to the next word instantly!</li>
          <li>Wrong? The tiles shake — clear the box and try again before time runs out.</li>
          <li>Use <strong>Reshuffle</strong> if the scramble isn't clicking — fresh arrangement, same word.</li>
          <li>Points are based on word length plus a time bonus — faster = more points!</li>
        </ol>
      </div>
      <h2>Scoring</h2>
      <Ex arrow="Points per word">Word length + (time remaining ÷ 10) + streak bonus</Ex>
      <h2>Tips & Strategies</h2>
      <ul>
        <li><strong>Spot vowels first:</strong> Identify all the vowels and mentally group them — most English words follow CV (consonant-vowel) patterns.</li>
        <li><strong>Look for common endings:</strong> -ING, -ED, -ER, -LY, -ION appear in thousands of words — check if your letters fit.</li>
        <li><strong>Reshuffle strategically:</strong> If you're stuck after 10 seconds, reshuffle — a new arrangement often triggers instant recognition.</li>
        <li><strong>Build your streak:</strong> Consecutive correct answers multiply your score — prioritize accuracy over speed when on a hot streak.</li>
      </ul>
      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'How long are the words?', a: 'Words range from 4 to 9 letters — short enough to be solvable under pressure, long enough to be genuinely challenging. Word length is random each round.' },
        { q: 'What if the scramble looks like the original word?', a: 'The scrambler checks that the scrambled version differs from the original and reshuffles automatically if it accidentally produces the real word.' },
        { q: 'Is there a multiplayer mode?', a: 'Not currently — it\'s a solo score-attack game. Challenge a friend by sharing your score and asking them to beat it!' },
      ]} />
      <h2>Related Tools</h2>
      <Related links={[['🔍 Word Unscrambler','/unscramble'],['🎭 Hangman','/hangman'],['🟩 Wordle Game','/wordle'],['⛓️ Word Chain','/word-chain']]} />
    </div>
  ),

  /* ══ WORD CHAIN GAME ═════════════════════════════════════════════════════ */
  'word-chain': () => (
    <div className="seo-section">
      <h2>About Word Chain</h2>
      <p>
        <strong>Word Chain</strong> (also called Word Association, Last Letter, or Shiritori in
        Japanese) is a vocabulary game played in classrooms, on long car rides, and in language
        learning programs around the world. The rule is elegantly simple: each new word must begin
        with the final letter of the previous word, forming an unbroken chain. The game ends when
        a player cannot think of a valid word — or repeats a word already used.
      </p>
      <p>
        Shiritori — the Japanese version — has been a children's staple for centuries and is still
        played competitively today, with speed and rare vocabulary depth separating champions from
        casual players. Our digital Word Chain validates every entry against a 72,000-word
        dictionary, ensuring fair play and exposing you to words you may never have considered.
      </p>
      <div className="seo-card">
        <h3>How to Play</h3>
        <ol>
          <li>Click <strong>Start Game</strong> — a random starting word is given to you.</li>
          <li>Type any valid English word that <strong>starts with the last letter</strong> of the current word.</li>
          <li>Words must be at least 3 letters long and must be real English words.</li>
          <li>You cannot reuse a word that has already appeared in the current chain.</li>
          <li>Keep going as long as you can — try to beat your personal chain-length record!</li>
        </ol>
      </div>
      <h2>Example Chain</h2>
      <Ex>APPLE → EAGLE → ELECT → TIGER → RAVEN → NIGHT → TRAIN → NOBLE → ELDER → …</Ex>
      <h2>Tips & Strategies</h2>
      <ul>
        <li><strong>End on a vowel:</strong> Words ending in A, E, I, O, U give you the most options for the next word — steer your chain toward vowel endings.</li>
        <li><strong>Beware of X and Z:</strong> Words ending in X or Z are chain-killers — the next player has very few options. Avoid ending there yourself.</li>
        <li><strong>Stock common starters:</strong> Know words starting with every letter, especially uncommon ones like Q (queen, quest, quill) and X (xenon, xylem).</li>
        <li><strong>Long chains need variety:</strong> Branch into different topic areas (animals, foods, places) to avoid exhausting a single category's words.</li>
      </ul>
      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'What happens if the chain gets stuck on a hard letter?', a: 'Every letter in English has at least some common words — even X (xenon, xylem, xerox) and Z (zero, zinc, zone, zoom). If you\'re truly stuck, use the Give Up button to see your final score.' },
        { q: 'Are proper nouns allowed?', a: 'No — our dictionary contains common English words only. Proper nouns (names, places, brands) are not valid, matching the standard rules of competitive Word Chain.' },
        { q: 'What\'s a good chain length?', a: 'A chain of 10+ words is solid, 20+ is impressive, and 30+ puts you in expert territory. The theoretical maximum is unlimited since English has enough words — it\'s your memory and vocabulary that\'s the real limit.' },
      ]} />
      <h2>Related Tools</h2>
      <Related links={[['⏱️ Word Scramble','/word-scramble'],['🪜 Word Ladder','/word-ladder'],['🎭 Hangman','/hangman'],['🟩 Wordle Game','/wordle']]} />
    </div>
  ),

  /* ══ SYLLABLE COUNTER ═══════════════════════════════════════════════════ */
  'syllable-counter': () => (
    <div className="seo-section">
      <h2>What Is a Syllable?</h2>
      <p>
        A <strong>syllable</strong> is the fundamental unit of spoken language — a single, uninterrupted
        sound that forms part of a word. Every syllable contains exactly one vowel sound (though not
        necessarily one vowel letter). The word <em>beautiful</em> has three syllables: <strong>beau-ti-ful</strong>.
        The word <em>cat</em> has one. <em>Communication</em> has five: <strong>com-mu-ni-ca-tion</strong>.
      </p>
      <p>
        Understanding syllables is crucial for poetry, songwriting, speech therapy, language learning, and
        teaching reading to children. Syllable awareness — the ability to hear and manipulate syllable
        units — is one of the strongest early predictors of reading success according to decades of
        literacy research.
      </p>

      <h2>How to Count Syllables</h2>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>The Chin Method</h3>
            <p>Place your hand under your chin. Say the word aloud slowly. Count how many times your chin
            drops — each drop is one syllable. Your jaw physically lowers once per vowel sound, making
            this a reliable physical technique for learners of any age.</p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>The Clap Method</h3>
            <p>Clap your hands as you say each part of a word. <em>Hap-pi-ness</em> gets three claps.
            <em>Sun</em> gets one. This method is widely used in primary school classrooms because
            the physical rhythm helps children feel the natural breaks in words.</p>
          </div>
        </div>
      </div>

      <h2>Syllable Counting Rules</h2>
      <ul>
        <li><strong>Each vowel sound = one syllable.</strong> "Cake" has two vowels (a, e) but only one vowel sound — one syllable.</li>
        <li><strong>Silent E doesn't count.</strong> "Bike" = 1 syllable. The e is silent.</li>
        <li><strong>Vowel teams count as one.</strong> "Rain" has ai together — one vowel sound, one syllable.</li>
        <li><strong>Diphthongs count as one.</strong> "Coin" uses oi as a single gliding sound — one syllable.</li>
        <li><strong>-le endings with a consonant before them form a syllable.</strong> "Ta-ble" = 2, "sim-ple" = 2.</li>
        <li><strong>Prefixes and suffixes often add syllables.</strong> "Un-hap-pi-ness" = 4 syllables.</li>
      </ul>

      <h2>Syllables in Poetry and Songwriting</h2>
      <p>
        Every major poetic form relies on syllable counting. The <strong>haiku</strong> follows a strict
        5-7-5 pattern across three lines. <strong>Iambic pentameter</strong> — used by Shakespeare — requires
        10 syllables per line in alternating unstressed and stressed beats. <strong>Limericks</strong> follow
        an AABBA scheme with 8-8-5-5-8 syllables per line.
      </p>
      <p>
        Songwriters use syllable counts to ensure lyrics fit naturally over a melody. When a lyric feels
        awkward or forced, it's often because the syllable count doesn't match the musical phrase. Our
        syllable counter helps you fine-tune every line before you commit it to music.
      </p>

      <h2>Common Syllable Counts</h2>
      <div className="row g-2">
        {[
          { n: '1 syllable', ex: 'cat, dog, sun, bright, strength, through' },
          { n: '2 syllables', ex: 'happy, garden, pencil, window, pretty' },
          { n: '3 syllables', ex: 'beautiful, cinema, however, umbrella' },
          { n: '4 syllables', ex: 'communication, education, information' },
          { n: '5 syllables', ex: 'extraordinary, congratulations, television' },
        ].map(({ n, ex }) => (
          <div key={n} className="col-12">
            <div className="seo-example"><strong>{n}:</strong> {ex}</div>
          </div>
        ))}
      </div>

      <h2>How Many Syllables in Common Words?</h2>
      <p>
        Some words trip people up — either because their spelling hides a spoken syllable, or because
        the count genuinely shifts between American and British English or casual and careful speech.
        Here are the words people most often look up, with the standard syllable count, the spoken
        breakdown, and a note where dialect or pace changes the answer. Type any of them into the
        counter above to hear the same result.
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table className="syllable-table" style={{ width: '100%', borderCollapse: 'collapse', margin: '0.5rem 0 1rem', fontFamily: 'Nunito, sans-serif' }}>
          <thead>
            <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
              <th style={{ padding: '0.5rem 0.75rem', borderBottom: '2px solid var(--border)' }}>Word</th>
              <th style={{ padding: '0.5rem 0.75rem', borderBottom: '2px solid var(--border)', textAlign: 'center' }}>Syllables</th>
              <th style={{ padding: '0.5rem 0.75rem', borderBottom: '2px solid var(--border)' }}>Breakdown</th>
              <th style={{ padding: '0.5rem 0.75rem', borderBottom: '2px solid var(--border)' }}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['fire', 1, 'fire', 'Often stretched to 2 (fi-er) in slow or Southern US speech.'],
              ['hour', 1, 'hour', 'The h is silent; rhymes with "our".'],
              ['our', 1, 'our', 'One sound; sometimes drawn out to 2.'],
              ['poem', 2, 'po-em', 'Two vowel sounds in a row — never just 1.'],
              ['every', 3, 'ev-er-y', 'Commonly compressed to 2 (ev-ry) in fast speech.'],
              ['different', 3, 'dif-fer-ent', 'Usually said as 2 (dif-rent) in everyday speech.'],
              ['chocolate', 3, 'choc-o-late', 'Frequently reduced to 2 (choc-late).'],
              ['beautiful', 3, 'beau-ti-ful', 'The "beau" is a single vowel sound.'],
              ['family', 3, 'fam-i-ly', 'Often shortened to 2 (fam-ly).'],
              ['favorite', 3, 'fa-vor-ite', 'Commonly 2 (fav-rit); UK spells it "favourite".'],
              ['science', 2, 'sci-ence', 'Two vowel sounds — not 1.'],
              ['being', 2, 'be-ing', 'The -ing adds a clear second syllable.'],
              ['people', 2, 'peo-ple', 'The -ple ending is its own syllable.'],
              ['little', 2, 'lit-tle', 'Consonant + -le always forms a syllable.'],
              ['area', 3, 'a-re-a', 'Three separate vowel sounds.'],
              ['idea', 3, 'i-de-a', 'Ends in two vowel sounds (de-a).'],
              ['quiet', 2, 'qui-et', 'Not to be confused with "quite" (1).'],
              ['orange', 2, 'or-ange', 'One of the classic "hard to rhyme" 2-syllable words.'],
              ['business', 2, 'busi-ness', 'Looks like 3, said as 2 (biz-ness).'],
              ['evening', 3, 'eve-ning', 'Often reduced to 2 (eev-ning).'],
              ['everything', 3, 'ev-ery-thing', 'Compound; commonly 3.'],
              ['interesting', 4, 'in-ter-est-ing', 'Very often said as 3 (in-tres-ting).'],
              ['comfortable', 4, 'com-fort-a-ble', 'Usually 3 in speech (comf-ter-ble).'],
              ['vegetable', 4, 'veg-e-ta-ble', 'Commonly reduced to 3 (veg-ta-ble).'],
              ['temperature', 4, 'tem-per-a-ture', 'Often 3 in casual speech (tem-pruh-chur).'],
              ['caramel', 3, 'car-a-mel', 'Regionally 2 (car-mel).'],
              ['squirrel', 2, 'squir-rel', 'US 2 syllables; UK often 1 (skwirl).'],
              ['rhythm', 2, 'rhyth-m', 'The final m carries a syllable with no written vowel.'],
              ['lion', 2, 'li-on', 'Two vowel sounds.'],
              ['giant', 2, 'gi-ant', 'Two vowel sounds (gi-ant).'],
              ['create', 2, 'cre-ate', 'The "ea" splits into two sounds.'],
              ['water', 2, 'wa-ter', 'Straightforward 2 syllables.'],
            ].map(([word, syl, breakdown, note]) => (
              <tr key={word} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '0.5rem 0.75rem', fontWeight: 700 }}>{word}</td>
                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center', fontWeight: 800, color: 'var(--primary)' }}>{syl}</td>
                <td style={{ padding: '0.5rem 0.75rem' }}><em>{breakdown}</em></td>
                <td style={{ padding: '0.5rem 0.75rem', color: '#64748b', fontSize: '0.9rem' }}>{note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'How does the syllable counter work?',
          a: 'The counter identifies vowel sound groups in each word using linguistic rules. It accounts for silent e endings, consonant + -le endings, vowel teams, common suffixes like -ed and -es, and a dictionary of well-known irregular words to produce an accurate count for the vast majority of English words.' },
        { q: 'Is this accurate for every word?',
          a: 'It is accurate for the vast majority of common English words. A small number of irregular words — particularly those borrowed from other languages — may differ slightly from strict dictionary pronunciation guides.' },
        { q: 'Can I count syllables in a full sentence?',
          a: 'Yes — simply paste your full sentence, paragraph, or poem into the input box. The counter will analyze each word individually and show you the total syllable count along with a per-word breakdown.' },
        { q: 'Does it work for haiku?',
          a: 'Yes! Enter your haiku with each line on a separate line. The tool will check if each line meets the 5-7-5 syllable requirement and highlight whether your haiku is valid.' },
        { q: 'Why do some words have different syllable counts in different dictionaries?',
          a: 'Syllabification can vary between American and British English, and some words have alternative acceptable pronunciations. For example, "caramel" is commonly pronounced with 2 or 3 syllables depending on dialect.' },
      ]} />

      <h2>Related Tools</h2>
      <Related links={[
        ['📝 Word Counter', '/word-counter'],
        ['🐷 Pig Latin', '/pig-latin'],
        ['🎵 Rhyme Finder', '/rhyme'],
        ['🔤 Anagram Solver', '/anagram-solver'],
      ]} />
    </div>
  ),

  /* ══ WORD COUNTER ════════════════════════════════════════════════════════ */
  'word-counter': () => (
    <div className="seo-section">
      <h2>Why Use a Word Counter?</h2>
      <p>
        Whether you're a student writing an essay, a blogger crafting an article, a novelist tracking
        your daily output, or a professional preparing a report — knowing your word count matters.
        Most writing platforms have word limits or minimums: college essays (650 words), tweets
        (280 characters), LinkedIn posts (3,000 characters), novels (80,000 words). Our
        <strong> free online word counter</strong> gives you all the stats you need in real time,
        as you type.
      </p>
      <p>
        Beyond simple word counts, our tool provides character counts, sentence counts, paragraph
        counts, estimated reading time, estimated speaking time, and keyword frequency analysis —
        everything a writer needs to understand and optimize their text.
      </p>

      <h2>What Our Word Counter Measures</h2>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Text Statistics</h3>
            <ul>
              <li><strong>Word count</strong> — total number of words</li>
              <li><strong>Characters with spaces</strong> — total characters including whitespace</li>
              <li><strong>Characters without spaces</strong> — non-whitespace characters only</li>
              <li><strong>Sentence count</strong> — based on . ! ? punctuation</li>
              <li><strong>Paragraph count</strong> — separated by blank lines</li>
              <li><strong>Unique words</strong> — vocabulary diversity of your text</li>
            </ul>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Time & Quality Metrics</h3>
            <ul>
              <li><strong>Reading time</strong> — at average 200 words per minute</li>
              <li><strong>Speaking time</strong> — at average 130 words per minute</li>
              <li><strong>Average word length</strong> — a proxy for text complexity</li>
              <li><strong>Longest word</strong> — the most complex word in your text</li>
              <li><strong>Top keywords</strong> — most frequently used meaningful words</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Word Count Requirements by Writing Type</h2>
      <div className="row g-2">
        {[
          { type: 'Tweet / X Post', count: '280 characters' },
          { type: 'Instagram Caption', count: 'Up to 2,200 characters' },
          { type: 'College Application Essay (Common App)', count: '650 words' },
          { type: 'Blog Post (good SEO)', count: '1,500 – 2,500 words' },
          { type: 'Short Story', count: '1,000 – 7,500 words' },
          { type: 'Novella', count: '20,000 – 50,000 words' },
          { type: 'Novel', count: '80,000 – 100,000 words' },
          { type: 'PhD Dissertation', count: '80,000 – 100,000 words' },
        ].map(({ type, count }) => (
          <div key={type} className="col-12">
            <div className="seo-example"><strong>{type}:</strong> {count}</div>
          </div>
        ))}
      </div>

      <h2>Understanding Reading Time</h2>
      <p>
        The average adult reads approximately <strong>200–250 words per minute</strong> for general
        content. Technical or complex material may be read at 150 wpm or slower. Speed readers can
        exceed 400 wpm. Our reading time estimate uses 200 wpm as a conservative baseline.
      </p>
      <p>
        <strong>Speaking time</strong> is estimated at 130 words per minute — the average pace for
        a clear, well-paced speech or presentation. Public speakers typically aim for 120–150 wpm
        to maintain clarity and allow the audience to absorb information. TED Talk speakers average
        around 163 wpm.
      </p>

      <h2>Keyword Density and SEO</h2>
      <p>
        The keyword frequency section shows which meaningful words appear most often in your text
        (excluding common stop words like "the", "and", "is"). This is useful for SEO writing —
        search engines look at keyword density to understand what a page is about. A good target
        is 1-2% keyword density: if you're writing 1,000 words targeting "crossword solver", that
        keyword should appear roughly 10-20 times.
      </p>

      <h2>Social Media Character Limits</h2>
      <p>
        Every platform caps how long a post can be, and going over means your caption gets cut off
        with a "…more" or is rejected outright. As you type, our built-in <strong>Social Media Post
        Fit</strong> meter shows a live bar for each network — green while you have room, amber near the
        limit, and red once you're over — so you can trim before you publish. Here are the current
        character limits:
      </p>
      <div className="row g-2">
        {[
          { p: 'SMS text message', n: '160 characters' },
          { p: 'X / Twitter post', n: '280 characters (free accounts)' },
          { p: 'Bluesky post', n: '300 characters' },
          { p: 'Threads post', n: '500 characters' },
          { p: 'Instagram caption', n: '2,200 characters' },
          { p: 'TikTok caption', n: '2,200 characters' },
          { p: 'LinkedIn post', n: '3,000 characters' },
          { p: 'Facebook post', n: '63,206 characters' },
        ].map(({ p, n }) => (
          <div key={p} className="col-12 col-md-6">
            <div className="seo-example"><strong>{p}:</strong> {n}</div>
          </div>
        ))}
      </div>
      <p>
        Remember that limits are counted in <em>characters</em> (including spaces and emoji), not words —
        which is why the meter tracks your character count. For the best engagement, most social posts
        perform well far below the maximum: short, punchy captions often beat wall-of-text posts.
      </p>

      <h2>How Many Words Count Per Page</h2>
      <p>
        "How many pages is 1,000 words?" is one of the most searched writing questions — and the honest
        answer is "it depends on your formatting." Page count shifts with font, size, spacing and margins.
        The table below uses the most common academic default — <strong>12&nbsp;pt Times New Roman with
        1-inch margins</strong> — which fits roughly <strong>500 words per single-spaced page</strong> and
        <strong> 250 words per double-spaced page</strong>. Paste your text above and the live counter tells
        you exactly where you stand.
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', margin: '0.5rem 0 1rem', fontFamily: 'Nunito, sans-serif' }}>
          <thead>
            <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
              <th style={{ padding: '0.5rem 0.75rem', borderBottom: '2px solid var(--border)' }}>Word Count</th>
              <th style={{ padding: '0.5rem 0.75rem', borderBottom: '2px solid var(--border)' }}>Single-Spaced</th>
              <th style={{ padding: '0.5rem 0.75rem', borderBottom: '2px solid var(--border)' }}>Double-Spaced</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['250 words', '½ page', '1 page'],
              ['500 words', '1 page', '2 pages'],
              ['750 words', '1½ pages', '3 pages'],
              ['1,000 words', '2 pages', '4 pages'],
              ['1,500 words', '3 pages', '6 pages'],
              ['2,000 words', '4 pages', '8 pages'],
              ['2,500 words', '5 pages', '10 pages'],
              ['3,000 words', '6 pages', '12 pages'],
              ['5,000 words', '10 pages', '20 pages'],
              ['10,000 words', '20 pages', '40 pages'],
            ].map(([w, s, d]) => (
              <tr key={w} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '0.5rem 0.75rem', fontWeight: 700 }}>{w}</td>
                <td style={{ padding: '0.5rem 0.75rem' }}>{s}</td>
                <td style={{ padding: '0.5rem 0.75rem' }}>{d}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        Prefer a different setup? Arial and Calibri run slightly smaller, so you'll fit a touch more per
        page, while 1.5 line spacing lands between the two columns above. Handwritten work is different
        again — a standard sheet of lined paper holds roughly <strong>250–300 handwritten words</strong>.
      </p>

      <h2>How Long Is a Speech at This Word Count?</h2>
      <p>
        Writing a speech, class presentation, wedding toast or video script? What your audience feels is
        <em> time</em>, not word count. At a natural speaking pace of about <strong>130 words per
        minute</strong>, here's how long your words will take to say out loud. Almost everyone speeds up
        under pressure, so read your draft aloud and time it before the real thing.
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', margin: '0.5rem 0 1rem', fontFamily: 'Nunito, sans-serif' }}>
          <thead>
            <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
              <th style={{ padding: '0.5rem 0.75rem', borderBottom: '2px solid var(--border)' }}>Speaking Time</th>
              <th style={{ padding: '0.5rem 0.75rem', borderBottom: '2px solid var(--border)' }}>Approx. Words (at 130 wpm)</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['1 minute', '~130 words'],
              ['2 minutes', '~260 words'],
              ['3 minutes', '~390 words'],
              ['5 minutes', '~650 words'],
              ['7 minutes', '~910 words'],
              ['10 minutes', '~1,300 words'],
              ['15 minutes', '~1,950 words'],
              ['20 minutes', '~2,600 words'],
              ['30 minutes', '~3,900 words'],
            ].map(([t, w]) => (
              <tr key={t} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '0.5rem 0.75rem', fontWeight: 700 }}>{t}</td>
                <td style={{ padding: '0.5rem 0.75rem' }}>{w}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        These figures assume a relaxed, conversational delivery. If you speak quickly you may hit 160 wpm
        (fewer words needed); a slow, deliberate keynote can drop to 100 wpm (more words). The live
        Speaking Time estimate above updates as you type so you can match your script to the clock.
      </p>

      <h2>How to Hit Your Target Word Count While Writing</h2>
      <p>
        Stuck just under a minimum or way over a limit? Editing to a target is a skill — and it's far
        easier when the counter updates live as you work. Here's how to move your word count in either
        direction without padding fluff or gutting your meaning.
      </p>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Need more words?</h3>
            <ul>
              <li>Support each point with a specific example, statistic, or short quote.</li>
              <li>Answer the "so what?" — spell out why each idea actually matters.</li>
              <li>Address the obvious counterargument, then respond to it.</li>
              <li>Define key terms the first time they appear.</li>
              <li>Spell out contractions ("don't" → "do not") for a small, legitimate bump.</li>
            </ul>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Need fewer words?</h3>
            <ul>
              <li>Delete filler: "very", "really", "in order to" → "to".</li>
              <li>Cut redundant pairs like "each and every" or "first and foremost".</li>
              <li>Switch passive voice to active — it's shorter and punchier.</li>
              <li>Swap phrases for single words: "at this point in time" → "now".</li>
              <li>Trim adverbs and let strong verbs carry the sentence.</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Words, Characters, Sentences: What Should You Count?</h2>
      <p>
        Different tasks care about different units, and using the wrong one is a common way to miss a
        requirement. Use this quick guide: count <strong>words</strong> for essays, articles, and most
        academic work; count <strong>characters</strong> for social media posts, meta descriptions, and
        SMS; count <strong>sentences and paragraphs</strong> when a rubric specifies structure; and watch
        <strong> reading time</strong> for blog posts and newsletters, where a "5-minute read" label sets
        reader expectations. Our counter shows all of them at once, so you never have to guess which one
        your assignment is really asking for.
      </p>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'How many pages is 500 words?',
          a: 'About 1 page single-spaced or 2 pages double-spaced, using 12 pt Times New Roman with 1-inch margins. A 500-word essay is roughly 5 short paragraphs.' },
        { q: 'How many words is a 5-minute speech?',
          a: 'Around 650 words at a natural 130-words-per-minute pace. If you speak quickly aim for a little less (~600), and if you pause a lot for effect, closer to 550. Always time yourself reading it aloud.' },
        { q: 'How many words should I write per day?',
          a: 'It varies by goal: NaNoWriMo targets about 1,667 words a day to hit 50,000 words in a month, many professional authors aim for 1,000–2,000 words daily, and students often draft an essay in a single 500–1,000 word sitting. Use the live counter to track your session.' },
        { q: 'Is 1,000 words a lot?',
          a: 'For an essay or blog post, 1,000 words is a solid, standard length — roughly 4 double-spaced pages and a 4–5 minute read. For a social caption it is very long; for a novel chapter it is on the short side. Length is always relative to the format.' },
        { q: 'Does the word counter work offline?',
          a: 'Yes — all processing happens entirely in your browser. No text is ever sent to a server. Your content stays completely private and secure.' },
        { q: 'What counts as a word?',
          a: 'Any sequence of letters separated by spaces or punctuation. Hyphenated words like "well-known" are counted as one word. Numbers like "2024" are not counted as words.' },
        { q: 'How is reading time calculated?',
          a: 'We divide the word count by 200, which is the average adult silent reading speed for general content. The result is rounded up to the nearest minute.' },
        { q: 'What are stop words in the keyword section?',
          a: 'Stop words are common English words like "the", "a", "is", "and" that don\'t carry meaningful content. They\'re excluded from keyword frequency so you can focus on your actual topic words.' },
        { q: 'Can I count characters for social media limits?',
          a: 'Yes — the built-in Social Media Post Fit meter shows a live bar for X/Twitter (280), Bluesky (300), Threads (500), Instagram and TikTok (2,200), LinkedIn (3,000), Facebook (63,206) and SMS (160). It turns amber near a limit and red once you go over, using the "Characters with spaces" count that platforms measure.' },
      ]} />

      <h2>Related Tools</h2>
      <Related links={[
        ['🔢 Syllable Counter', '/syllable-counter'],
        ['🐷 Pig Latin Converter', '/pig-latin'],
        ['🔤 Anagram Solver', '/anagram-solver'],
        ['🎵 Rhyme Finder', '/rhyme'],
      ]} />
    </div>
  ),

  /* ══ CRYPTOGRAM SOLVER ══════════════════════════════════════════════════ */
  cryptogram: () => (
    <div className="seo-section">
      <h2>What Is a Cryptogram?</h2>
      <p>
        A <strong>cryptogram</strong> is a short piece of text encoded using a <strong>substitution cipher</strong> —
        a system where each letter of the original message is consistently replaced by a different letter.
        If A is replaced by X in one place, it's replaced by X everywhere in that message. Your job as the
        solver is to figure out the entire substitution key and reveal the hidden message.
      </p>
      <p>
        Cryptograms have entertained puzzle lovers for centuries. They appear in newspapers, puzzle books,
        and brain-training apps around the world under a few different names — most commonly as a{' '}
        <strong>Cryptoquip</strong> or a <strong>Cryptoquote</strong>. Famous historical figures used
        substitution ciphers to protect secrets — Julius Caesar used one of the simplest (shifting each
        letter by 3), while Mary Queen of Scots used a complex cipher that, when broken, revealed
        treasonous plans that cost her life.
      </p>

      <h2>Cryptogram vs. Cryptoquip vs. Cryptoquote</h2>
      <p>
        These three names all describe the same underlying puzzle — a message encoded with a{' '}
        <strong>substitution cipher</strong> — just packaged differently, and our solver works for all of them.
      </p>
      <div className="row g-3">
        <div className="col-md-4">
          <div className="seo-card">
            <h3>Cryptogram</h3>
            <p>The general term for any substitution-cipher puzzle. Can encode a single sentence, a quote,
            or a whole paragraph. Common in puzzle books and brain-training apps.</p>
          </div>
        </div>
        <div className="col-md-4">
          <div className="seo-card">
            <h3>Cryptoquip</h3>
            <p>A syndicated daily newspaper puzzle (King Features Syndicate) that encodes a short,
            punny sentence or joke. Solved the same way as any cryptogram — letter by letter.</p>
          </div>
        </div>
        <div className="col-md-4">
          <div className="seo-card">
            <h3>Cryptoquote</h3>
            <p>A cryptogram built from a famous quotation, usually with the author's name encoded too.
            Popular in newspapers and puzzle magazines as a daily brain teaser.</p>
          </div>
        </div>
      </div>

      <h2>How Substitution Ciphers Work</h2>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>The Caesar Cipher (Shift Cipher)</h3>
            <p>The simplest substitution cipher shifts every letter by a fixed number. Julius Caesar used
            a shift of 3: A→D, B→E, C→F… and so on. Our sample cryptograms use Caesar shift-3 so you
            can verify your solutions easily.</p>
            <Ex arrow="HELLO (shift +3)">KHOOR</Ex>
            <Ex arrow="KHOOR (shift -3)">HELLO</Ex>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Simple Substitution Cipher</h3>
            <p>A more secure cryptogram uses a random key where each letter maps to a completely
            different letter (not just shifted). All 26 letters are remapped, creating 403 septillion
            possible keys — uncrackable by brute force but solvable by frequency analysis.</p>
          </div>
        </div>
      </div>

      <h2>How to Solve a Cryptogram: Frequency Analysis</h2>
      <p>
        The most powerful technique for cracking substitution ciphers is <strong>frequency analysis</strong>,
        invented by the 9th-century Arab mathematician Al-Kindi. The insight: certain letters appear far more
        often in English than others. By counting how often each cipher letter appears and comparing to known
        English frequencies, you can make educated guesses.
      </p>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>English Letter Frequency Order</h3>
            <ol>
              <li><strong>E</strong> — ~13% of all text</li>
              <li><strong>T</strong> — ~9%</li>
              <li><strong>A</strong> — ~8%</li>
              <li><strong>O</strong> — ~7.5%</li>
              <li><strong>I</strong> — ~7%</li>
              <li><strong>N</strong> — ~6.7%</li>
              <li><strong>S, H, R</strong> — ~6% each</li>
            </ol>
            <p>If cipher letter <strong>Q</strong> appears 15 times in a message, it's very likely <strong>E</strong>.</p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Pattern-Based Clues</h3>
            <ul>
              <li>Single-letter word = <strong>A</strong> or <strong>I</strong></li>
              <li>Most common 2-letter words: <strong>OF, TO, IN, IS, IT, BE, AS, AT, SO</strong></li>
              <li>Most common 3-letter words: <strong>THE, AND, FOR, ARE, BUT</strong></li>
              <li>Double letters: <strong>LL, SS, EE, TT, FF, OO, PP, RR</strong></li>
              <li>Apostrophe pattern _'T = IT or AT</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>How to Use Our Cryptogram Solver</h2>
      <p>
        Whether you're stuck on today's newspaper <strong>Cryptoquip</strong>, a <strong>Cryptoquote</strong> from
        a puzzle magazine, or any other substitution cryptogram, the steps are the same:
      </p>
      <ol>
        <li>Paste your encrypted message into the input box or click a sample puzzle.</li>
        <li>Study the <strong>Letter Frequency Analysis</strong> bar chart — taller bars = more frequent cipher letters.</li>
        <li>Click <strong>"Frequency Hint"</strong> to auto-map the 3 most common cipher letters to E, T, A.</li>
        <li>For each cipher letter, type your guessed plaintext letter in the mapping box.</li>
        <li>Watch the <strong>Decoded Message</strong> update in real time — dots (·) are unmapped letters.</li>
        <li>When you've mapped all letters, the progress bar reaches 100% and the full message is revealed.</li>
      </ol>

      <h2>How to Make Your Own Cryptogram</h2>
      <p>
        Want to act as a <strong>cryptogram generator</strong> and stump a friend, a classroom, or a puzzle
        club? Making one by hand takes just a few minutes:
      </p>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Step by Step</h3>
            <ol>
              <li>Pick a short sentence, joke, or quotation — 6 to 20 words works best.</li>
              <li>Write the alphabet A–Z, then write a scrambled version of the alphabet underneath it
              as your cipher key (or use a fixed shift for an easier Caesar-style puzzle).</li>
              <li>Replace every letter in your message with its matched letter from the key. Keep spacing
              and punctuation exactly as in the original — only letters change.</li>
              <li>Double-check that no letter secretly maps to itself, which can give away easy clues.</li>
              <li>Share the encoded text (and, for a Cryptoquote, the author's name encoded too) and let
              the solver work backward using frequency analysis.</li>
            </ol>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Tips for a Good Puzzle</h3>
            <ul>
              <li>Use a real, natural-sounding sentence — random word salad is harder to solve fairly and
              less fun to crack.</li>
              <li>A random substitution key (not a simple shift) makes the puzzle noticeably harder, since
              solvers can't just try all 25 shifts.</li>
              <li>Include a few short words (<em>the, and, is, a</em>) — they give solvers a fair foothold
              and keep the puzzle from feeling impossible.</li>
              <li>Test your own puzzle in our solver first to confirm it decodes cleanly before sharing it.</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Famous Historical Cryptograms</h2>
      <p>
        Cryptography has shaped the course of history. During World War II, Allied codebreakers at Bletchley
        Park cracked the German Enigma machine cipher — an achievement credited with shortening the war by
        up to two years. The Zodiac Killer sent cryptograms to newspapers in the 1960s-70s; one took
        51 years to crack (solved in 2020). Edgar Allan Poe was so obsessed with ciphers that he challenged
        readers to stump him — and featured one in his story "The Gold Bug."
      </p>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'Is this a Cryptoquip solver?',
          a: 'Yes. A Cryptoquip is just a substitution-cipher cryptogram, so you can paste today\'s Cryptoquip straight into the tool and solve it the same way — map cipher letters to real letters and watch the punny sentence decode as you type.' },
        { q: 'Does this work as a Cryptoquote solver too?',
          a: 'Yes. A Cryptoquote is a cryptogram made from a famous quotation, often with the author\'s name encoded as well. Paste the full encoded quote (and byline, if included) into the input and solve it letter by letter, same as any cryptogram.' },
        { q: 'What is the difference between a cryptogram and a code?',
          a: 'A cipher (like a cryptogram) operates on individual letters or groups of letters. A code substitutes whole words or phrases for code words or numbers. Cryptograms use ciphers — specifically substitution ciphers where each letter maps to another letter.' },
        { q: 'Can I solve any cryptogram with frequency analysis?',
          a: 'Frequency analysis works well for long texts (100+ letters) where English letter frequencies are observable. Very short cryptograms (under 30 letters) may not have enough data for reliable frequency analysis, and you\'ll rely more on word-pattern recognition.' },
        { q: 'What if two cipher letters seem to map to the same plaintext letter?',
          a: 'In a substitution cipher, each cipher letter maps to exactly one plaintext letter and vice versa — it\'s a one-to-one mapping. If your guesses create a conflict, one of your mappings is wrong. Revisit your frequency analysis.' },
        { q: 'Are the sample cryptograms the same each time?',
          a: 'Yes — our four sample cryptograms are fixed classic examples so you can check your solutions against the revealed answers. For fresh challenges, copy cryptograms from puzzle books or newspapers and paste them in.' },
        { q: 'How do I create my own cryptogram puzzle?',
          a: 'Pick a short sentence, write a scrambled (or shifted) version of the alphabet as your key, then swap every letter in your message for its matched letter from the key. Keep spacing and punctuation unchanged. Test it in our solver first to make sure it decodes cleanly.' },
      ]} />

      <h2>Related Tools</h2>
      <Related links={[
        ['📡 Morse Code Converter', '/morse-code'],
        ['🐷 Pig Latin Converter', '/pig-latin'],
        ['🔤 Anagram Solver', '/anagram-solver'],
        ['✏️ Crossword Solver', '/crossword'],
      ]} />
    </div>
  ),

  /* ══ MORSE CODE CONVERTER ════════════════════════════════════════════════ */
  'morse-code': () => (
    <div className="seo-section">
      <h2>What Is Morse Code?</h2>
      <p>
        <strong>Morse code</strong> is a method of encoding text using sequences of short signals (dots, ·)
        and long signals (dashes, —). Originally designed for telegraph communication in the 1830s, it became
        the global standard for maritime distress signals and long-distance radio communication for over a
        century. Today, Morse code is studied by amateur radio operators, used in accessibility devices, and
        enjoyed as a fascinating historical communication system.
      </p>
      <p>
        Each letter, digit, and punctuation mark has a unique Morse sequence. The letter E (the most common
        in English) is just a single dot — the shortest possible signal. Q is "– – · –" — one of the longest.
        This clever design minimized transmission time for common letters.
      </p>

      <h2>The History of Morse Code</h2>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Samuel Morse & Alfred Vail (1836–1844)</h3>
            <p>Samuel F.B. Morse, an American artist and inventor, developed the electrical telegraph and
            its accompanying code system with his partner Alfred Vail. On May 24, 1844, Morse sent the
            first official message — "What hath God wrought" — from Washington D.C. to Baltimore,
            inaugurating a new era of instant long-distance communication.</p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>International Morse Code (1865)</h3>
            <p>The original American Morse code was simplified and standardized into <strong>International
            Morse Code</strong> at the International Telegraphy Conference in Paris (1865). This version,
            with cleaner dot-dash patterns for all 26 letters, is what we use today and what our converter
            is built on.</p>
          </div>
        </div>
      </div>

      <h2>How to Read and Write Morse Code</h2>
      <p>
        Morse code uses two symbols: <strong>dot (·)</strong> and <strong>dash (—)</strong>, combined into
        sequences of 1 to 5 signals per character. Spacing is critical:
      </p>
      <ul>
        <li><strong>Between dots/dashes in one letter:</strong> 1 unit of silence</li>
        <li><strong>Between letters in a word:</strong> 3 units of silence</li>
        <li><strong>Between words:</strong> 7 units of silence</li>
      </ul>
      <p>
        In written form (like our converter output), letters are separated by spaces and words are separated
        by a forward slash (/). So "SOS" = <strong>... / --- / ...</strong> with spaces between letter groups.
      </p>

      <h2>Famous Morse Code Messages</h2>
      <div className="row g-2">
        {[
          { msg: 'SOS', morse: '... --- ...', note: 'Universal distress signal (chosen for its simplicity, not an acronym)' },
          { msg: 'WHAT HATH GOD WROUGHT', morse: '.-- .... .- - / .... .- - .... / --. --- -.. / .-- .-. --- ..- --. .... -', note: 'First telegraph message, 1844' },
          { msg: 'CQD', morse: '-.-. --.- -..', note: 'Original maritime distress signal before SOS (1904–1908)' },
          { msg: 'V FOR VICTORY', morse: '...- / ..-. --- .-. / ...- .. -.-. - --- .-. -.--', note: 'Churchill\'s WWII symbol — three dots + dash = Beethoven\'s 5th opening' },
        ].map(({ msg, morse, note }) => (
          <div key={msg} className="col-12">
            <div className="seo-example">
              <strong>{msg}</strong> → <code style={{ letterSpacing: 2 }}>{morse}</code>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 2 }}>{note}</div>
            </div>
          </div>
        ))}
      </div>

      <h2>Morse Code in Modern Use</h2>
      <p>
        While telegraph lines are long gone, Morse code lives on in several modern contexts. <strong>Amateur
        radio</strong> (ham radio) operators still use Morse code — it's useful when voice is unclear and
        requires minimal bandwidth. Many countries require Morse proficiency for certain radio licenses.
        <strong>Military and aviation</strong> use Morse for navigation beacons (VOR stations broadcast their
        identifier in Morse). <strong>Accessibility technology</strong> uses Morse input — people with
        physical disabilities can type using two switches for dot and dash. Even the iPhone lets you type
        using Morse code via AssistiveTouch.
      </p>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'Does SOS stand for "Save Our Souls" or "Save Our Ship"?',
          a: 'Neither! SOS was chosen as an international distress signal in 1908 purely because its Morse pattern (... --- ...) is distinctive and easy to remember — three dots, three dashes, three dots. The acronym expansions are folk etymologies that came later.' },
        { q: 'How fast can someone transmit Morse code?',
          a: 'Speed is measured in WPM (words per minute). Beginners manage 5-10 WPM. Proficient operators reach 20-30 WPM. The world record for receiving Morse is around 75 WPM. The standard PARIS word (with its letter pattern) is used as the basis for WPM measurement.' },
        { q: 'Can I use the audio player for all text?',
          a: 'Yes — our audio player works for any English text you enter in encode mode. It plays the Morse sequence at a comfortable 70ms per dot unit, with correct inter-letter and inter-word spacing.' },
        { q: 'What characters does your converter support?',
          a: 'Letters A–Z, digits 0–9, and common punctuation: period, comma, question mark, exclamation point, slash, at-sign, and hyphen. Unsupported characters are silently skipped in encoding mode.' },
      ]} />

      <h2>Related Tools</h2>
      <Related links={[
        ['🔐 Cryptogram Solver', '/cryptogram'],
        ['🐷 Pig Latin Converter', '/pig-latin'],
        ['🔤 Anagram Solver', '/anagram-solver'],
        ['📝 Word Counter', '/word-counter'],
      ]} />
    </div>
  ),

  /* ══ DORDLE ══════════════════════════════════════════════════════════════ */
  dordle: () => (
    <div className="seo-section">
      <h2>What Is Dordle?</h2>
      <p>
        <strong>Dordle</strong> is a word puzzle game where you solve <strong>two Wordle puzzles
        simultaneously</strong> using the same set of guesses. Every word you enter is checked against
        both secret words at once — giving you colored feedback on two separate boards. You get
        <strong> 7 guesses</strong> to figure out both 5-letter words (compared to Wordle's 6 guesses for one).
      </p>
      <p>
        Dordle was created by Guilherme Töws in January 2022 as a "double Wordle" challenge. It immediately
        gained a following among Wordle fans who wanted more complexity. The game tests your ability to
        track two independent deduction chains simultaneously — a significantly harder cognitive task than
        solving one Wordle alone.
      </p>

      <h2>How to Play Dordle</h2>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Basic Rules</h3>
            <ol>
              <li>Two secret 5-letter words are chosen at the start of each game.</li>
              <li>You have <strong>7 guesses</strong> total — shared between both boards.</li>
              <li>Each guess applies to <strong>both boards simultaneously</strong>.</li>
              <li>Color-coded feedback: 🟩 correct position, 🟨 wrong position, ⬛ not in word.</li>
              <li>Win by solving both words before using all 7 guesses.</li>
            </ol>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Keyboard Color Coding</h3>
            <p>The on-screen keyboard shows the <strong>best state</strong> across both boards for each
            key. If a letter is green (correct) in Board 1, the key shows green even if it's gray in
            Board 2. This gives you the most optimistic view of your confirmed letter placements.</p>
          </div>
        </div>
      </div>

      <h2>Dordle Strategy Tips</h2>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Information-First Opening</h3>
            <p>Your first 2-3 guesses should maximize information across the alphabet. Unlike regular
            Wordle where you might get lucky early, in Dordle you need to build a complete letter map
            before committing to guesses for either specific word. Classic strong openers: CRANE, STALE,
            MIGHT — cover 15 of the most common English letters.</p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Track Boards Independently</h3>
            <p>After your opening guesses, mentally (or on paper) separate the clues for each board.
            A yellow letter in Board 1 tells you something about Word 1; it tells you nothing about
            Word 2. Keep two separate mental models as you work toward each solution.</p>
          </div>
        </div>
      </div>
      <div className="seo-card">
        <h3>Prioritize the Harder Board</h3>
        <p>
          If one word is nearly solved (only one possible answer) and the other has many possibilities,
          focus your guesses on the harder board. You can often "sacrifice" guesses toward a harder board
          confident that a later guess will confirm the easier one. Don't celebrate prematurely — solving
          Board 1 in guess 4 doesn't help if Board 2 is still wide open with 3 guesses left.
        </p>
      </div>

      <h2>Dordle vs Wordle: Key Differences</h2>
      <div className="row g-2">
        {[
          { feature: 'Number of words', wordle: '1', dordle: '2' },
          { feature: 'Number of guesses', wordle: '6', dordle: '7' },
          { feature: 'Boards', wordle: '1', dordle: '2 side by side' },
          { feature: 'Difficulty', wordle: 'Standard', dordle: 'Significantly harder' },
          { feature: 'Keyboard', wordle: 'Single board coloring', dordle: 'Best state across both boards' },
        ].map(({ feature, wordle, dordle }) => (
          <div key={feature} className="col-12">
            <div className="seo-example">
              <strong>{feature}:</strong> Wordle → {wordle} · Dordle → {dordle}
            </div>
          </div>
        ))}
      </div>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'Do I need to solve Board 1 before Board 2?',
          a: 'No — you can solve them in any order or solve both at the same time. The boards are completely independent in terms of their secret words, but your guesses are shared.' },
        { q: 'What happens if I solve one board but not the other?',
          a: 'The solved board shows a green "Done" indicator and stops updating. Your remaining guesses apply only to feedback purposes for the unsolved board. You must solve both to win.' },
        { q: 'Are both words from the same word list as Wordle?',
          a: 'Yes — both words are drawn from the same 5-letter English word list (72,000+ words) used by our Wordle game, with validation ensuring both guesses and targets are real dictionary words.' },
        { q: 'Can both words be the same?',
          a: 'No — we ensure the two target words are always different. If the random selection returns duplicates, we substitute the second word with a fallback to guarantee variety.' },
      ]} />

      <h2>Related Games</h2>
      <Related links={[
        ['🟩🟩🟩🟩 Quordle (4 boards!)', '/quordle'],
        ['🟩 Classic Wordle', '/wordle'],
        ['💡 Wordle Solver', '/wordle-solver'],
        ['🐝 Spelling Bee Solver', '/spelling-bee'],
      ]} />
    </div>
  ),

  /* ══ QUORDLE ════════════════════════════════════════════════════════════ */
  quordle: () => (
    <div className="seo-section">
      <h2>What Is Quordle?</h2>
      <p>
        <strong>Quordle</strong> is the ultimate Wordle variant: you solve <strong>four 5-letter words
        simultaneously</strong> using just <strong>9 guesses</strong>. Each guess you enter is evaluated
        against all four secret words at once, giving you color-coded feedback on a 2×2 grid of boards.
        You need to crack all four words before running out of guesses to win.
      </p>
      <p>
        Quordle was created by Freddie Meyer in January 2022, just weeks after Wordle went viral. It
        quickly became one of the most popular Wordle spinoffs, with millions of daily players. In
        March 2022, Merriam-Webster acquired Quordle — a testament to its mainstream popularity. Our
        free Quordle lets you play unlimited rounds with new words every game.
      </p>

      <h2>How to Play Quordle</h2>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>The Rules</h3>
            <ol>
              <li>Four different 5-letter words are chosen at game start.</li>
              <li>You have <strong>9 guesses</strong> total — shared across all four boards.</li>
              <li>Each guess is evaluated against <strong>all 4 boards simultaneously</strong>.</li>
              <li>Green = correct letter, correct position; Yellow = correct letter, wrong position; Gray = letter not in that word.</li>
              <li>Solve all four words before using all 9 guesses to win.</li>
            </ol>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Why Quordle Is Harder Than Wordle</h3>
            <ul>
              <li>You track <strong>4 separate deduction chains</strong> at once</li>
              <li>Each guess must balance information across all 4 boards</li>
              <li>A guess useful for Board 1 may be useless for Board 3</li>
              <li>You have fewer "free" guesses per board (9 ÷ 4 = 2.25 effective)</li>
              <li>The chance of all 4 letters being in the right place by luck is essentially zero</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Quordle Strategy Guide</h2>
      <div className="seo-card">
        <h3>Phase 1: Information Gathering (Guesses 1–3)</h3>
        <p>
          Your first three guesses should focus <em>entirely</em> on gathering information — not on solving
          any specific board. Use words that cover as many different common letters as possible. Strong
          opening combinations: <strong>CRANE + DOILY + BUMPS</strong> (covers 15 letters) or
          <strong>ADIEU + STORY + CLAMP</strong>. By guess 3, you should have identified most letters
          across all four boards.
        </p>
      </div>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Phase 2: Parallel Solving (Guesses 4–7)</h3>
            <p>Start targeting specific boards while still gaining information for others. Look for
            guesses that move multiple boards forward simultaneously. If a letter appears in 3 boards,
            a word using that letter in different positions can help all three at once.</p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Phase 3: Endgame (Guesses 8–9)</h3>
            <p>With 1-2 guesses left, hopefully you have 1-2 boards nearly solved. Prioritize the
            most constrained board — the one with fewest possible answers. If you know a board's answer,
            delay guessing it if another board still needs information from that guess.</p>
          </div>
        </div>
      </div>

      <h2>Quordle vs Dordle vs Wordle</h2>
      <div className="row g-2">
        {[
          { feature: 'Boards to solve', wordle: '1', dordle: '2', quordle: '4' },
          { feature: 'Guesses', wordle: '6', dordle: '7', quordle: '9' },
          { feature: 'Difficulty', wordle: '⭐', dordle: '⭐⭐⭐', quordle: '⭐⭐⭐⭐⭐' },
          { feature: 'Avg solve rate', wordle: '~95%', dordle: '~60%', quordle: '~35%' },
          { feature: 'Boards layout', wordle: '1×1', dordle: '1×2', quordle: '2×2' },
        ].map(({ feature, wordle, dordle, quordle }) => (
          <div key={feature} className="col-12">
            <div className="seo-example">
              <strong>{feature}:</strong> Wordle → {wordle} · Dordle → {dordle} · Quordle → {quordle}
            </div>
          </div>
        ))}
      </div>

      <h2>Tips for Winning Quordle</h2>
      <ul>
        <li><strong>Never guess randomly</strong> — every one of your 9 guesses must carry maximum information value</li>
        <li><strong>Know your vowels</strong> — boards with A, E, I, O, U all confirmed are easier to finish; prioritize boards missing vowel info</li>
        <li><strong>Watch for duplicates</strong> — if CRANE gave you no information on Board 2, that word has unusual letters; it might have two of the same vowel or uncommon consonants</li>
        <li><strong>Use "sacrificial" guesses</strong> — sometimes guessing a word you know is wrong on 3 boards is the only way to get critical info on the 4th board</li>
        <li><strong>Track letter absences</strong> — gray letters are just as valuable as colored ones; a letter eliminated from possibility narrows word choices significantly</li>
      </ul>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'Is Quordle harder than Wordle?',
          a: 'Significantly harder. The average Wordle player solves ~95% of games. For Quordle, even experienced players solve only about 35-50% of games. The combination of 4 independent deduction chains and a limited 9-guess budget makes it a genuine challenge.' },
        { q: 'Can I play Quordle unlimited times?',
          a: 'Yes! Unlike the original Quordle which offers one daily puzzle, our version generates a new random set of 4 words each game, so you can play as many rounds as you like.' },
        { q: 'What are the best starting words for Quordle?',
          a: 'Words that cover many common letters across different positions. Popular Quordle openers: CRANE (C,R,A,N,E), DOILY (D,O,I,L,Y), BUMPS (B,U,M,P,S). Together these 3 words cover 15 of the 26 letters — a strong information base.' },
        { q: 'Why does the keyboard show green for a letter that\'s gray on one board?',
          a: 'The keyboard shows the best state across all 4 boards. If a letter is correct (green) on any one board, the key shows green to help you remember where it\'s confirmed. You need to track each board\'s gray feedback separately.' },
      ]} />

      <h2>Related Games</h2>
      <Related links={[
        ['🟩🟩 Dordle (2 boards)', '/dordle'],
        ['🟩 Classic Wordle', '/wordle'],
        ['💡 Wordle Solver', '/wordle-solver'],
        ['🐝 Spelling Bee Solver', '/spelling-bee'],
      ]} />
    </div>
  ),

  /* ══ CONNECTIONS ════════════════════════════════════════════════════════ */
  connections: () => (
    <div className="seo-section">
      <h2>What Is the Connections Word Game?</h2>
      <p>
        <strong>Connections</strong> is a category-grouping word puzzle where you are shown
        16 words and must sort them into exactly 4 groups of 4, each sharing a hidden common
        theme. Popularised by The New York Times, Connections has become one of the
        fastest-growing daily word games in the world, attracting tens of millions of players
        who enjoy the "aha!" moment of spotting a tricky link.
      </p>
      <p>
        Each puzzle has four difficulty tiers. <strong>Yellow</strong> categories are
        straightforward — think "Types of Cheese" or "Planets." <strong>Green</strong> is
        getting tricky. <strong>Blue</strong> requires lateral thinking. <strong>Purple</strong>
        is fiendish — categories often rely on wordplay, hidden phrases, or double meanings
        that make even experienced solvers groan in delighted surprise.
      </p>

      <h2>How to Play Connections</h2>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Step-by-Step Guide</h3>
            <ol>
              <li>You see a 4×4 grid of <strong>16 words</strong>.</li>
              <li>Tap or click words to select up to <strong>4 at a time</strong>.</li>
              <li>When you have 4 selected, press <strong>Submit</strong>.</li>
              <li>A correct group is revealed and locked in with its colour.</li>
              <li>A wrong guess costs one <strong>life</strong> — you have 4 in total.</li>
              <li>Solve all 4 groups to win!</li>
            </ol>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Colour Difficulty Key</h3>
            <p><strong style={{color:'#b45309'}}>🟨 Yellow</strong> — Straightforward. Usually a familiar everyday category.</p>
            <p><strong style={{color:'#4d7c0f'}}>🟩 Green</strong> — Getting tricky. May need a second look.</p>
            <p><strong style={{color:'#1e40af'}}>🟦 Blue</strong> — Challenging. Watch for red herrings.</p>
            <p><strong style={{color:'#6b21a8'}}>🟪 Purple</strong> — Fiendish. Wordplay, hidden phrases, or sneaky double meanings.</p>
          </div>
        </div>
      </div>

      <h2>Winning Strategies for Connections</h2>
      <ul>
        <li>
          <strong>Start with yellow.</strong> Lock in the easiest category first to shrink the
          grid and remove obvious words that might otherwise mislead you on harder groups.
        </li>
        <li>
          <strong>Watch out for red herrings.</strong> Puzzle designers deliberately include
          words that <em>seem</em> to fit one group but actually belong to another. If you spot
          three clear matches, pause — the fourth might not be what you think.
        </li>
        <li>
          <strong>Think about word forms.</strong> Purple categories often use words that can
          follow or precede a common word. For example, BOARD, CAST, HAUL, COAT might all
          follow "OVER" — a link that's invisible until you test it.
        </li>
        <li>
          <strong>Use the "one away" hint.</strong> If you get the "😬 One away…" message,
          only one of your four words is wrong. Mentally swap each word out one at a time to
          find the culprit.
        </li>
        <li>
          <strong>Don't rush.</strong> Use the Shuffle button to see the words in a fresh
          arrangement — spatial rearrangement often reveals hidden groupings your brain missed
          in the original layout.
        </li>
      </ul>

      <h2>Why Connections Is So Addictive</h2>
      <p>
        The game's brilliance lies in its deceptive simplicity. At first glance, 16 random
        words. Seconds later, you see a loose pattern. Then another. Then the rug-pull moment
        when a word you were certain belonged to one group turns out to be the anchor of a
        completely different category. That cycle of confidence, doubt, and revelation is
        precisely what keeps players coming back every day.
      </p>
      <p>
        Connections also rewards broad general knowledge — science, music, sport, literature,
        food, geography, and pop culture all appear — making it a genuinely educational
        experience dressed up as entertainment.
      </p>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        {
          q: 'Can a word appear in more than one possible category?',
          a: 'Each word belongs to exactly one correct category, but puzzle designers deliberately choose words that seem to fit multiple groups. That ambiguity is the puzzle — part of the challenge is figuring out which group each word truly belongs to.',
        },
        {
          q: 'What happens when I run out of lives?',
          a: 'All four categories are revealed so you can see the answers. You can then start a new puzzle immediately — no need to wait until tomorrow!',
        },
        {
          q: 'How many puzzles are available?',
          a: 'WordZingo Connections features 10 hand-crafted puzzles covering a wide range of topics. New puzzles are added regularly. Use the "Next Puzzle" button to cycle through them all.',
        },
        {
          q: 'What does "One away…" mean?',
          a: 'It means exactly 3 of your 4 selected words belong to the same correct group. One word in your selection is the intruder — swap it out with a word you haven\'t tried yet.',
        },
      ]} />

      <h2>Related Games</h2>
      <Related links={[
        ['🟩 Wordle', '/wordle'],
        ['🟩🟩 Dordle', '/dordle'],
        ['🟩🟩🟩🟩 Quordle', '/quordle'],
        ['🔎 Word Search', '/word-search'],
        ['🎭 Hangman', '/hangman'],
      ]} />
    </div>
  ),

  /* ══ PANGRAM CHECKER ════════════════════════════════════════════════════ */
  'pangram-checker': () => (
    <div className="seo-section">
      <h2>What Is a Pangram?</h2>
      <p>
        A <strong>pangram</strong> (from Greek <em>pan gramma</em> — "every letter") is a
        sentence or phrase that contains every letter of the alphabet at least once. The most
        famous English pangram is <em>"The quick brown fox jumps over the lazy dog"</em> — a
        35-word sentence that has been used to test typewriters, fonts, and keyboards for over
        a century because it exercises all 26 letters in a single, readable sentence.
      </p>
      <p>
        Pangrams appear across many fields: typographers use them to preview fonts, programmers
        use them to test character encoding, teachers use them for handwriting practice, and
        word-game lovers use them as a creative writing challenge. The NYT Spelling Bee also
        awards bonus points for "pangrams" — words that use all seven of the day's letters.
      </p>

      <h2>How to Use the Pangram Checker</h2>
      <ol>
        <li><strong>Type or paste</strong> any text into the input box above.</li>
        <li>Watch the <strong>A–Z grid</strong> fill up in real time — green tiles mean that letter is used.</li>
        <li>The <strong>result badge</strong> shows whether your text is a pangram, or lists the missing letters.</li>
        <li>Use the <strong>progress bar</strong> to track how close you are to a perfect pangram.</li>
        <li>Click <strong>▶ Try it</strong> on any famous pangram to load it into the checker.</li>
        <li>Click <strong>🎲 Random Pangram</strong> to get a surprise example from the gallery.</li>
      </ol>

      <h2>What Makes a "Perfect" Pangram?</h2>
      <p>
        An ordinary pangram uses all 26 letters but may repeat letters many times — "The quick
        brown fox…" uses 35 characters for 26 unique letters. A <strong>perfect pangram</strong>
        uses each letter <em>exactly once</em>, producing a 26-character sentence. Perfect
        pangrams in English are extremely rare and often sacrifice readability — they tend to
        include obscure words and unusual constructions. The checker's character counter helps
        you judge how efficient your pangram is.
      </p>
      <div className="row g-3 mb-3">
        {[
          { label: '≤ 34 chars', badge: '🏆 Elite', desc: 'Exceptionally compact — among the shortest known pangrams' },
          { label: '35–40 chars', badge: '✅ Great',  desc: 'Short and natural-sounding — the sweet spot for pangrams' },
          { label: '41–55 chars', badge: '👍 Good',   desc: 'A solid pangram — readable and complete' },
          { label: '56+ chars',   badge: '📝 Valid',  desc: 'A valid pangram but with room to trim letters' },
        ].map(({ label, badge, desc }) => (
          <div key={label} className="col-12">
            <div className="seo-example">
              <strong>{label} — {badge}:</strong> {desc}
            </div>
          </div>
        ))}
      </div>

      <h2>Tips for Writing Your Own Pangram</h2>
      <ul>
        <li>
          <strong>Start with rare letters.</strong> J, Q, X, and Z appear in very few common
          words. Lock them in first with words like <em>jinx</em>, <em>quiz</em>, <em>fox</em>,
          <em> jazz</em>, <em>vex</em>, or <em>quartz</em>.
        </li>
        <li>
          <strong>Use the missing-letter panel.</strong> The checker highlights exactly which
          letters you still need — use it as a live checklist as you write.
        </li>
        <li>
          <strong>Try uncommon but valid words.</strong> Words like <em>cwm</em> (a mountain
          hollow), <em>fjord</em>, <em>lynx</em>, <em>nymph</em>, and <em>waltz</em> pack
          multiple rare letters into a short space.
        </li>
        <li>
          <strong>Combine animal names.</strong> Animals cover a surprising range of uncommon
          letters: zebra (Z), fox (X), jay (J), quail (Q), viper (V), yak (Y), gnu (G).
        </li>
        <li>
          <strong>Watch for silent letters.</strong> Not all letters in a word are pronounced,
          but they all count — "knight" gives you K, N, I, G, H, T even though the K is silent.
        </li>
      </ul>

      <h2>Pangrams in Everyday Life</h2>
      <ul>
        <li><strong>Font previews:</strong> Type foundries display "The quick brown fox…" to show how a typeface renders every character.</li>
        <li><strong>Keyboard tests:</strong> New keyboards and typing software are tested with pangrams to verify every key works correctly.</li>
        <li><strong>Handwriting practice:</strong> Penmanship books use pangrams so students practise every letter in one exercise.</li>
        <li><strong>Spell-check seeding:</strong> Pangrams seed dictionaries and autocorrect systems during development testing.</li>
        <li><strong>Word games:</strong> The NYT Spelling Bee calls words that use all 7 given letters "pangrams" and awards bonus points for finding them.</li>
      </ul>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        { q: 'What is the shortest English pangram?',
          a: 'One of the shortest valid pangrams is "Cwm fjord bank glyphs vext quiz" at just 31 characters. A "cwm" is a Welsh word for a mountain hollow. Perfect pangrams using each letter exactly once (26 characters) exist but are generally not readable sentences.' },
        { q: 'Is "The quick brown fox" really a pangram?',
          a: 'Yes — "The quick brown fox jumps over the lazy dog" uses all 26 letters of the English alphabet. It became the go-to pangram for typewriter and font testing in the late 1800s because it is readable, short (relative to other pangrams at the time), and covers every letter.' },
        { q: 'What is the difference between a pangram and an anagram?',
          a: 'A pangram is a sentence containing all 26 letters of the alphabet at least once. An anagram rearranges the letters of one word or phrase to form another (e.g. "listen" → "silent"). They are unrelated concepts — one is about coverage, the other about rearrangement.' },
        { q: 'Does punctuation and spacing count in a pangram?',
          a: 'No — only the 26 alphabetic letters (A–Z) count. Spaces, punctuation, numbers, and special characters are ignored. The checker strips all non-letter characters before analysis, so "Mr. Jock…" counts only the letters M, R, J, O, C, K, etc.' },
      ]} />

      <h2>Related Tools</h2>
      <Related links={[
        ['🔤 Anagram Solver', '/anagram-solver'],
        ['🔍 Word Unscrambler', '/unscramble'],
        ['🐝 Spelling Bee Solver', '/spelling-bee'],
        ['🎲 Random Word Generator', '/random-word-generator'],
        ['🔢 Syllable Counter', '/syllable-counter'],
      ]} />
    </div>
  ),

  /* ══ RANDOM WORD GENERATOR ══════════════════════════════════════════════ */
  'random-word-generator': () => (
    <div className="seo-section">
      <h2>What Is a Random Word Generator?</h2>
      <p>
        A <strong>random word generator</strong> is a tool that selects one or more English words
        at random from a large dictionary. It is used by writers seeking inspiration, teachers
        creating classroom exercises, game designers populating word puzzles, developers
        testing software, and anyone who needs a quick creative spark.
      </p>
      <p>
        WordZingo's Random Word Generator goes beyond a simple dice roll — you can filter results
        by <strong>word type</strong> (nouns, verbs, adjectives, or adverbs), <strong>word
        length</strong> (short 1–4 letters, medium 5–8, or long 9+), and even
        <strong> starting letter</strong>. Each generated word shows its part of speech, letter
        count, and syllable count so you have instant context. Save words you love with the ⭐
        button, copy them all in one click, or share your list with friends.
      </p>

      <h2>Why Use a Random Word Generator?</h2>
      <ul>
        <li>
          <strong>Creative writing prompts.</strong> Stuck on a story? Generate 5–10 random
          nouns and adjectives to build a scene, character, or conflict you'd never have
          thought of on your own.
        </li>
        <li>
          <strong>Vocabulary building.</strong> Students and language learners can generate a
          batch of medium or long words they don't know yet, look them up, and practise using
          them in sentences.
        </li>
        <li>
          <strong>Game design and puzzles.</strong> Word game creators use random word lists to
          seed Hangman puzzles, scramble games, trivia rounds, and crossword grids.
        </li>
        <li>
          <strong>Icebreakers and party games.</strong> Generate a random adjective and noun,
          then challenge everyone to write the funniest sentence using both words — instant fun.
        </li>
        <li>
          <strong>Naming and brainstorming.</strong> Random words can suggest unexpected product
          names, character names, or brand concepts by combining with your existing ideas.
        </li>
        <li>
          <strong>Testing and development.</strong> Developers use random word lists as
          placeholder text, test data, or randomised display names.
        </li>
      </ul>

      <h2>How the Filters Work</h2>
      <div className="row g-3 mb-3">
        {[
          { name: 'Word Type',     desc: 'Choose Nouns, Verbs, Adjectives, Adverbs, or Any. Each category is drawn from a hand-curated dictionary ensuring only real, natural English words.' },
          { name: 'Word Length',   desc: 'Short (1–4 letters) returns quick, punchy words. Medium (5–8) is the everyday vocabulary sweet spot. Long (9+) surfaces sophisticated, expressive words.' },
          { name: 'Starts With',   desc: 'Pick a letter A–Z to get words beginning with that letter — great for alphabet games, alliterative writing, or when you need words from a specific part of the alphabet.' },
          { name: 'Count',         desc: 'Generate 1, 5, 10, 20, or any custom number up to 50 words per click. The tool guarantees no duplicates within a single generation.' },
        ].map(({ name, desc }) => (
          <div key={name} className="col-12">
            <div className="seo-example">
              <strong>{name}:</strong> {desc}
            </div>
          </div>
        ))}
      </div>

      <h2>Understanding the Word Badges</h2>
      <p>
        Every generated word card shows three small badges: the <strong>word type</strong> (noun /
        verb / adj / adv), the <strong>letter count</strong>, and the <strong>syllable count</strong>.
        Syllable count is useful for poetry (haiku requires exactly 5-7-5 syllables), song lyrics,
        and word games where rhythm matters. The syllable counter uses a vowel-group heuristic that
        accurately estimates syllables for the vast majority of English words.
      </p>

      <h2>Saving and Sharing Your Word List</h2>
      <p>
        Click the ⭐ star on any word card to save it to your <strong>Saved Words</strong> panel.
        Saved words persist in your browser between sessions — close the tab and come back, your
        list is still there. Click any saved word to copy it instantly, or use the
        <strong> Copy All</strong> button to grab every generated word as a newline-separated list
        ready to paste anywhere. The <strong>Share</strong> button opens a sharing modal so you can
        send your word list to friends, students, or collaborators via any messaging platform.
      </p>

      <h2>Tips for Getting the Most from the Generator</h2>
      <ul>
        <li>
          <strong>Writing prompts:</strong> Generate 3 random nouns and 2 random adjectives, then
          write a paragraph that includes all five words. This constraint-based technique is
          used by professional authors to break writer's block.
        </li>
        <li>
          <strong>Poetry and haiku:</strong> Filter to short or medium words and generate 20 at a
          time — then sort through them for words that carry strong imagery. The syllable badge
          helps you slot words into the right position in a haiku.
        </li>
        <li>
          <strong>Classroom games:</strong> Teachers can generate 10 random words, project them,
          and ask students to use as many as possible in one story paragraph within 5 minutes.
        </li>
        <li>
          <strong>Vocabulary quizzes:</strong> Generate 10 random long words, then challenge
          students to define each one before revealing the definition.
        </li>
        <li>
          <strong>Naming contests:</strong> Generate two sets of 5 words — one adjective batch,
          one noun batch — and combine them in interesting ways to brainstorm brand or project names.
        </li>
      </ul>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        {
          q: 'How many words are in the dictionary?',
          a: 'WordZingo\'s random word generator draws from a curated list of 400+ high-quality English words spanning nouns, verbs, adjectives, and adverbs across all length ranges. The list is hand-selected to include natural, commonly used words — not obscure technical jargon — making it ideal for creative and educational use.',
        },
        {
          q: 'Can I get words from a specific category like animals or food?',
          a: 'The current tool filters by grammatical type (noun/verb/adj/adv) and word length. Subject-specific category filters (animals, food, nature, etc.) are a planned enhancement. In the meantime, generating a batch of nouns will surface many concrete objects including natural vocabulary from various domains.',
        },
        {
          q: 'Are the generated words truly random?',
          a: 'Yes — each click shuffles the filtered word pool using JavaScript\'s Math.random() and draws the first N words from the shuffled array. This ensures uniform randomness across the available pool, and each word appears at most once per generation.',
        },
        {
          q: 'Will my saved words be lost if I clear my browser cache?',
          a: 'Saved words are stored in your browser\'s localStorage. Clearing site data or browser cache will remove them, so copy or export important lists before clearing. The saved words are stored locally and never sent to any server.',
        },
      ]} />

      <h2>Related Tools</h2>
      <Related links={[
        ['🔤 Anagram Generator', '/anagram-generator'],
        ['🔀 Jumble Solver', '/jumble'],
        ['🔍 Word Unscrambler', '/unscramble'],
        ['🐷 Pig Latin Converter', '/pig-latin'],
        ['🔢 Syllable Counter', '/syllable-counter'],
      ]} />
    </div>
  ),

  /* ══ TEXT TWIST SOLVER ═══════════════════════════════════════════════════ */
  'text-twist-solver': () => (
    <div className="seo-section">
      <h2>What Is the Text Twist Solver?</h2>
      <p>
        The <strong>Text Twist Solver</strong> is a free online tool that instantly unscrambles your
        Text Twist letters and reveals <em>every</em> valid word you can spell — neatly grouped by
        length so you can spot the high-scoring answers first. Whether you are racing the clock in the
        original <strong>Text Twist</strong>, its sequel <strong>Text Twist 2</strong>, the expanded
        <strong> Super Text Twist</strong>, or any of the dozens of clones inspired by the classic
        Yahoo and GameHouse hit, this solver turns a jumbled rack of 6 or 7 letters into a complete,
        sorted answer list in a fraction of a second.
      </p>
      <p>
        Unlike a plain word unscrambler, the Text Twist Solver is built specifically around the way the
        game actually plays. Text Twist fills in word slots from <strong>3-letter words up to the full
        6 or 7-letter bonus word</strong>, so our solver mirrors that structure exactly: results are
        organized into 3-letter, 4-letter, 5-letter, 6-letter and 7-letter columns, with the longest
        (and most valuable) words highlighted at the top. Type your letters, press solve, and you have
        the full board of answers — including the elusive bonus word that clears the round.
      </p>

      <h2>How to Use the Text Twist Solver</h2>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Step 1 — Enter Your Letters</h3>
            <p>Type the 6 or 7 scrambled letters from your current Text Twist round into the box. Order does not matter — the solver rearranges them for you. You can enter anywhere from 3 to 12 letters.</p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Step 2 — Pick a Minimum Length</h3>
            <p>Leave it at <strong>3+</strong> to match standard Text Twist rules, or raise it to 5+ or 6+ when you only care about the long, high-scoring words and the bonus answer.</p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Step 3 — Solve</h3>
            <p>Hit <strong>Solve Text Twist</strong>. In an instant you get every valid word, sorted from longest to shortest, with the full-length bonus word flagged with a 🏆 badge.</p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Step 4 — Type Them In</h3>
            <p>Work down from the longest words first to bank the most points, then fill in the shorter ones. Use <strong>Copy All</strong> to grab the whole list for reference.</p>
          </div>
        </div>
      </div>

      <h2>How the Solver Finds Every Word</h2>
      <p>
        Behind the scenes, the Text Twist Solver checks your letters against a curated dictionary of more
        than <strong>190,000 words</strong> — the same word list that powers our{' '}
        <Link to="/scrabble">Scrabble Finder</Link>, <Link to="/boggle">Boggle Solver</Link>, and{' '}
        <Link to="/jumble">Jumble Solver</Link>. For every word in the dictionary it performs a{' '}
        <em>letter-frequency check</em>: the word qualifies only if each of its letters appears in your
        rack at least as many times as the word needs it. That means if your letters are
        T-W-I-S-T-R, the solver knows you have two T&apos;s available and will happily return words that use
        both — while correctly rejecting any word that needs a letter you do not have.
      </p>
      <p>
        Because the matching is based on letter counts rather than position, the solver finds words of
        every length your rack can support — not just full anagrams. From those same six letters
        T-W-I-S-T-R you can spell <strong>WRIST</strong>, <strong>WRITS</strong>, <strong>TWIST</strong>,
        <strong> STIR</strong>, <strong>WITS</strong>, <strong>RIT</strong> and many more. The solver
        surfaces all of them, removes duplicates, and presents the results sorted by length so nothing
        slips through the cracks.
      </p>

      <h2>Text Twist Scoring: Why Word Length Matters</h2>
      <p>
        In Text Twist, points scale sharply with word length, which is exactly why our solver puts the
        longest words first. Short words keep you alive, but the long ones win rounds. A rough guide to
        how most versions of the game reward you:
      </p>
      <Ex arrow="3-letter word">small points — quick to grab, low value</Ex>
      <Ex arrow="4-letter word">moderate points</Ex>
      <Ex arrow="5-letter word">strong points</Ex>
      <Ex arrow="6-letter word">big points</Ex>
      <Ex arrow="6 or 7-letter bonus word">clears the round and unlocks the next level 🏆</Ex>
      <p>
        The single most important answer in any round is the <strong>bonus word</strong> — the word that
        uses <em>all</em> of your letters. Finding it is what advances you to the next round in the
        original Text Twist, and our solver flags it with a &quot;Uses all letters 🏆&quot; badge so you
        can spot it immediately. If you only have time for one word, make it the bonus word.
      </p>

      <h2>Finding the Text Twist Bonus Word</h2>
      <p>
        The bonus word is the longest possible word from your rack — 6 letters in classic Text Twist and
        Text Twist 2, or 7 letters in Super Text Twist. Many racks have more than one full-length
        answer. The letters in G-A-R-D-E-N, for example, spell not only <strong>GARDEN</strong> but also
        <strong> DANGER</strong> and <strong>GANDER</strong>. Our solver lists every full-length word it
        finds, so you always have a backup if the first one you try is not accepted by your particular
        version of the game.
      </p>
      <p>
        If the top group in your results matches the number of letters you entered, you have found the
        bonus word. If the longest words are one letter short of your rack, it usually means the puzzle
        is hiding an uncommon long word — try our <Link to="/longest-word">Longest Word Finder</Link> or
        lower the minimum length to make sure you are seeing every option.
      </p>

      <h2>Strategy Tips to Get Better at Text Twist</h2>
      <div className="row g-3">
        {[
          ['Learn common 3-letter words', 'Memorizing short staples like AIT, RIT, NTH, ZAX and QAT keeps your score ticking even when the long words hide. They are the foundation of fast clears.'],
          ['Spot prefixes and suffixes', 'Endings like -ING, -ED, -ER, -EST, -LY and prefixes like RE-, UN-, IN- instantly stretch a short root into a high-scoring 5 or 6-letter word.'],
          ['Anchor on vowels', 'Count your vowels first. Racks rich in vowels (like those containing A, E and I) almost always hide more words and at least one long bonus word.'],
          ['Shuffle your mental rack', 'In the live game, reordering the tiles helps your brain pattern-match. The solver does this automatically, but practicing it sharpens your own recall.'],
          ['Work longest-first', 'Always bank the bonus word and 5-letter words before the timer pressure builds. Short words are easy to add in the final seconds.'],
          ['Study the words you missed', 'Each time you solve a round, review the words you did not know. Building that vocabulary is what turns the solver from a crutch into a coach.'],
        ].map(([title, text]) => (
          <div className="col-md-6" key={title}>
            <div className="seo-card">
              <h3>{title}</h3>
              <p>{text}</p>
            </div>
          </div>
        ))}
      </div>

      <h2>Text Twist, Text Twist 2 &amp; Super Text Twist</h2>
      <p>
        The Text Twist franchise has appeared in many forms over the years — the original{' '}
        <strong>Text Twist</strong>, the popular sequel <strong>Text Twist 2</strong>, the 7-letter
        <strong> Super Text Twist</strong>, and countless free browser and mobile clones. They all share
        the same core mechanic: a scrambled rack of letters, a ticking clock, and a hunt for every word
        from 3 letters up to the full bonus word. Because our solver lets you enter up to 12 letters and
        choose your minimum length, it works seamlessly across <em>all</em> of these variants. Playing a
        6-letter board? Enter 6 letters. Tackling a 7-letter Super Text Twist round? Enter 7. The solver
        adapts instantly.
      </p>
      <p>
        Prefer to play rather than solve? Try our own timed take on the genre,{' '}
        <Link to="/word-twist">Word Twist</Link>, where you unscramble 6 letters against a 2-minute clock —
        then come back here whenever you want to check the words you missed.
      </p>

      <h2>Is Using a Text Twist Solver Cheating?</h2>
      <p>
        That depends entirely on how you use it. Text Twist is a single-player game with no leaderboard
        stakes, so using a solver to get unstuck, to settle whether an obscure word is real, or to study
        the answers after a round hurts no one. Thousands of players use a <strong>Text Twist cheat</strong>
        or <strong>Text Twist word finder</strong> as a learning aid — a way to discover new vocabulary,
        verify tricky words, and improve over time. We recommend trying each round yourself first, then
        using the solver to reveal what you missed. Used that way, it is one of the best vocabulary-building
        tools around.
      </p>

      <h2>What Counts as a Valid Text Twist Word?</h2>
      <p>
        Text Twist accepts standard English words of <strong>three letters or more</strong> that can be
        built from the letters in your rack. It does <em>not</em> accept proper nouns (names of people,
        places or brands), abbreviations, acronyms, contractions, or hyphenated words. Plurals formed by
        adding an S are valid as long as your rack contains the S, and so are common verb forms ending in
        -ED, -ING, -ER and -EST. Our solver follows the same conventions: it draws only from a dictionary
        of ordinary lowercase English words, which is why it is such a reliable companion for the game.
      </p>
      <p>
        One thing worth knowing is that different editions of Text Twist ship with slightly different
        dictionaries. A word that one version rejects, another may accept — particularly with short,
        obscure entries like <strong>QAT</strong>, <strong>ZAX</strong>, <strong>AIT</strong> or{' '}
        <strong>NTH</strong>. If the solver returns a word your game will not take, it simply is not in
        that edition&apos;s list; move on to the next answer. Conversely, if your game accepts a word the
        solver did not show, it is likely a regional spelling or a very rare term outside our core
        190,000+ word dictionary. In practice, the overlap is well above 95%, so the solver gives you a
        near-complete board nearly every time.
      </p>

      <h2>Text Twist Solver vs. a Plain Word Unscrambler</h2>
      <p>
        At a glance, a Text Twist Solver and a generic <Link to="/unscramble">word unscrambler</Link> look
        similar — both take scrambled letters and return real words. The difference is in the
        presentation and intent. A word unscrambler is designed to find anagrams or fill a single answer.
        A Text Twist Solver is tuned for the rhythm of the game: it always returns <em>partial-rack</em>
        words of every length, groups them the way the game&apos;s answer board is laid out, and spotlights
        the full-length bonus word that actually advances you to the next round. If your goal is to clear
        Text Twist rounds quickly and learn the words you are missing, the purpose-built solver on this
        page will serve you far better than a one-size-fits-all unscrambler.
      </p>
      <p>
        It also pairs naturally with our other word tools. Use the{' '}
        <Link to="/anagram-solver">Anagram Solver</Link> when you want only the exact full-length
        rearrangements, the <Link to="/longest-word">Longest Word Finder</Link> when you are hunting one
        maximum-length answer, or the <Link to="/scrabble">Scrabble Finder</Link> when letter values and
        board bonuses matter. They all share the same dictionary, so the words stay consistent across
        every tool on WordZingo.
      </p>

      <h2>Worked Example</h2>
      <p>
        Say your Text Twist rack is <strong>P-L-A-N-E-T</strong>. Drop those six letters into the solver
        and you will see the answers organized by length: the 6-letter bonus words <strong>PLANET</strong>
        and <strong>PLATEN</strong> at the top, 5-letter words like <strong>PLANE</strong>,{' '}
        <strong>PLATE</strong>, <strong>PETAL</strong> and <strong>LEAPT</strong>, a long list of 4-letter
        words such as <strong>PANE</strong>, <strong>LANE</strong>, <strong>TAPE</strong> and{' '}
        <strong>NEAP</strong>, and finally the 3-letter words like <strong>APE</strong>,{' '}
        <strong>NET</strong>, <strong>PAT</strong> and <strong>TEN</strong>. Bank the 6-letter bonus first,
        then sweep down through the rest — a perfect round in seconds.
      </p>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        {
          q: 'Is the Text Twist Solver free?',
          a: 'Yes — it is 100% free with no sign-up and no download required. Enter your letters and solve as many rounds as you like, right in your browser on any device.',
        },
        {
          q: 'How many letters can I enter?',
          a: 'Anywhere from 3 to 12 letters. Standard Text Twist and Text Twist 2 use 6 letters, while Super Text Twist uses 7. The extra range lets you solve longer custom puzzles and other word games too.',
        },
        {
          q: 'Does it find the bonus word that clears the round?',
          a: 'Yes. The solver always lists the longest possible words first and tags any word that uses all of your letters with a trophy badge, so the round-clearing bonus word is impossible to miss.',
        },
        {
          q: 'Can the same letter be used twice?',
          a: 'Only if it appears twice in your rack. If your letters are T-W-I-S-T-R, the solver knows you have two T’s and will return words that use both. If a letter appears once, it can only be used once per word.',
        },
        {
          q: 'What dictionary does the solver use?',
          a: 'A 190,000+ word Scrabble dictionary (the TWL/NWL tournament list), which includes far more valid words than a basic word list. Some Text Twist versions use slightly different word lists, so a rare word here may occasionally differ from your specific game.',
        },
        {
          q: 'Why are the words grouped by length?',
          a: 'Because that is how Text Twist itself is structured — you fill word slots from 3 letters up to the full bonus word, and longer words score far more. Grouping by length lets you grab the high-value answers first.',
        },
      ]} />

      <h2>Related Games &amp; Solvers</h2>
      <Related links={[
        ['🔤 Word Twist', '/word-twist'],
        ['🔀 Jumble Solver', '/jumble'],
        ['🔍 Word Unscrambler', '/unscramble'],
        ['📏 Longest Word Finder', '/longest-word'],
        ['🃏 Scrabble Finder', '/scrabble'],
        ['🎯 Boggle Solver', '/boggle'],
      ]} />
    </div>
  ),

  /* ══ WORD TWIST ══════════════════════════════════════════════════════════ */
  'word-twist': () => (
    <div className="seo-section">
      <h2>What Is Word Twist?</h2>
      <p>
        <strong>Word Twist</strong> is a fast-paced word-building game where you're given
        6 scrambled letters and must find as many valid English words as possible before
        the 2-minute timer runs out. It's inspired by the classic arcade game concept
        popularized as "Text Twist" — a beloved word puzzle that challenges your vocabulary,
        pattern recognition, and speed simultaneously.
      </p>
      <p>
        The goal isn't just to find one word — it's to find <em>all</em> of them: 3-letter
        words, 4-letter words, 5-letter words, and the elusive 6-letter <strong>Word Twist</strong>{' '}
        that uses every single tile. The longer the word, the more points it scores.
      </p>

      <h2>How to Play Word Twist</h2>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Step 1 — Start a Round</h3>
            <p>Click <strong>Start Game</strong> and 6 letter tiles appear on screen. They're shuffled randomly — the same 6 letters might hide 20 or more valid words.</p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Step 2 — Build Words</h3>
            <p>Type directly in the input box, or <strong>click the letter tiles</strong> to snap them into your word. Click a used tile again to remove that letter.</p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Step 3 — Submit &amp; Score</h3>
            <p>Press <strong>Enter</strong> (or the Enter button) to submit. Valid words are added to your found-words list and score instantly.</p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Step 4 — Shuffle When Stuck</h3>
            <p>Can't see new words? Hit <strong>Shuffle</strong> (or press Space) to reorder the tiles. A fresh arrangement often unlocks words you missed.</p>
          </div>
        </div>
      </div>

      <h2>Scoring System</h2>
      <p>Points increase dramatically with word length, rewarding players who push beyond 3-letter words:</p>
      <Ex arrow="3-letter word">100 points</Ex>
      <Ex arrow="4-letter word">200 points</Ex>
      <Ex arrow="5-letter word">400 points</Ex>
      <Ex arrow="6-letter word (Word Twist!)">800 points + 🎉 celebration</Ex>
      <p>
        Finding the 6-letter WORD TWIST is the ultimate goal. It triggers a special celebration
        and gives you the biggest single-word payout of the round. Some puzzles even have multiple
        valid 6-letter anagrams — for example, the letters G-A-R-D-E-N also spell DANGER and GANDER!
      </p>

      <h2>Strategy Tips</h2>
      <div className="row g-3">
        {[
          ['Start short', 'Quickly spot the obvious 3-letter words first — ATE, AGE, ARE, END, etc. They\'re easy points that add up fast.'],
          ['Look for suffixes', 'Common endings like -ING, -ED, -ER, -LY, and -EST can transform a 3-letter root into a 5-letter scorer.'],
          ['Use Shuffle liberally', 'Your brain pattern-matches based on tile position. Shuffling literally gives you a new visual puzzle to solve.'],
          ['Find the long word', 'Once you\'ve exhausted shorter words, focus on finding the 5- or 6-letter word. It\'s often worth more than five 3-letter words combined.'],
        ].map(([title, text]) => (
          <div className="col-md-6" key={title}>
            <div className="seo-card">
              <h3>{title}</h3>
              <p>{text}</p>
            </div>
          </div>
        ))}
      </div>

      <h2>Word Twist vs Text Twist</h2>
      <p>
        If you've searched for <em>text twist online</em> or <em>text twist free</em>, you're looking
        for exactly this game. Word Twist is our version of the classic letter-unscrambling mechanic,
        fully playable in your browser with no download and no account needed. We've added tile-click
        interaction, a live found-words panel, and a detailed end-of-round breakdown showing every word
        you missed — so each game is also a vocabulary lesson.
      </p>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        {
          q: 'How many words can I find from 6 letters?',
          a: 'It depends on the letters, but most 6-letter puzzle sets contain between 15 and 50 valid 3–6 letter words. High-vowel combinations like GARDEN or PLANET tend to yield the most words, while consonant-heavy sets like STRONG yield fewer but often more satisfying ones.',
        },
        {
          q: 'Can I use the same letter twice?',
          a: 'Only if it appears twice in the 6-letter set. For example, if the puzzle word is CORNER (C-O-R-N-E-R), you can form words using up to two R\'s. Most puzzle words have all unique letters, so each tile can only be used once.',
        },
        {
          q: 'What word list does Word Twist use?',
          a: 'We validate words against a curated English dictionary of common words. It excludes proper nouns, abbreviations, and slang, so gameplay stays fair and familiar.',
        },
        {
          q: 'Is the 6-letter source word always a valid English word?',
          a: 'Yes! Every puzzle uses a recognizable English word as its source (GARDEN, STREAM, MASTER, etc.), and finding that exact word always gives you the 800-point Word Twist bonus.',
        },
        {
          q: 'Can I play Word Twist on my phone?',
          a: 'Absolutely. The tile interface is touch-friendly — tap tiles to build words just like clicking on desktop. The game layout adapts to any screen size, so it works great on iPhone, Android, and tablets.',
        },
      ]} />

      <h2>Related Games</h2>
      <Related links={[
        ['🟩 Wordle', '/wordle'],
        ['⏱️ Word Scramble', '/word-scramble'],
        ['⛓️ Word Chain', '/word-chain'],
        ['🔀 Jumble Solver', '/jumble'],
        ['🔍 Word Unscrambler', '/unscramble'],
        ['🎯 Boggle Solver', '/boggle'],
      ]} />
    </div>
  ),

  /* ══ WAFFLE WORD GAME ════════════════════════════════════════════════════ */
  waffle: () => (
    <div className="seo-section">
      <h2>What Is the Waffle Word Game?</h2>
      <p>
        The <strong>Waffle Word Game</strong> is a letter-swapping puzzle played on a grid shaped like a
        waffle — a 5×5 board with the four corner-adjacent squares hollowed out. Hidden inside are
        <strong> six interlocking five-letter words</strong>: three running across and three running down,
        crossing each other at shared letters. Every tile you need is already on the board, just in the
        wrong place. Your job is to <strong>swap pairs of tiles</strong> until all six words read correctly.
      </p>
      <p>
        It's part anagram, part logic puzzle, and completely addictive. Because the words overlap, a single
        well-chosen swap can lock several letters into place at once. You have a limited number of swaps,
        so every move counts — solve it with swaps to spare and you earn stars.
      </p>

      <h2>How to Play Waffle</h2>
      <ol>
        <li><strong>Tap two tiles to swap them.</strong> The letters trade places instantly.</li>
        <li><strong>Read the colors</strong> after every swap — they tell you exactly how close you are.</li>
        <li><strong>Lock in greens.</strong> Once a tile turns green it's in the right spot and can't be moved.</li>
        <li><strong>Finish all six words</strong> — three across and three down — before your swaps run out.</li>
        <li><strong>Solve under the swap limit</strong> to bank leftover swaps as ⭐ stars (up to 5).</li>
      </ol>

      <h2>What the Tile Colors Mean</h2>
      <div className="row g-3">
        <div className="col-md-4">
          <div className="seo-card">
            <h3>🟩 Green</h3>
            <p>The letter is in its <strong>correct final position</strong>. Green tiles lock in place — you can't swap them, so they become anchors for solving the rest of the grid.</p>
          </div>
        </div>
        <div className="col-md-4">
          <div className="seo-card">
            <h3>🟨 Yellow</h3>
            <p>The letter <strong>belongs in this word but sits in the wrong square</strong>. Slide it along the row or column it's part of to find its true home.</p>
          </div>
        </div>
        <div className="col-md-4">
          <div className="seo-card">
            <h3>⬜ Gray</h3>
            <p>The letter <strong>doesn't belong in that word at all</strong> at this spot. It needs to move to a different word elsewhere on the waffle.</p>
          </div>
        </div>
      </div>

      <h2>Waffle Strategy &amp; Tips</h2>
      <div className="row g-3">
        {[
          ['Start with the corners', 'Corner tiles belong to two words at once — an across word and a down word. Getting a corner green simultaneously helps solve both, so prioritize them.'],
          ['Follow the yellows', 'A yellow tile already tells you which word it belongs to. Trace that row or column and look for the empty-feeling slot where it fits.'],
          ['Chain your swaps', 'The best Waffle moves are double-helpers: a single swap that turns both tiles green. Look for two tiles that are each sitting in the other\'s correct spot.'],
          ['Solve the center cross last', 'The middle row and column share letters with everything. Once the outer words are mostly green, the center often falls into place with just one or two swaps.'],
        ].map(([title, text]) => (
          <div className="col-md-6" key={title}>
            <div className="seo-card">
              <h3>{title}</h3>
              <p>{text}</p>
            </div>
          </div>
        ))}
      </div>

      <h2>Why Players Love Waffle</h2>
      <p>
        Unlike guessing games, Waffle gives you <strong>every letter from the start</strong> — there's no
        luck involved, just deduction. That makes it endlessly replayable and genuinely fair: a perfect
        score is always achievable if you read the colors carefully. It's a fantastic daily brain workout
        that sharpens pattern recognition, vocabulary, and planning skills, and it plays beautifully on
        both desktop and mobile with simple tap-to-swap controls.
      </p>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        {
          q: 'How many swaps do you get in Waffle?',
          a: 'You get 15 swaps to solve the grid. Most puzzles can be solved in around 10 swaps, and every swap you save at the end is converted into a star — up to a maximum of 5 stars for a near-perfect solve.',
        },
        {
          q: 'Why can\'t I move a green tile?',
          a: 'Green means the letter is already in its correct final position, so the game locks it to prevent you from accidentally breaking a word you\'ve already solved. Build your remaining swaps around these anchor tiles.',
        },
        {
          q: 'Is Waffle the same as Wordle?',
          a: 'They share the green/yellow/gray color system, but the gameplay is different. In Wordle you type guesses to find one hidden word; in Waffle every letter is already on the board and you rearrange six interlocking words by swapping tiles. No typing or guessing required.',
        },
        {
          q: 'Is this Waffle Word Game free?',
          a: 'Yes — it\'s 100% free with no sign-up and no download required. You can play unlimited puzzles right in your browser on any device.',
        },
        {
          q: 'Can I play more than one puzzle a day?',
          a: 'Absolutely. While the original Waffle is a once-a-day puzzle, our version lets you hit "New Puzzle" as many times as you like, drawing from a large rotating set of hand-validated waffle grids.',
        },
      ]} />

      <h2>Related Games</h2>
      <Related links={[
        ['🟩 Wordle', '/wordle'],
        ['🔗 Connections', '/connections'],
        ['🟩🟩 Dordle', '/dordle'],
        ['🔤 Word Twist', '/word-twist'],
        ['🎯 Boggle Solver', '/boggle'],
        ['⏱️ Word Scramble', '/word-scramble'],
      ]} />
    </div>
  ),

  /* ══ WORDBLOOM ══════════════════════════════════════════════════════════════ */
  wordbloom: () => (
    <div className="seo-section">
      <h2>What Is WordBloom?</h2>
      <p>
        <strong>WordBloom</strong> is a free flower-shaped word game inspired by the classic
        <strong> Blossom Word Game</strong>. Seven letters are arranged as a blossom — one letter in the
        center and six surrounding petals — and your job is to spell as many words as you can. Every word
        must use the <strong>center letter</strong>, must be at least four letters long, and letters may be
        reused as often as you like. It's a relaxing, beautiful daily brain workout that rewards a sharp
        vocabulary and clever letter combinations.
      </p>
      <p>
        What makes WordBloom special is the <strong>golden bonus petal</strong>. One petal glows gold and any
        word that uses it earns a juicy <strong>+5 point bonus</strong>. The bonus petal changes after every
        word you play, so there's always a fresh target to aim for. Use all seven letters in a single word and
        you score a <strong>Pangram</strong> for an extra +7 points — the ultimate bloom.
      </p>

      <h2>How to Play WordBloom</h2>
      <ol>
        <li><strong>Tap the petals</strong> (or type on your keyboard) to build a word from the seven letters.</li>
        <li><strong>Use the center letter</strong> — every valid word must contain it.</li>
        <li><strong>Make words of 4+ letters.</strong> Letters can be repeated within a word.</li>
        <li><strong>Hit the golden petal</strong> for a +5 bonus, and aim for pangrams for +7.</li>
        <li><strong>You get 12 words</strong> per flower — choose long, high-scoring words to maximize your bloom.</li>
      </ol>

      <h2>How Scoring Works</h2>
      <div className="row g-3">
        <div className="col-md-4">
          <div className="seo-card">
            <h3>📏 Word Length</h3>
            <p>Longer words score more: 4 letters = 2 points, 5 = 4, 6 = 6, and 7+ letters = 12 points and up. Reach for length whenever you can.</p>
          </div>
        </div>
        <div className="col-md-4">
          <div className="seo-card">
            <h3>🌟 Bonus Petal</h3>
            <p>The glowing gold petal adds <strong>+5 points</strong> to any word that uses it. Because it rotates each turn, planning around it is the key to a high score.</p>
          </div>
        </div>
        <div className="col-md-4">
          <div className="seo-card">
            <h3>🌸 Pangram</h3>
            <p>Use all seven flower letters in one word and earn a <strong>+7 pangram bonus</strong> on top of the length score — the most rewarding play in the game.</p>
          </div>
        </div>
      </div>

      <h2>WordBloom Strategy &amp; Tips</h2>
      <div className="row g-3">
        {[
          ['Chase the bonus petal', 'Before locking in a word, check whether you can fit the glowing gold letter in — that single petal is worth +5, often more than the base word itself.'],
          ['Think in word stems', 'Common endings like -ING, -ED, -ER, -EST and -LY let you stretch a short root into a longer, higher-scoring word using the same petals.'],
          ['Reuse letters freely', 'Unlike many word games, WordBloom lets you use a letter as many times as you want — words like "LEVEL" or "BANANA" are fair game if the letters are on the flower.'],
          ['Save guesses for pangrams', 'With only 12 words per flower, a single pangram can outscore several short words. If you spot one, play it early so you don\'t run out of turns.'],
        ].map(([title, text]) => (
          <div className="col-md-6" key={title}>
            <div className="seo-card">
              <h3>{title}</h3>
              <p>{text}</p>
            </div>
          </div>
        ))}
      </div>

      <h2>Why Players Love WordBloom</h2>
      <p>
        WordBloom combines the calm, satisfying flow of petal-based word hunting with just enough strategy to
        keep things interesting. There's no timer and no pressure — just you, a beautiful blossom, and your
        vocabulary. It's perfect for a quick mental stretch on a coffee break, and the rotating bonus petal plus
        pangram hunt give it real replay value. Every flower is freshly generated, so you'll never run out of new
        puzzles to grow.
      </p>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        {
          q: 'How is WordBloom different from Spelling Bee?',
          a: 'Both use seven letters with a required center letter, but WordBloom is shaped like a blossom and adds a rotating golden bonus petal worth +5 points, plus a 12-word limit per game. It rewards planning around the bonus petal rather than simply finding every word.',
        },
        {
          q: 'Do words have to use the center letter?',
          a: 'Yes. Every valid word in WordBloom must include the center letter of the flower, must be at least four letters long, and can only use the seven letters shown — though you may reuse those letters as many times as you like.',
        },
        {
          q: 'What is a pangram in WordBloom?',
          a: 'A pangram is a word that uses all seven flower letters at least once. Pangrams earn a +7 bonus on top of their length score, making them the highest-value words in the game.',
        },
        {
          q: 'How many words do I get?',
          a: 'You get 12 words per flower. Because your turns are limited, it pays to look for long words, the golden bonus petal, and pangrams rather than rattling off lots of short four-letter words.',
        },
        {
          q: 'Is WordBloom free to play?',
          a: 'Yes — WordBloom is completely free, with no sign-up and no download. Just open the page and start spelling. A brand-new flower is generated every time you hit "New Flower."',
        },
      ]} />

      <h2>Related Games</h2>
      <Related links={[
        ['🐝 Spelling Bee Solver', '/spelling-bee'],
        ['🔤 Word Twist', '/word-twist'],
        ['🧇 Waffle', '/waffle'],
        ['🟩 Wordle', '/wordle'],
        ['🎯 Boggle Solver', '/boggle'],
        ['⏱️ Word Scramble', '/word-scramble'],
      ]} />
    </div>
  ),

  /* ══ LETTER BOXED ════════════════════════════════════════════════════════ */
  'letter-boxed': () => (
    <div className="seo-section">
      <h2>What Is Letter Boxed?</h2>
      <p>
        <strong>Letter Boxed</strong> is a word puzzle played on a square with three letters on
        each of its four sides — twelve letters in all. Your job is to spell words by linking those
        letters together, hopping from side to side, until you've used every single letter on the
        box. It's a simple idea with surprising depth: finding the longest, most efficient words is
        a genuine test of vocabulary and lateral thinking.
      </p>
      <p>
        Our version gives you a fresh, fully solvable box every time you play — no waiting for a
        daily puzzle. Every box is guaranteed to have at least one two-word solution, so there's
        always an elegant answer hiding in there for you to chase.
      </p>

      <h2>How to Play Letter Boxed</h2>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Rule 1 — Build words of 3+ letters</h3>
            <p>Click or type letters from around the box to form a word. Every word must be at least three letters long and appear in the dictionary.</p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Rule 2 — Switch sides each letter</h3>
            <p>You can't use two letters from the <strong>same side</strong> in a row. Each letter of your word has to come from a different side than the one before it.</p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Rule 3 — Chain your words</h3>
            <p>The first letter of each new word must be the <strong>last letter</strong> of the word you just played, so your words link together into a chain.</p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Rule 4 — Use all 12 letters</h3>
            <p>Keep going until every letter around the box has been used at least once. Reuse letters freely — the goal is coverage, not avoiding repeats.</p>
          </div>
        </div>
      </div>

      <h2>The Goal: Fewer Words Is Better</h2>
      <p>
        You win the moment all twelve letters have been used, but the real challenge is doing it in
        as few words as possible. Two words is par — the gold standard — and every box we generate
        has a two-word answer. Three or four words is a solid, satisfying solve. Stuck? A single tap
        reveals one possible two-word solution to point you in the right direction.
      </p>
      <Ex arrow="Use all 12 letters in 2 words">Par — brilliant! 🌟</Ex>
      <Ex arrow="3–4 words">A great, natural solve 👍</Ex>
      <Ex arrow="5+ words">You cracked it — now try to trim it down</Ex>

      <h2>Strategy Tips</h2>
      <div className="row g-3">
        {[
          ['Hunt the long words', 'A single seven- or eight-letter word can knock out half the box at once. Long words are the fastest route to a two-word finish.'],
          ['Plan your last letter', 'Since the next word must start with your previous word\'s final letter, end on a versatile letter like S, T, or E that begins lots of words.'],
          ['Attack the awkward letters', 'Spot the hardest letters first — a lone J, Q, V, or Z — and build a word around them early, so they don\'t strand you at the end.'],
          ['Different side, every step', 'Before adding a letter, glance at which side your last letter came from. Keeping the rule in mind as you go saves a lot of backtracking.'],
        ].map(([title, text]) => (
          <div className="col-md-6" key={title}>
            <div className="seo-card">
              <h3>{title}</h3>
              <p>{text}</p>
            </div>
          </div>
        ))}
      </div>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        {
          q: 'Can I reuse the same letter more than once?',
          a: 'Yes. Letters can be used as many times as you like, and across as many words as you like — the only restriction is that you can\'t use two letters from the same side of the box back-to-back within a word.',
        },
        {
          q: 'Do I have to solve it in two words?',
          a: 'No. Two words is par and every box has a two-word answer, but you can use as many words as you need. Finishing in three or four words is a great result — the two-word solve is just the bonus challenge for word lovers.',
        },
        {
          q: 'Is every puzzle actually solvable?',
          a: 'Every box is generated from a real two-word solution that covers all twelve letters, so there is always a valid answer. If you get stuck, use the "Show a solution" button to reveal one.',
        },
        {
          q: 'Is Letter Boxed free to play?',
          a: 'Completely free, with no sign-up and no download. Play as many boxes as you want — hit "New Puzzle" any time for a fresh, fully solvable box.',
        },
      ]} />

      <h2>Related Games</h2>
      <Related links={[
        ['🌸 WordBloom', '/wordbloom'],
        ['🐝 Spelling Bee Solver', '/spelling-bee'],
        ['🧇 Waffle', '/waffle'],
        ['🪜 Word Ladder', '/word-ladder'],
        ['🎯 Boggle Solver', '/boggle'],
        ['🔤 Word Twist', '/word-twist'],
      ]} />
    </div>
  ),

  /* ══ WORDFEUD HELPER ═════════════════════════════════════════════════════ */
  'wordfeud-helper': () => (
    <div className="seo-section">
      <h2>What Is Wordfeud?</h2>
      <p>
        <strong>Wordfeud</strong> is one of the most popular word games ever made for phones and
        tablets. Created in 2010 by Norwegian developer Håkon Bertheussen, it took the timeless
        crossword-tile format made famous by Scrabble and rebuilt it for the mobile era: quick
        turn-based matches you can play against friends, family, or random opponents anywhere in
        the world. Within a couple of years it had been downloaded tens of millions of times and
        become a genuine cultural phenomenon in Scandinavia, the Netherlands, and beyond. Today it
        remains a daily habit for millions of players across more than 30 language dictionaries.
      </p>
      <p>
        The rules will feel instantly familiar to any Scrabble player. Each player holds a rack of
        seven letter tiles and takes turns placing words on a 15×15 grid, connecting to letters
        already on the board. Words score points based on the value of each tile, multiplied by any
        premium squares they cover. But Wordfeud is not a Scrabble clone — it has its own tile
        values, its own board layouts (including a signature randomized board), and its own
        dictionaries. Those differences matter, and they are exactly why a dedicated
        <strong> Wordfeud Helper</strong> is far more useful than a generic Scrabble tool.
      </p>

      <h2>What the Wordfeud Helper Does</h2>
      <p>
        Our <strong>Wordfeud Helper</strong> is a fast, free word finder built specifically for
        Wordfeud. You type in the tiles sitting on your rack — up to seven letters, plus any blank
        tiles — and it instantly searches a dictionary of more than 190,000 words to show you every
        legal word you can spell. Crucially, it ranks those words using <strong>Wordfeud&rsquo;s own
        point values</strong>, not Scrabble&rsquo;s, so the word at the top of the list really is your
        highest-scoring option in Wordfeud terms.
      </p>
      <p>
        Think of it as a study partner rather than a crutch. When you are stuck staring at an
        awkward rack of consonants, or you simply want to see whether a cluster of letters hides a
        seven-letter bonus word, the helper reveals the possibilities you might have missed. Over
        time, seeing which letter combinations produce big words trains your own eye to spot them
        faster — which is how the best players got good in the first place.
      </p>

      <h2>How to Use the Wordfeud Helper</h2>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Step-by-Step</h3>
            <ol>
              <li>Type the letters from your rack into the input box (2–7 tiles).</li>
              <li>Enter a <strong>?</strong> for every blank tile you are holding.</li>
              <li>Press <strong>&ldquo;Find Words&rdquo;</strong> to see a ranked list of every playable word.</li>
              <li>Read the <strong>Score</strong> column — results are sorted highest first.</li>
              <li>Use the <strong>Min Score</strong> buttons to hide low-value words and focus on the big plays.</li>
              <li>Check the <strong>Blanks Used</strong> column to see which words need one of your blank tiles.</li>
            </ol>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Wordfeud Tile Values</h3>
            <ul>
              <li><strong>10 pts:</strong> J, Q, Z</li>
              <li><strong>8 pts:</strong> X</li>
              <li><strong>5 pts:</strong> K</li>
              <li><strong>4 pts:</strong> B, C, F, H, P, V, W, Y</li>
              <li><strong>3 pts:</strong> G, M</li>
              <li><strong>2 pts:</strong> D</li>
              <li><strong>1 pt:</strong> A E I O U L N R S T</li>
              <li><strong>0 pts:</strong> blank tiles</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>How Wordfeud Scoring Differs From Scrabble</h2>
      <p>
        This is where many players quietly lose points. If you have only ever played Scrabble, your
        instincts about which letters are &ldquo;worth a lot&rdquo; are slightly wrong for Wordfeud. Five
        letters carry different values between the two games, and knowing them changes how you value
        your rack:
      </p>
      <ul>
        <li><strong>B is worth 4 in Wordfeud</strong> (only 3 in Scrabble).</li>
        <li><strong>C is worth 4 in Wordfeud</strong> (3 in Scrabble).</li>
        <li><strong>P is worth 4 in Wordfeud</strong> (3 in Scrabble).</li>
        <li><strong>G is worth 3 in Wordfeud</strong> (only 2 in Scrabble).</li>
        <li><strong>J is worth a huge 10 in Wordfeud</strong> (8 in Scrabble) — tying it with Q and Z as the most valuable tile in the bag.</li>
      </ul>
      <p>
        Everything else lines up with Scrabble: the vowels plus L, N, R, S and T are worth one point;
        D is worth two; K is five; X is eight; and Q and Z are ten. But those five differences add
        up. A word like <Ex>JUMP</Ex> scores noticeably more in Wordfeud than in Scrabble because
        both the J and the P are worth more. When you feed your rack into this helper, it applies the
        correct Wordfeud values automatically — so you never have to do that mental conversion
        yourself, and you never overlook a word that happens to be a Wordfeud gem.
      </p>

      <h2>Understanding the Wordfeud Board</h2>
      <p>
        A Wordfeud board is a 15×15 grid dotted with coloured premium squares, just like Scrabble.
        There are four kinds, and understanding them is the difference between a decent play and a
        game-swinging one:
      </p>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="seo-card">
            <h3>The Four Premium Squares</h3>
            <ul>
              <li><strong>DL — Double Letter:</strong> doubles the value of the tile placed on it.</li>
              <li><strong>TL — Triple Letter:</strong> triples that single tile&rsquo;s value.</li>
              <li><strong>DW — Double Word:</strong> doubles the entire word&rsquo;s score.</li>
              <li><strong>TW — Triple Word:</strong> triples the entire word&rsquo;s score.</li>
            </ul>
          </div>
        </div>
        <div className="col-md-6">
          <div className="seo-card">
            <h3>Normal vs Random Boards</h3>
            <p>
              Wordfeud lets you choose the board when you start a game. On a <strong>Normal</strong>{' '}
              board the bonus squares sit in fixed, symmetrical positions you can memorise. On a
              <strong> Random</strong> board the bonuses are scattered to fresh positions every single
              game, so you must read the layout on the fly. Random boards are Wordfeud&rsquo;s signature
              twist and reward flexible, opportunistic play.
            </p>
          </div>
        </div>
      </div>
      <p>
        Letter bonuses (DL, TL) are applied first, then word bonuses (DW, TW) multiply the whole
        total. If you place a triple-letter tile <em>and</em> land the word across a triple-word
        square, the two stack for an enormous score. Placing all seven of your tiles in one turn — a
        &ldquo;bingo&rdquo; — earns a <strong>40-point bonus</strong> in Wordfeud (note that this differs from
        Scrabble&rsquo;s 50-point bonus). The helper shows you which racks contain a seven-letter word so
        you can chase that bonus whenever the board opens up.
      </p>

      <h2>Winning Wordfeud Strategy</h2>
      <p>
        A word finder tells you what you <em>can</em> play; strategy tells you what you <em>should</em>
        play. Here are the habits that separate strong Wordfeud players from casual ones:
      </p>
      <ul>
        <li>
          <strong>Guard the triple-word squares.</strong> A single tile on a TW square can multiply a
          modest word into 40 or 50 points. Never open a lane to a TW square unless you are the one
          using it — otherwise you are handing your opponent a gift on their next turn.
        </li>
        <li>
          <strong>Hoard your blanks and S tiles.</strong> Blank tiles let you complete words you could
          never otherwise spell, and S tiles pluralise existing words while starting a brand-new one
          alongside. Both are so flexible that it is usually worth passing up a small play now to keep
          them for a bingo or a premium-square blockbuster later.
        </li>
        <li>
          <strong>Balance your rack.</strong> A rack that is all consonants or all vowels is nearly
          unplayable. When you have a choice, dump doubled letters and keep a mix of common consonants
          (R, S, T, N, L) and vowels so next turn stays flexible.
        </li>
        <li>
          <strong>Play the board, not just the rack.</strong> The highest-scoring word in a vacuum is
          worthless if it will not connect to the tiles already down. Look for open premium squares
          first, then work out which of your words can reach them.
        </li>
        <li>
          <strong>Learn the little words.</strong> Two- and three-letter words are the glue of every
          crossword game. They let you hook onto existing words, reach bonus squares, and play
          parallel words that score in two directions at once.
        </li>
        <li>
          <strong>Think one move ahead.</strong> After you place a word, glance at what you are opening
          up for your opponent. Sometimes a slightly lower-scoring play that keeps the board tight is
          worth more than a flashy one that exposes a triple-word square.
        </li>
      </ul>

      <h2>Two-Letter and High-Value Words Worth Knowing</h2>
      <p>
        Every serious Wordfeud player memorises a core set of short words, because they unlock plays
        that longer words cannot. Handy two-letter words include <Ex>QI</Ex>, <Ex>ZA</Ex>,
        <Ex>XI</Ex>, <Ex>JO</Ex>, <Ex>KA</Ex>, <Ex>OX</Ex>, <Ex>AX</Ex> and <Ex>EX</Ex> — several of
        which let you cash in your most valuable tiles for big points in a tiny footprint. When you
        are holding an awkward high-value letter like J, Q, X or Z, the goal is to land it on a
        letter-bonus square inside one of these short words. Feed the letters into the helper and you
        will often discover a compact, high-scoring play you would never have spotted by eye. To go
        deeper, our{' '}
        <Link to="/scrabble" style={{color:'var(--primary)',fontWeight:700}}>Scrabble Word Finder</Link>{' '}
        and{' '}
        <Link to="/unscramble" style={{color:'var(--primary)',fontWeight:700}}>Word Unscrambler</Link>{' '}
        are great companions for building the same instincts.
      </p>

      <h2>Wordfeud vs Scrabble vs Words With Friends</h2>
      <p>
        The three big crossword-tile games look similar but reward different knowledge. <strong>Scrabble</strong>{' '}
        is the original, with the classic fixed board and the tile values everyone half-remembers.
        <strong> Words With Friends</strong> uses its own, quite different point values and a distinct
        board, so scores between the two games are not comparable. <strong>Wordfeud</strong> sits
        between them: its board and bonus rules are close to Scrabble, but its tile values — and
        especially that 10-point J — are its own. It also offers the randomized board that neither of
        the others has. The practical takeaway is simple: if you play Wordfeud, use a tool tuned for
        Wordfeud. A Scrabble calculator will mis-rank your options every time a B, C, G, P or J is on
        your rack. This helper removes that guesswork completely.
      </p>

      <h2>Wordfeud Dictionaries and Languages</h2>
      <p>
        One reason Wordfeud spread so widely is that it never limited itself to English. The game
        ships with more than thirty language dictionaries — from Norwegian, Swedish and Danish to
        Dutch, German, French, Spanish, Portuguese and many more — and each game is validated against
        the dictionary the players agree on. Within English itself there are separate word lists,
        including a British-leaning set and an American one, and the two do not always agree on which
        spellings are legal. That is worth remembering when a word you are certain about gets
        rejected in one game but accepted in another. Our Wordfeud Helper draws on a large,
        Scrabble-based English dictionary of over 190,000 words, which covers the overwhelming
        majority of everyday and competitive plays. If your specific game uses a stricter tournament
        list, treat the helper&rsquo;s suggestions as strong candidates and let the game itself be the
        final referee on any borderline word.
      </p>
      <p>
        Because the tile values in this tool are the English Wordfeud values, the scores you see are
        accurate for English games. If you play in another language, the words and the point values
        will differ, but the core skills the helper builds — recognising bonus-word potential,
        valuing your rack correctly, and spotting compact high-scoring plays — transfer to every
        language you play in.
      </p>

      <h2>Is Using a Wordfeud Helper Cheating?</h2>
      <p>
        It depends entirely on how and why you use it — and that is your call. In a friendly, casual
        game, most people treat a word finder as a learning aid: a way to check whether a word exists,
        to study which racks hide bonus words, or to get unstuck when a game has stalled for days. Used
        that way, it makes you a sharper player and keeps games moving. What it is <em>not</em> is a
        substitute for your own thinking in a competitive match where both players expect to rely only
        on their own vocabulary. Our honest advice: use the Wordfeud Helper to <strong>learn</strong> —
        to review your games, expand your word knowledge, and train your eye — and you will steadily
        need it less. That is the whole point of a good practice tool.
      </p>

      <h2>Frequently Asked Questions</h2>
      <FAQ items={[
        {
          q: 'Does the Wordfeud Helper use the correct Wordfeud tile values?',
          a: 'Yes. It scores every word with Wordfeud’s own English point values, where B, C, F, H, P, V, W and Y are worth 4, G and M are worth 3, and J joins Q and Z at 10 points. That is why its ranking can differ from a Scrabble tool for the same rack.',
        },
        {
          q: 'How do I enter blank tiles?',
          a: 'Type a question mark (?) for each blank tile on your rack — for example AEIR?T. The helper will use a blank wherever it is needed to complete a word, and the Blanks Used column tells you exactly how many blanks each word requires.',
        },
        {
          q: 'How many tiles can I enter?',
          a: 'Enter up to seven letters, which matches a full Wordfeud rack. You can enter fewer if you only want to check part of your rack, and you can include one or two blanks as question marks.',
        },
        {
          q: 'Why do blank tiles score zero points?',
          a: 'A blank can stand in for any letter, but it always contributes zero points to the word’s value — that trade-off is what makes blanks so flexible. Use them to complete high-value words, not to save a point or two on a small one.',
        },
        {
          q: 'What is the bingo bonus in Wordfeud?',
          a: 'If you play all seven tiles from your rack in a single turn, Wordfeud awards a 40-point bonus on top of the word’s score. (Scrabble’s equivalent bonus is 50 points.) The helper flags seven-letter words so you can chase that bonus whenever the board allows.',
        },
        {
          q: 'Is the Wordfeud Helper free to use?',
          a: 'Completely free, with no sign-up and no download. Enter your rack as many times as you like — there are no limits, no accounts, and no cost.',
        },
      ]} />

      <h2>Related Tools</h2>
      <Related links={[
        ['🃏 Scrabble Word Finder', '/scrabble'],
        ['🧮 Scrabble Calculator', '/scrabble-calculator'],
        ['🔍 Word Unscrambler', '/unscramble'],
        ['🔀 Jumble Solver', '/jumble'],
        ['🔤 Anagram Solver', '/anagram-solver'],
        ['📏 Longest Word Finder', '/longest-word'],
      ]} />
    </div>
  ),

};

export default function SeoContent({ game }) {
  const Content = CONTENT[game];
  if (!Content) return null;
  return <Content />;
}
